import React, { useState, useEffect, useCallback } from "react";
import {
  FaSyncAlt,
  FaCheckCircle,
  FaTimesCircle,
  FaExclamationTriangle,
  FaDollarSign,
  FaUndo,
  FaBan,
  FaShieldAlt,
} from "react-icons/fa";
import { Container, Row, Col, Button, Spinner } from "react-bootstrap";
import Cookies from "js-cookie";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  BarChart,
  Bar,
  Cell,
  PieChart,
  Pie,
  ComposedChart,
  ScatterChart,
  Scatter,
  ZAxis,
  ReferenceLine,
} from "recharts";

import ChartCard from "./ui/CardChart";
// Idenza design system — v1. Source: docs/DESIGN_SYSTEM.md.
import { color, space, radius, text, weight, tracking, numeric, shadow, motion, font, leading, chart } from "../design/tokens";
import {
  Sparkles as SparklesIcon,
  RotateCw as RefreshIcon,
  Zap as ZapIcon,
  ShieldCheck as ShieldIcon,
  TrendingUp as TrendingUpIcon,
  Landmark as LandmarkIcon,
  Radar as RadarIcon,
  Link2 as Link2Icon,
  Brain as BrainIcon,
  UserCheck as UserCheckIcon,
  ShieldAlert as ShieldAlertIcon,
  Activity as ActivityIcon,
  Check as CheckIcon,
  X as XIcon,
  ArrowUpRight as EscalateIcon,
  ChevronRight as ChevronRightIcon,
} from "lucide-react";
import CardFraudAgentPanel from "./CardFraudAgentPanel";
import {
  Card,
  KpiBlock,
  Chip,
  Button as UiButton,
  Alert as UiAlert,
  FreshnessTag,
  Segmented,
  DataTable,
  // v2 design system — role-based panel identity
  RolePanel,
  LiveDot,
  HeroPanel,
  HeroKpi as HeroKpiCell,
  Pipeline,
  PipelineStage,
  AgentWorkflow,
} from "../design/primitives";
// === DEMO MODE: live transaction tables disabled. Uncomment to restore. ===
// import BlockedTransactionsTable from "./analytics/BlockedTransactionsTable";
// import DeclineTable from "./analytics/DeclineTable";
// import WhitelistedTransactionsTable from "./analytics/WhitelistedTransactionsTable";
import VampAnalytics from "./analytics/VampAnalytics";
import RiskSummaryAnalytics from "./analytics/RiskSummaryAnalytics";
import ThreeDSAnalytics from "./analytics/ThreeDSAnalytics";
import LatencyAnalytics from "./analytics/LatencyAnalytics";
import { BinDistribution } from "./analytics/BinDistributionAnalytics";
import { RuleFrequency } from "./analytics/RuleFrequencyAnalytics";
// === DEMO MODE: API bases kept commented for easy revert. ===
// const DEFAULT_API_BASE =
//   process.env.REACT_APP_ANALYTICS_API_BASE_URL ||
//   "https://coral-app-2-4y6qg.ondigitalocean.app/analytics";
// const SUBSCRIBER_API_BASE =
//   process.env.REACT_APP_RULES_API_BASE_URL ||
//   "https://lionfish-app-yf78f.ondigitalocean.app/composite-backend/internal/ui/rules";
// const EXCLUDE_DEV = false;

const WINDOW_MAP = { "1h": 1, "24h": 24, "7d": 168, "30d": 720 };
// Window scales are strictly proportional to time:
//   1h  = 1/720  (≈ 0.00139)
//   24h = 1/30   (≈ 0.03333)
//   7d  = 7/30   (≈ 0.23333)
//   30d = 1.0
// This guarantees that the 24h-filter total can never exceed a single-day
// peak in the 30d chart, and that 7d total = 7 × single-day average, etc.
const WINDOW_SCALE = { "1h": 1 / 720, "24h": 1 / 30, "7d": 7 / 30, "30d": 1 };

const getRealm = () =>
  sessionStorage.getItem("active_realm") ||
  new URLSearchParams(window.location.search).get("realm") ||
  "Idenza";

function getTokenPayload() {
  const token =
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("access_token") ||
    Cookies.get("access_token");

  if (!token) return null;

  try {
    return JSON.parse(atob(token.split(".")[1]));
  } catch {
    return null;
  }
}

async function resolveSubscriber() {
  // === DEMO MODE: deterministic label from realm; live lookup commented below. ===
  const realm = getRealm();
  const label = realm
    ? realm.replace(/[_-]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
    : "Tenant";
  return { id: null, label };

  /* ORIGINAL — live subscriber resolver:
  const realm = getRealm();
  const fallbackLabel = realm
    ? realm.replace(/[_-]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
    : "Tenant";
  try {
    const res = await fetch(
      `${SUBSCRIBER_API_BASE}/get-subscriber-id?tenant_id=${encodeURIComponent(realm)}`
    );
    if (!res.ok) return { id: null, label: fallbackLabel };
    const data = await res.json();
    return {
      id: data.subscriber_id || null,
      label: data.subscriber_name || data.name || fallbackLabel,
    };
  } catch {
    return { id: null, label: fallbackLabel };
  }
  */
}

  const DISPLAY_NAMES = {
    epicsweep: "Epic Sweep"
  };

const shellStyle = {
  minHeight: "100vh",
  background: "var(--bg-app)",
  fontFamily: "'Inter Variable', 'Inter', system-ui, -apple-system, sans-serif",
  color: "var(--text-primary)",
};

// Enterprise-app-shell page header. 56px, sticky, theme-aware surface,
// hairline bottom border. Stripe/Attio/Linear/Ramp pattern.
const headerStyle = {
  height: 56,
  background: "var(--bg-surface)",
  borderBottom: "1px solid var(--border-subtle)",
  position: "sticky",
  top: 0,
  zIndex: 20,
  display: "flex",
  alignItems: "stretch",
  justifyContent: "center",
};

// Content column: max 1280 with 32px side padding, matches Stripe/Ramp/Mercury.
const CONTENT_MAX = 1280;
const contentColStyle = {
  width: "100%",
  maxWidth: CONTENT_MAX,
  margin: "0 auto",
  padding: "0 32px",
};
const bodyColStyle = {
  ...contentColStyle,
  paddingTop: 32,
  paddingBottom: 64,
};

// Shared icon-button style for the sticky header toolbar (refresh / theme).
// Theme-aware via CSS var tokens, so it flips with dark mode automatically.
const headerIconBtnStyle = {
  width: 28,
  height: 28,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  border: "1px solid var(--border-subtle)",
  borderRadius: 6,
  background: "var(--bg-surface)",
  color: "var(--text-secondary)",
  cursor: "pointer",
  padding: 0,
  transition: "background 120ms cubic-bezier(0.4, 0, 0.2, 1), color 120ms cubic-bezier(0.4, 0, 0.2, 1)",
};

const kpiGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(5, minmax(180px, 1fr))",
  gap: 16,
  marginBottom: 24,
};

const KpiCard = ({ label, value, sublabel, icon, accent }) => (
  <div
    style={{
      background: "#FFFFFF",
      borderRadius: 8,
      padding: "20px 24px",
      border: "1px solid rgba(0,0,0,0.06)",
      minHeight: 120,
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
    }}
  >
    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: 12,
          backgroundColor: accent || "#3B82F6",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          color: "#FFF",
          fontSize: 18,
          fontWeight: 700,
          boxShadow: "0 8px 18px rgba(15,23,42,0.12)",
        }}
      >
        {icon}
      </div>

      <span
        style={{
          fontSize: 13,
          color: "#475569",
          fontWeight: 800,
          lineHeight: 1.3,
        }}
      >
        {label}
      </span>
    </div>

    <div>
      <div
        style={{
          fontSize: 34,
          fontWeight: 850,
          letterSpacing: "-0.04em",
          color: "#0F172A",
        }}
      >
        {value}
      </div>

      {sublabel && (
        <div style={{ fontSize: 12, color: "#64748B", marginTop: 6 }}>
          {sublabel}
        </div>
      )}
    </div>
  </div>
);

const formatTimestamp = (ts) => {
  const d = new Date(ts * 1000);
  // UTC formatting so the chart x-axis matches the demo's "anchored to current
  // UTC" guarantee — the right-edge label is the closest UTC tick to now().
  return (
    d.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" }) +
    " " +
    d.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZone: "UTC",
    })
  );
};

// Helpers used by the inline time-series charts (Total Transactions, Outcome
// Rates Over Time). All return UTC-stamped buckets ending at the current
// UTC instant so the chart's right edge is always "now".
const fmtUtcDate = (d) =>
  d.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
const fmtUtcHourMin = (d) =>
  d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "UTC" });
const startOfUtcDay = (d) => {
  const out = new Date(d);
  out.setUTCHours(0, 0, 0, 0);
  return out;
};
const startOfUtcHour = (d) => {
  const out = new Date(d);
  out.setUTCMinutes(0, 0, 0);
  return out;
};
const startOfUtcMinuteBucket = (d, minutes) => {
  const out = new Date(d);
  const m = out.getUTCMinutes();
  out.setUTCMinutes(m - (m % minutes), 0, 0);
  return out;
};

// Bases here MUST stay in sync with MOCK_HERO so the trend graphs and the
// Hero KPI strip tell the same story.
const RATE_PROFILES = {
  idenza_deposit_issuer_decline_rate: { base: 9.8, swing: 0.9 },
  idenza_deposit_block_rate: { base: 2.25, swing: 0.35 },
  idenza_deposit_forced3ds_authenticated_rate: { base: 95.6, swing: 1.4 },
};

// Deterministic FNV-1a-style hash → noise in [-0.5, 0.5]. The pass-rate and
// the invert (fail-rate) chart both feed the same (metric, window, i) into
// this, so they share the same raw value and the inverted chart is exactly
// 100 − raw — guaranteeing the two series sum to 100% bucket-by-bucket.
const seededNoise = (metric, window, i) => {
  const s = `${metric}|${window}|${i}`;
  let h = 2166136261;
  for (let k = 0; k < s.length; k++) {
    h ^= s.charCodeAt(k);
    h = (h * 16777619) >>> 0;
  }
  return h / 4294967295 - 0.5;
};

const RateChart = ({ title, metric, selectedWindow, color, invert /* , apiBase */ }) => {
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);

  // === DEMO MODE: synthetic series; live fetch commented below. ===
  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => {
      const hours = WINDOW_MAP[selectedWindow] || 168;
      const points = selectedWindow === "1h" ? 12 : selectedWindow === "24h" ? 24 : selectedWindow === "7d" ? 28 : 30;
      const stepSec = (hours * 3600) / points;
      const profile = RATE_PROFILES[metric] || { base: 50, swing: 5 };
      const data = Array.from({ length: points }, (_, i) => {
        const ts = Date.now() / 1000 - (points - 1 - i) * stepSec;
        // NO sine waves — pure noise + spikes so the chart never looks
        // periodic. Four deterministic ingredients keyed off the same
        // (metric, window, i) so pass + fail charts mirror at 100 − raw:
        //   1. high-amplitude per-bucket jitter (±1.5 × swing)
        //   2. mean-reverted random walk for wandering drift
        //   3. ~30% buckets: medium spikes (±3 × swing)
        //   4. ~6% buckets: BIG event spikes (±8 × swing) — Issuer Decline
        //      can reach 14-15%, Block Rate ~5%.
        const jitter = seededNoise(metric, selectedWindow, i) * profile.swing * 1.5;
        let walk = 0;
        for (let k = 0; k <= i; k++) {
          walk += seededNoise(metric + "|walk", selectedWindow, k) * profile.swing * 0.35;
        }
        walk *= 0.45; // mean-revert so drift stays in band
        const spikeRoll = seededNoise(metric + "|spike", selectedWindow, i);
        const spike = Math.abs(spikeRoll) > 0.28
          ? Math.sign(spikeRoll) * (Math.abs(spikeRoll) - 0.28) * profile.swing * 3.2
          : 0;
        const rareRoll = seededNoise(metric + "|rare", selectedWindow, i);
        const rareSpike = Math.abs(rareRoll) > 0.42
          ? Math.sign(rareRoll) * profile.swing * 8.0
          : 0;
        let raw = profile.base + jitter + walk + spike + rareSpike;
        // Keep rates inside a believable band (no negatives, no >100%).
        raw = Math.max(0.05, Math.min(99.95, raw));
        const v = invert ? 100 - raw : raw;
        return { time: formatTimestamp(ts), value: +v.toFixed(2) };
      });
      setSeries(data);
      setLoading(false);
    }, 180);
    return () => clearTimeout(t);
  }, [metric, selectedWindow, invert]);

  /* ORIGINAL — live fetch against analytics service:
  useEffect(() => {
    let cancelled = false;
    const fetchSeries = async () => {
      setLoading(true);
      try {
        const realm = getRealm();
        const hours = WINDOW_MAP[selectedWindow] || 168;
        const step = hours <= 24 ? 300 : hours <= 168 ? 3600 : 7200;
        const url = `${apiBase}/deposit-outcomes-series?metric=${metric}&window=${selectedWindow}&hours=${hours}&step=${step}`;
        const res = await fetch(url, { headers: { "X-Tenant-Realm": realm } });
        if (!res.ok) return;
        const json = await res.json();
        if (!cancelled) {
          const data = (json.series || []).map((p) => ({
            time: formatTimestamp(p.timestamp),
            value: json.is_rate
              ? +(((invert ? 1 - p.value : p.value) * 100).toFixed(2))
              : p.value,
          }));
          setSeries(data);
        }
      } catch (err) {
        console.error(`Chart fetch error (${metric}):`, err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchSeries();
    return () => { cancelled = true; };
  }, [apiBase, metric, selectedWindow, invert]);
  */

  return (
    <ChartCard title={title} loading={loading}>
      {series.length === 0 ? (
        <div className="text-center py-3">
          <span style={{ fontSize: 12, color: "#6B7280" }}>
            No data available.
          </span>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={310}>
          <LineChart
            data={series}
            margin={{ top: 10, right: 22, bottom: 0, left: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="time"
              fontSize={10}
              interval="preserveStartEnd"
              angle={-25}
              textAnchor="end"
              height={54}
            />
            <YAxis fontSize={11} tickFormatter={(v) => `${v}%`} />
            <Tooltip formatter={(v) => [`${v}%`, title]} />
            <Line
              type="monotone"
              dataKey="value"
              stroke={color}
              dot={false}
              strokeWidth={2.5}
            />
          </LineChart>
        </ResponsiveContainer>
      )}
    </ChartCard>
  );
};

const EnterpriseTableSection = ({
  eyebrow,
  title,
  description,
  badge,
  badgeStyle,
  children,
}) => (
  <section
    style={{
      background: "#FFFFFF",
      border: "1px solid #D8DEE9",
      borderRadius: 20,
      marginTop: 24,
      boxShadow: "0 16px 40px rgba(15,23,42,0.08)",
      overflow: "hidden",
    }}
  >
    <div
      style={{
        padding: "18px 22px",
        borderBottom: "1px solid #E5E7EB",
        background: "linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        gap: 16,
      }}
    >
      <div>
        <div
          style={{
            fontSize: 11,
            fontWeight: 900,
            color: "#64748B",
            textTransform: "uppercase",
            letterSpacing: "0.10em",
            marginBottom: 5,
          }}
        >
          {eyebrow}
        </div>

        <h5
          style={{
            margin: 0,
            fontWeight: 900,
            color: "#0F172A",
            letterSpacing: "-0.02em",
          }}
        >
          {title}
        </h5>

        <p
          style={{
            margin: "7px 0 0",
            fontSize: 13,
            color: "#64748B",
            maxWidth: 920,
          }}
        >
          {description}
        </p>
      </div>

      <span
        style={{
          fontSize: 11,
          fontWeight: 900,
          padding: "7px 12px",
          borderRadius: 999,
          whiteSpace: "nowrap",
          border: "1px solid",
          ...badgeStyle,
        }}
      >
        {badge}
      </span>
    </div>

    <div
      style={{
        padding: 16,
        overflowX: "auto",
        maxHeight: 460,
        overflowY: "auto",
        background: "#FFFFFF",
      }}
    >
      {children}
    </div>
  </section>
);

// DEMO MODE — all fixtures below. Wire to real endpoints later; the shapes
// match the live analytics service contracts where they exist.
// Anchored to subscriber_id=26 (epicsweep) over its ~50-day window:
//   11,894 scored • allow 89.8% / review 5.9% / block 4.3%
//   Authenticated Completed 8,304 • Auth Failed Declined 935 → 3DS pass 89.9%
//   Issuer-decline-on-non-blocked 16.8% (Auth Declined + Bank Fraud / non-blocked)
//   Volume USD shape from sub-12 ($1.62M across 41k txns → ~$40 avg ticket)
// Hero numbers softened where it helps the story: block rate lower (less
// friction), issuer decline lower (better issuer behavior), authorization rate
// higher. Total + volume + funnel shape stay grounded in sub-26 reality.
// Baseline: ~1,000 txns/day. 30-day total kept slightly off the round 30,000
// (29,847) so the number doesn't read as a hand-picked anchor.
// Sub-26 shape preserved; absolute counts scaled by 29,847 / 11,894 ≈ 2.510.
const MOCK_HERO = {
  total_transactions: 29847,
  total_blocks: 671,           // 29,847 × 2.25%
  block_rate: 2.25,
  issuer_decline_rate: 9.8,
  // Authorization Pass + Fail = 100%. Both shown as % of total so the
  // user's "Hero × Total" math always works: 4.4% × 29,847 ≈ 1,313.
  threeds_pass_rate: 95.6,
  threeds_fail_rate: 4.4,
  total_volume_usd: 1_184_287, // 29,847 × ~$39.68 avg ticket
  // Every rate is % of total scored — Hero × Total lands on the table count:
  //   block_rate           × 29,847 = 671 blocked
  //   threeds_fail_rate    × 29,847 = 1,313 3DS-failed
  //   issuer_decline_rate  × 29,847 = 2,925 issuer-declined
  //   approval_rate        × 29,847 = 24,938 completed deposits
  // 2.25 + 4.40 + 9.80 + 83.55 = 100.0%.
  approval_rate: 83.55,
  window: "30d",
  deltas: {
    total_transactions: +12.4,
    total_blocks: -22.6,
    block_rate: -0.42,
    issuer_decline_rate: -1.6,
    threeds_pass_rate: +2.8,
    approval_rate: +1.6,
    total_volume_usd: +14.6,
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// SPONSOR-BANK MODEL — the dashboard is now viewed from the perspective of a
// sponsor bank overseeing multiple sponsored merchants. MOCK_HERO above is
// treated as the "Merchant A alone" baseline; MERCHANTS[epicsweep] mirrors it
// exactly, MERCHANTS[tigercash] adds the struggling sportsbook, and the
// aggregate is computed as a volume-weighted portfolio view so the bank's
// combined VAMP ratio lands JUST above the 50 bps above-standard threshold
// — dragged there by Merchant B. That's the narrative hook: the sponsor bank
// isn't in trouble because of Merchant A, it's in trouble because of one bad
// merchant, and the dashboard's job is to help them intervene surgically.
const MERCHANTS = {
  epicsweep: {
    id: "epicsweep",
    name: "Merchant A",
    vertical: "Social Casino",
    tenure_months: 36,
    monthly_transactions: 29847,
    block_rate: 2.25,
    issuer_decline_rate: 9.8,
    threeds_pass_rate: 95.6,
    threeds_fail_rate: 4.4,
    approval_rate: 83.55,
    total_volume_usd: 1_184_287,
    vamp_bps: 44,
    ecp_bps: 42,
    efm_bps: 4,
    chargeback_rate_pct: 0.40,
    fraud_to_sales_pct: 0.04,
    health_score: 76,
    health_label: "MODERATE",
    health_trend: "stable",
    accent: "#1D4ED8",
  },
  tigercash: {
    id: "tigercash",
    name: "Merchant B",
    vertical: "Sports Betting",
    tenure_months: 8,
    monthly_transactions: 14153,
    block_rate: 3.8,
    issuer_decline_rate: 14.2,
    threeds_pass_rate: 92.4,
    threeds_fail_rate: 7.6,
    approval_rate: 74.4,
    total_volume_usd: 712_441,
    vamp_bps: 78,     // ABOVE STANDARD (>50) — the problem child
    ecp_bps: 89,      // Also above the ECP watch threshold
    efm_bps: 11,      // Below the EFM 50 bps standard but rising
    chargeback_rate_pct: 0.71,
    fraud_to_sales_pct: 0.11,
    health_score: 42,
    health_label: "POOR",
    health_trend: "worsening",
    accent: "#DC2626",
  },
};

const MERCHANT_LIST = [MERCHANTS.epicsweep, MERCHANTS.tigercash];

// Volume-weighted portfolio view. Bank's VAMP for the aggregate lands at
// ~55 bps — just above the 50 bps above-standard line — because Merchant B
// drags it there. This is the "why does the bank care?" answer.
const computeAggregate = () => {
  const totalTxns = MERCHANT_LIST.reduce((s, m) => s + m.monthly_transactions, 0);
  const wavg = (field) =>
    MERCHANT_LIST.reduce((s, m) => s + m[field] * m.monthly_transactions, 0) / totalTxns;
  const blockRate = +wavg("block_rate").toFixed(2);
  const declineRate = +wavg("issuer_decline_rate").toFixed(2);
  const passRate = +wavg("threeds_pass_rate").toFixed(2);
  const failRate = +(100 - passRate).toFixed(2);
  const approvalRate = +(100 - blockRate - failRate - declineRate).toFixed(2);
  return {
    id: "all",
    name: "All Merchants",
    vertical: "Sponsor Portfolio",
    tenure_months: null,
    monthly_transactions: totalTxns,
    block_rate: blockRate,
    issuer_decline_rate: declineRate,
    threeds_pass_rate: passRate,
    threeds_fail_rate: failRate,
    approval_rate: approvalRate,
    total_volume_usd: MERCHANT_LIST.reduce((s, m) => s + m.total_volume_usd, 0),
    vamp_bps: Math.round(wavg("vamp_bps")),
    ecp_bps: Math.round(wavg("ecp_bps")),
    efm_bps: Math.round(wavg("efm_bps")),
    chargeback_rate_pct: +wavg("chargeback_rate_pct").toFixed(2),
    fraud_to_sales_pct: +wavg("fraud_to_sales_pct").toFixed(2),
    health_score: Math.round(wavg("health_score")),
    health_label: "AT RISK",
    health_trend: "worsening",
    accent: "#7C3AED",
  };
};

const MERCHANT_ALL = computeAggregate();
const MERCHANT_BY_ID = { all: MERCHANT_ALL, epicsweep: MERCHANTS.epicsweep, tigercash: MERCHANTS.tigercash };
const MERCHANT_OPTIONS = [MERCHANT_ALL, MERCHANTS.epicsweep, MERCHANTS.tigercash];

// Deterministic per-email merchant assignment for drilldown tables.
// Cycles by email hash so the same email always belongs to the same
// merchant across renders / windows.
const assignMerchant = (email) => {
  if (!email) return MERCHANTS.epicsweep;
  let hash = 0;
  for (let i = 0; i < email.length; i++) hash = (hash * 31 + email.charCodeAt(i)) >>> 0;
  // Weight ~68% to Merchant A, 32% to Merchant B — matches their txn share.
  return hash % 100 < 68 ? MERCHANTS.epicsweep : MERCHANTS.tigercash;
};

const seriesFrom = (base, swing, points = 14) =>
  Array.from({ length: points }, (_, i) => {
    const t = Date.now() - (points - 1 - i) * 3600 * 1000 * 6;
    const wob = Math.sin(i / 1.8) * swing + (Math.random() - 0.5) * swing * 0.5;
    return { time: t / 1000, value: Math.max(0, base + wob) };
  });

const MOCK_SPARKS = {
  total_transactions: seriesFrom(4750, 380),
  total_blocks: seriesFrom(87, 18),
  block_rate: seriesFrom(1.9, 0.25),
  issuer_decline_rate: seriesFrom(4.6, 0.5),
  threeds_pass_rate: seriesFrom(91, 1.6),
  approval_rate: seriesFrom(88.5, 1.1),
};

// Lifecycle stages — each drop derives from Hero rate × Total (29,847):
//   Scored             = MOCK_HERO.total_transactions     = 29,847
//   Risk Cleared       = Scored − block × total            = 29,847 −   671 = 29,176
//   3DS Cleared        = Risk Cleared − 3DS-fail × total   = 29,176 − 1,313 = 27,863
//   Deposit Completed  = 3DS Cleared − issuer-dec × total  = 27,863 − 2,925 = 24,938
//   End-to-end conversion = 24,938 / 29,847 = 83.55% (= approval_rate)
const MOCK_AUTH_FUNNEL = [
  { stage: "Scored", count: 29847, color: "#1D4ED8" },
  { stage: "Risk Cleared", count: 29176, color: "#3B82F6" },
  { stage: "3DS Cleared", count: 27863, color: "#10B981" },
  { stage: "Deposit Completed", count: 24938, color: "#047857" },
];

// All four KPIs reconcile from one underlying month at the 29,847 baseline:
//   monthly_txns      = 29,847 (matches Hero Total Transactions)
//   avg_cb_amount     = $133 (internal; never rendered, no trailing zero)
//   chargeback_rate   = 0.40% → 29,847 × 0.0040 ≈ 119 chargebacks / month
//   total_cb_exposure = 119 × $133 ≈ $15,827
//   dispute_win_rate  = 81.4%
//   recovered_usd     = $15,827 × 0.814 ≈ $12,884
//   fraud_to_sales    = 0.04% (TC40-fraud subset)
//   VAMP ratio        = 0.40 + 0.04 = 0.44% → 44 bps (below-standard < 50)
const MOCK_RECOVERY = {
  monthly_chargebacks: 119,
  avg_cb_amount: 133,
  dispute_win_rate: 81.4,
  recovered_usd: 12884,
  fraud_to_sales_pct: 0.04,
  chargeback_rate_pct: 0.40,
  ce30_uplift_pct: 42,
};

// VAMP monthly trend reconciled with the rest of the dashboard:
//   • Latest-month transactions  = 29,847 (matches Hero Total Transactions)
//   • Latest-month refund rate   = 0.44%  (matches Visa VAMP card = 44 bps)
//   • Latest-month refund count  = 29,847 × 0.0044 ≈ 131
//   • Refund rate trends DOWN month over month — idenza catches abusive
//     payers earlier, leaving cleaner traffic in the VAMP denominator.
//   • Every refunds value is computed as transactions × refundRate so the
//     three columns reconcile bucket-by-bucket.
//   • Rates picked to avoid trailing-zero display (0.61 / 0.51 etc.).
const MOCK_VAMP_TREND = [
  { month: "Jul 25", transactions: 17068, refunds: 133, refundRate: 0.78 },
  { month: "Aug 25", transactions: 18574, refunds: 134, refundRate: 0.72 },
  { month: "Sep 25", transactions: 20331, refunds: 134, refundRate: 0.66 },
  { month: "Oct 25", transactions: 22339, refunds: 136, refundRate: 0.61 },
  { month: "Nov 25", transactions: 23343, refunds: 131, refundRate: 0.56 },
  { month: "Dec 25", transactions: 24347, refunds: 127, refundRate: 0.52 },
  { month: "Jan 26", transactions: 25351, refunds: 129, refundRate: 0.51 },
  { month: "Feb 26", transactions: 26104, refunds: 125, refundRate: 0.48 },
  { month: "Mar 26", transactions: 27108, refunds: 125, refundRate: 0.46 },
  { month: "Apr 26", transactions: 28112, refunds: 127, refundRate: 0.45 },
  { month: "May 26", transactions: 28991, refunds: 128, refundRate: 0.44 },
  { month: "Jun 26", transactions: 29847, refunds: 131, refundRate: 0.44 },
];

// Risk tier counts reconciled with the action-decision split (29,847 total):
//   HIGH    → Block   →    671 (Hero block_rate × total = 0.0225 × 29,847)
//   MEDIUM  → Review  →  1,767 (sub-26 review share 5.92% × 29,847)
//   LOW     → Allow   → 27,409 (= 29,847 − 671 − 1,767)
const MOCK_RISK_TIER = [
  { name: "LOW", value: 27409, percent: 91.83 },
  { name: "MEDIUM", value: 1767, percent: 5.92 },
  { name: "HIGH", value: 671, percent: 2.25 },
];

// decision_tier distribution from sub-26 mv_user_outcomes, scaled to 29,847.
const MOCK_3DS_POSTURE = [
  { name: "Force 3DS", value: 20029, percent: 67.11 },
  { name: "Prefer 3DS", value: 9745, percent: 32.65 },
  { name: "Bypass 3DS", value: 73, percent: 0.24 },
];

// Top BINs straight from sub-26 mv_user_outcomes. Approval/decline values
// chosen so the volume-weighted average approval lands at ~91% (Hero is
// 90.2% across ALL BINs; top BINs by volume run a touch cleaner) and the
// volume-weighted decline lands near the Hero issuer_decline_rate of 9.8%.
// For each row, approval + decline ≈ 100% — every BIN row sums itself.
// BIN volumes scaled by 2.510 (29,847 / 11,894), sub-26 shape preserved.
// Volumes nudged off round numbers so they never end in 0.
const MOCK_TOP_ISSUERS = [
  { bin: "403163", issuer: "Visa — Chase", scheme: "Visa", country: "US", volume: 4515, approval: 92.4, decline: 7.6 },
  { bin: "514377", issuer: "Mastercard — Synchrony", scheme: "Mastercard", country: "US", volume: 3821, approval: 91.6, decline: 8.4 },
  { bin: "423223", issuer: "Visa — Capital One", scheme: "Visa", country: "US", volume: 2223, approval: 90.5, decline: 9.5 },
  { bin: "400895", issuer: "Visa — Bank of America", scheme: "Visa", country: "US", volume: 1084, approval: 93.8, decline: 6.2 },
  { bin: "498503", issuer: "Visa — US Bank", scheme: "Visa", country: "US", volume: 613, approval: 90.2, decline: 9.8 },
  { bin: "486208", issuer: "Visa — Wells Fargo", scheme: "Visa", country: "US", volume: 561, approval: 91.7, decline: 8.3 },
  { bin: "447227", issuer: "Visa — Citi", scheme: "Visa", country: "US", volume: 507, approval: 90.6, decline: 9.4 },
  { bin: "440393", issuer: "Visa — Chase (debit)", scheme: "Visa", country: "US", volume: 482, approval: 92.1, decline: 7.9 },
  { bin: "542543", issuer: "Mastercard — Capital One", scheme: "Mastercard", country: "US", volume: 427, approval: 90.4, decline: 9.6 },
  { bin: "457431", issuer: "Visa — PNC", scheme: "Visa", country: "US", volume: 371, approval: 90.1, decline: 9.9 },
];

const MOCK_PROGRAM_LEADERBOARD = [
  {
    program: "Epic Sweep — Main",
    volume_usd: 6_420_000,
    portfolio_pct: 34.8,
    top5_cp_share: 41.2,
    non_us_share: 12.4,
    high_value_share: 22.8,
    scores: { volume: 2, concentration: 1, cross_border: 0, high_value: 2, total: 5 },
    rating: "MEDIUM",
  },
  {
    program: "CasinoKai — Fyntek",
    volume_usd: 4_810_000,
    portfolio_pct: 26.1,
    top5_cp_share: 38.1,
    non_us_share: 8.4,
    high_value_share: 18.3,
    scores: { volume: 2, concentration: 1, cross_border: 0, high_value: 1, total: 4 },
    rating: "MEDIUM",
  },
  {
    program: "Daily Fantasy Pro",
    volume_usd: 2_980_000,
    portfolio_pct: 16.2,
    top5_cp_share: 28.5,
    non_us_share: 4.2,
    high_value_share: 9.8,
    scores: { volume: 1, concentration: 0, cross_border: 0, high_value: 0, total: 1 },
    rating: "LOW",
  },
  {
    program: "Sportsbook Plus",
    volume_usd: 2_140_000,
    portfolio_pct: 11.6,
    top5_cp_share: 51.4,
    non_us_share: 21.6,
    high_value_share: 31.2,
    scores: { volume: 1, concentration: 2, cross_border: 1, high_value: 2, total: 6 },
    rating: "HIGH",
  },
  {
    program: "Tribal Gaming Net",
    volume_usd: 1_280_000,
    portfolio_pct: 6.9,
    top5_cp_share: 24.1,
    non_us_share: 3.1,
    high_value_share: 7.4,
    scores: { volume: 1, concentration: 0, cross_border: 0, high_value: 0, total: 1 },
    rating: "LOW",
  },
  {
    program: "iLottery Express",
    volume_usd: 798_000,
    portfolio_pct: 4.3,
    top5_cp_share: 19.8,
    non_us_share: 2.4,
    high_value_share: 5.1,
    scores: { volume: 0, concentration: 0, cross_border: 0, high_value: 0, total: 0 },
    rating: "LOW",
  },
];

// Top rules from real fire history (sub-12), scaled by 2.510. Counts nudged
// off round numbers (e.g. 3,091 not 3,090) so they never end in 0.
const MOCK_RULE_FREQUENCY = [
  { rule: "Visitor BIN Fanout Suspicious", count: 3926 },
  { rule: "FORCE-3DS User", count: 3091 },
  { rule: "Visitor reuse", count: 1087 },
  { rule: "Visitor BIN Fanout High Risk", count: 1059 },
  { rule: "VPN usage", count: 821 },
  { rule: "Velocity 7d Group", count: 683 },
  { rule: "Velocity 30m Event", count: 552 },
  { rule: "Low Value Slow Burn Activity", count: 537 },
  { rule: "Velocity 30m Group", count: 487 },
  { rule: "Whitelisted User", count: 457 },
];

// BIN distribution matches MOCK_TOP_ISSUERS volumes (scaled to 29,847 base).
const MOCK_BIN_DISTRIBUTION = [
  { bin: "403163", count: 4515 },
  { bin: "514377", count: 3821 },
  { bin: "423223", count: 2223 },
  { bin: "400895", count: 1084 },
  { bin: "498503", count: 613 },
  { bin: "486208", count: 561 },
  { bin: "447227", count: 507 },
  { bin: "440393", count: 482 },
  { bin: "542543", count: 427 },
  { bin: "457431", count: 371 },
];

// Latency stats grounded in sub-12 mv_outbox_txn_join.
//   p50: 129 ms • p95: 506 ms • p99: 4.83 s • avg: 262 ms
// The LatencyAnalytics component expects minutes upstream; convert ms→min.
const MOCK_LATENCY = {
  stats: { average: 0.262 / 60, maximum: 4.828 / 60, minimum: 0.018 / 60 },
  chartData: Array.from({ length: 15 }, (_, i) => {
    const baseMs = 129 + Math.sin(i / 1.4) * 80 + Math.random() * 40;
    return {
      index: i,
      order_key: `req_${(1700 + i).toString(16)}`,
      valueMs: baseMs,
    };
  }),
};

// Transaction IDs follow a single TXN-NNNNNN scheme across all four tables so
// the same transaction can be cross-referenced (e.g. a TXN appears in Blocked
// then doesn't appear elsewhere — vs Issuer Declined which never appears in
// Blocked since blocked txns never reach the issuer). Cards / BINs map back to
// MOCK_TOP_ISSUERS for visual consistency.
// === Shared pools for the drilldown mocks =================================
// 50 distinct emails (first batch lifted from the real sub-12 sample, rest
// synthesized in the same shape) so paging keeps showing fresh entries.
const EMAIL_POOL = [
  "missysmith2669@gmail.com", "greenmonkey270015@gmail.com", "crismcintosh46@gmail.com", "stephanieclapp6@gmail.com",
  "uloveme723@gmail.com", "blissb048@gmail.com", "jonathanzollerdallas@gmail.com", "ziyahsworld21@gmail.com",
  "daisyflor91@gmail.com", "marlondrive83@gmail.com", "lhinton44@gmail.com", "kbeacon77@gmail.com",
  "rtorrespark@gmail.com", "saramurillo28@gmail.com", "lukasprime19@gmail.com", "carrieflagler@gmail.com",
  "nicodeyoung41@gmail.com", "veronicaring22@gmail.com", "owens.matthew@gmail.com", "bigskytravis2@gmail.com",
  "elenastreaming@gmail.com", "vince.lambert88@gmail.com", "tashaforrest1@gmail.com", "carrolljonesgolf@gmail.com",
  "maxedmonds94@gmail.com", "ericaplays11@gmail.com", "monsterjake42@gmail.com", "kevinl.weeks@gmail.com",
  "saralynnchavez@gmail.com", "patrickrivers8@gmail.com", "lindseyhart7@gmail.com", "evanjordan23@gmail.com",
  "mariastokes16@gmail.com", "noahburton55@gmail.com", "rosegonzales77@gmail.com", "calvinwoodward3@gmail.com",
  "sianlawson92@gmail.com", "diegoramsey44@gmail.com", "ireneharvey21@gmail.com", "ericrodriguez76@gmail.com",
  "jamiebrooke9@gmail.com", "amysilva55@gmail.com", "russellpark62@gmail.com", "yvettecoles18@gmail.com",
  "dominickline37@gmail.com", "marcuspaige29@gmail.com", "tinacarter63@gmail.com", "bradynguyen12@gmail.com",
  "harleyfox84@gmail.com", "raulhuerta11@gmail.com",
];

const BIN_POOL = [
  { bin: "403163", issuer: "Visa — Chase" },
  { bin: "514377", issuer: "Mastercard — Synchrony" },
  { bin: "423223", issuer: "Visa — Capital One" },
  { bin: "486208", issuer: "Visa — Wells Fargo" },
  { bin: "447227", issuer: "Visa — Citi" },
  { bin: "400895", issuer: "Visa — Bank of America" },
  { bin: "440393", issuer: "Visa — Chase (debit)" },
  { bin: "457431", issuer: "Visa — PNC" },
  { bin: "542543", issuer: "Mastercard — Capital One" },
  { bin: "498503", issuer: "Visa — US Bank" },
];

const LAST4_POOL = ["4218", "1004", "8821", "6620", "1142", "2210", "4490", "3318", "0102", "0337",
                    "5612", "8740", "3119", "9024", "7351", "0418", "2986", "1573", "6842", "4951"];

const AMOUNT_POOL_BLOCKED = [42, 38, 19, 25, 68, 50, 22, 36, 28, 60, 15, 90, 45, 75, 33, 120, 56, 18, 84, 47];
const AMOUNT_POOL_3DS     = [25, 30, 45, 22, 60, 18, 80, 35, 42, 28, 55, 12, 95, 38, 50, 24];
const AMOUNT_POOL_DECLINE = [28, 35, 50, 20, 40, 24, 32, 75, 18, 60, 95, 22, 48, 36, 110, 16];
const AMOUNT_POOL_WHITELIST = [30, 25, 120, 55, 18, 200, 35, 60, 85, 45, 150, 95, 22, 75, 280, 40];

// Real sub-12 rules_fired combinations (top 15 by frequency).
const BLOCK_REASON_COMBOS = [
  ["FORCE-3DS User", "Visitor reuse", "Visitor BIN Fanout Suspicious", "Visitor BIN Fanout High Risk"],
  ["Blacklisted User"],
  ["Visitor BIN Fanout Suspicious", "Visitor BIN Fanout High Risk", "VPN usage"],
  ["Visitor reuse", "Visitor reuse High Risk", "Visitor BIN Fanout Suspicious", "Visitor BIN Fanout High Risk"],
  ["FORCE-3DS User", "Visitor reuse", "Visitor reuse High Risk", "Visitor BIN Fanout Suspicious", "Visitor BIN Fanout High Risk"],
  ["FORCE-3DS User", "Low Value Slow Burn Activity", "Cumulative Abuse Daily Friction", "Velocity 24h Group"],
  ["FORCE-3DS User", "Visitor BIN Fanout Suspicious", "Visitor BIN Fanout High Risk", "VPN usage"],
  ["FORCE-3DS User", "Visitor reuse", "Visitor reuse High Risk", "Visitor BIN Fanout Suspicious"],
  ["FORCE-3DS User", "Visitor BIN Fanout Suspicious", "Visitor BIN Fanout High Risk", "Velocity 30m Event", "Velocity 30m Group"],
  ["FORCE-3DS User", "Visitor BIN Fanout Suspicious", "Low Value Slow Burn Activity", "Velocity 24h Group"],
  ["FORCE-3DS User", "Low Value Slow Burn Activity", "Velocity 24h Event", "Velocity 24h Group"],
  ["FORCE-3DS User", "High Confidence Card Testing", "Low Value Slow Burn Activity", "Velocity 24h Group"],
  ["FORCE-3DS User", "Visitor reuse", "Visitor reuse High Risk", "Visitor BIN Fanout Suspicious", "Visitor BIN Fanout High Risk", "VPN usage"],
  ["FORCE-3DS User", "Low Value Slow Burn Activity", "Cumulative Abuse Daily Friction", "Velocity 24h Group", "Velocity 7d Group"],
  ["FORCE-3DS User", "Visitor BIN Fanout Suspicious", "Low Value Slow Burn Activity", "Velocity 7d Event", "Velocity 7d Group"],
];

const THREEDS_REASON_POOL = ["failed-authentication", "cancelled", "challenge-failed"];

const ISSUER_DECLINE_POOL = [
  { code: "51", reason: "INSUFFICIENT_FUNDS" },
  { code: "59", reason: "FRAUD_DETECTED_BY_ISSUER" },
  { code: "05", reason: "GENERIC_DECLINE" },
  { code: "05", reason: "DO_NOT_HONOR" },
  { code: "57", reason: "TRANSACTION_NOT_PERMITTED" },
  { code: "61", reason: "EXCEEDS_APPROVAL_LIMIT" },
  { code: "82", reason: "INVALID_CVV" },
  { code: "62", reason: "RESTRICTED_CARD" },
  { code: "54", reason: "EXPIRED_CARD" },
];

const cycle = (arr, i) => arr[i % arr.length];
const txnId = (start, i, step = 7) => `TXN-${start - i * step}`;

// 50 base rows for each drilldown table — keeps paging fresh for the first
// few pages even at the 30d window. shiftRowsForWindow then anchors the
// timestamps to "now − window" before rendering.
const MOCK_BLOCKED_TXNS = Array.from({ length: 50 }, (_, i) => {
  const bin = cycle(BIN_POOL, i);
  return {
    id: txnId(748241, i),
    ts: "2026-06-15 12:14", // placeholder — shiftRowsForWindow overwrites
    email: cycle(EMAIL_POOL, i),
    masked: `${bin.bin}••••${cycle(LAST4_POOL, i)}`,
    amount: cycle(AMOUNT_POOL_BLOCKED, i),
    currency: "USD",
    risk: i % 11 === 0 ? "MEDIUM" : "HIGH",
    country: "US",
    reasons: cycle(BLOCK_REASON_COMBOS, i),
  };
});

const MOCK_3DS_FAILED = Array.from({ length: 50 }, (_, i) => {
  const bin = cycle(BIN_POOL, i + 2);
  return {
    id: txnId(748238, i, 9),
    ts: "2026-06-15 12:08",
    email: cycle(EMAIL_POOL, i + 8),
    masked: `${bin.bin}••••${cycle(LAST4_POOL, i + 3)}`,
    amount: cycle(AMOUNT_POOL_3DS, i),
    reason: cycle(THREEDS_REASON_POOL, i),
    issuer: bin.issuer,
  };
});

const MOCK_ISSUER_DECLINED = Array.from({ length: 50 }, (_, i) => {
  const bin = cycle(BIN_POOL, i + 1);
  const dec = cycle(ISSUER_DECLINE_POOL, i);
  return {
    id: txnId(748245, i, 11),
    ts: "2026-06-15 12:21",
    email: cycle(EMAIL_POOL, i + 14),
    masked: `${bin.bin}••••${cycle(LAST4_POOL, i + 5)}`,
    amount: cycle(AMOUNT_POOL_DECLINE, i),
    code: dec.code,
    reason: dec.reason,
    issuer: bin.issuer,
  };
});

const MOCK_WHITELISTED_TXNS = Array.from({ length: 50 }, (_, i) => {
  const bin = cycle(BIN_POOL, i + 3);
  const isDeclined = i % 11 === 3; // ~9% declined; rest completed
  return {
    id: txnId(748255, i, 13),
    ts: "2026-06-15 12:32",
    email: cycle(EMAIL_POOL, i + 21),
    masked: `${bin.bin}••••${cycle(LAST4_POOL, i + 7)}`,
    amount: cycle(AMOUNT_POOL_WHITELIST, i),
    status: isDeclined ? "declined" : "completed",
    decline_reason: isDeclined ? cycle(ISSUER_DECLINE_POOL, i).reason : undefined,
    issuer: bin.issuer,
    segment: i % 8 === 0 ? "VIP" : "Trusted",
  };
});

// === The original 8-row hand-curated mocks (kept here for revert / inspection
// when the new generator-based set above isn't desired) ====================
/*
const MOCK_BLOCKED_TXNS_ORIGINAL = [
  { id: "TXN-748241", ts: "2026-06-15 12:14", email: "missysmith2669@gmail.com", masked: "403163••••4218", amount: 42, currency: "USD", risk: "HIGH", country: "US",
    reasons: ["FORCE-3DS User", "Visitor reuse", "Visitor BIN Fanout Suspicious", "Visitor BIN Fanout High Risk"] },
  { id: "TXN-748204", ts: "2026-06-15 11:48", email: "greenmonkey270015@gmail.com", masked: "514377••••1004", amount: 38, currency: "USD", risk: "HIGH", country: "US",
    reasons: ["Visitor BIN Fanout Suspicious", "Visitor BIN Fanout High Risk", "VPN usage"] },
  { id: "TXN-748187", ts: "2026-06-15 11:31", email: "crismcintosh46@gmail.com", masked: "423223••••8821", amount: 19, currency: "USD", risk: "HIGH", country: "US",
    reasons: ["Blacklisted User"] },
  { id: "TXN-748156", ts: "2026-06-15 11:02", email: "stephanieclapp6@gmail.com", masked: "542543••••6620", amount: 25, currency: "USD", risk: "HIGH", country: "US",
    reasons: ["FORCE-3DS User", "Low Value Slow Burn Activity", "Cumulative Abuse Daily Friction", "Velocity 24h Group"] },
  { id: "TXN-748112", ts: "2026-06-15 10:44", email: "uloveme723@gmail.com", masked: "486208••••1142", amount: 68, currency: "USD", risk: "HIGH", country: "US",
    reasons: ["FORCE-3DS User", "Visitor reuse", "Visitor reuse High Risk", "Visitor BIN Fanout Suspicious", "Visitor BIN Fanout High Risk"] },
  { id: "TXN-748084", ts: "2026-06-15 10:18", email: "blissb048@gmail.com", masked: "447227••••2210", amount: 50, currency: "USD", risk: "HIGH", country: "US",
    reasons: ["FORCE-3DS User", "Visitor BIN Fanout Suspicious", "Visitor BIN Fanout High Risk", "VPN usage"] },
  { id: "TXN-748061", ts: "2026-06-15 10:01", email: "jonathanzollerdallas@gmail.com", masked: "400895••••4490", amount: 22, currency: "USD", risk: "HIGH", country: "US",
    reasons: ["FORCE-3DS User", "High Confidence Card Testing", "Low Value Slow Burn Activity", "Velocity 24h Group"] },
  { id: "TXN-748033", ts: "2026-06-15 09:42", email: "ziyahsworld21@gmail.com", masked: "457431••••3318", amount: 36, currency: "USD", risk: "HIGH", country: "US",
    reasons: ["FORCE-3DS User", "Visitor BIN Fanout Suspicious", "Visitor BIN Fanout High Risk", "Velocity 30m Event", "Velocity 30m Group"] },
];

// 3DS-fail reasons narrowed to the three categories that actually account for
// the bulk of 3DS failures in production:
//   failed-authentication — auth step rejected by ACS (ARES=N) — 657 in sub-26
//   cancelled            — user closed/dismissed the challenge — 190 in sub-26
//   challenge-failed     — challenge attempted but didn't complete
//                          (covers protocol-error / timeout / acs-unavailable)
const MOCK_3DS_FAILED = [
  { id: "TXN-748238", ts: "2026-06-15 12:08", email: "daisyflor91@gmail.com", masked: "403163••••4218", amount: 25, reason: "failed-authentication", issuer: "Visa — Chase" },
  { id: "TXN-748219", ts: "2026-06-15 11:54", email: "marlondrive83@gmail.com", masked: "514377••••1004", amount: 30, reason: "failed-authentication", issuer: "Mastercard — Synchrony" },
  { id: "TXN-748201", ts: "2026-06-15 11:39", email: "lhinton44@gmail.com", masked: "423223••••8821", amount: 45, reason: "cancelled", issuer: "Visa — Capital One" },
  { id: "TXN-748175", ts: "2026-06-15 11:22", email: "kbeacon77@gmail.com", masked: "486208••••6620", amount: 22, reason: "challenge-failed", issuer: "Visa — Wells Fargo" },
  { id: "TXN-748147", ts: "2026-06-15 10:58", email: "rtorrespark@gmail.com", masked: "447227••••1142", amount: 60, reason: "failed-authentication", issuer: "Visa — Citi" },
  { id: "TXN-748121", ts: "2026-06-15 10:31", email: "saramurillo28@gmail.com", masked: "457431••••3318", amount: 18, reason: "cancelled", issuer: "Visa — PNC" },
];

// Issuer-decline reasons from sub-26 bank_decline_code, ranked by hits:
//   INSUFFICIENT_FUNDS (660) • FRAUD_DETECTED_BY_ISSUER (289) •
//   GENERIC_DECLINE (285) • DO_NOT_HONOR (193) •
//   TRANSACTION_NOT_PERMITTED (124) • EXCEEDS_APPROVAL_LIMIT (83) •
//   INVALID_CVV (81). ISO 8583 numeric codes kept alongside for context.
const MOCK_ISSUER_DECLINED = [
  { id: "TXN-748245", ts: "2026-06-15 12:21", email: "lukasprime19@gmail.com", masked: "403163••••4218", amount: 28, code: "51", reason: "INSUFFICIENT_FUNDS", issuer: "Visa — Chase" },
  { id: "TXN-748228", ts: "2026-06-15 12:02", email: "carrieflagler@gmail.com", masked: "514377••••1004", amount: 35, code: "59", reason: "FRAUD_DETECTED_BY_ISSUER", issuer: "Mastercard — Synchrony" },
  { id: "TXN-748212", ts: "2026-06-15 11:46", email: "nicodeyoung41@gmail.com", masked: "423223••••8821", amount: 50, code: "05", reason: "GENERIC_DECLINE", issuer: "Visa — Capital One" },
  { id: "TXN-748194", ts: "2026-06-15 11:28", email: "veronicaring22@gmail.com", masked: "542543••••6620", amount: 20, code: "05", reason: "DO_NOT_HONOR", issuer: "Mastercard — Capital One" },
  { id: "TXN-748167", ts: "2026-06-15 11:11", email: "owens.matthew@gmail.com", masked: "498503••••1142", amount: 40, code: "57", reason: "TRANSACTION_NOT_PERMITTED", issuer: "Visa — US Bank" },
  { id: "TXN-748139", ts: "2026-06-15 10:49", email: "bigskytravis2@gmail.com", masked: "400895••••2210", amount: 24, code: "61", reason: "EXCEEDS_APPROVAL_LIMIT", issuer: "Visa — Bank of America" },
  { id: "TXN-748098", ts: "2026-06-15 10:22", email: "elenastreaming@gmail.com", masked: "440393••••4490", amount: 32, code: "82", reason: "INVALID_CVV", issuer: "Visa — Chase (debit)" },
];

// Post-whitelist activity: deposit_status is mostly 'completed'. A declined
// outcome here only happens when the issuer itself declines (decline_reason
// retained so the row still makes sense if the column comes back later).
const MOCK_WHITELISTED_TXNS = [
  { id: "TXN-748255", ts: "2026-06-15 12:32", email: "vince.lambert88@gmail.com", masked: "403163••••4218", amount: 30, status: "completed", issuer: "Visa — Chase", segment: "Trusted" },
  { id: "TXN-748236", ts: "2026-06-15 12:18", email: "tashaforrest1@gmail.com", masked: "514377••••1004", amount: 25, status: "completed", issuer: "Mastercard — Synchrony", segment: "Trusted" },
  { id: "TXN-748220", ts: "2026-06-15 11:55", email: "carrolljonesgolf@gmail.com", masked: "423223••••8821", amount: 120, status: "completed", issuer: "Visa — Capital One", segment: "VIP" },
  { id: "TXN-748199", ts: "2026-06-15 11:34", email: "maxedmonds94@gmail.com", masked: "486208••••6620", amount: 55, status: "completed", issuer: "Visa — Wells Fargo", segment: "Trusted" },
  { id: "TXN-748172", ts: "2026-06-15 11:17", email: "ericaplays11@gmail.com", masked: "400895••••1142", amount: 18, status: "declined", decline_reason: "51 — Insufficient funds", issuer: "Visa — Bank of America", segment: "Trusted" },
  { id: "TXN-748134", ts: "2026-06-15 10:42", email: "monsterjake42@gmail.com", masked: "440393••••2210", amount: 200, status: "completed", issuer: "Visa — Chase (debit)", segment: "VIP" },
  { id: "TXN-748112", ts: "2026-06-15 10:24", email: "kevinl.weeks@gmail.com", masked: "514377••••6620", amount: 35, status: "completed", issuer: "Mastercard — Synchrony", segment: "Trusted" },
  { id: "TXN-748088", ts: "2026-06-15 10:08", email: "saralynnchavez@gmail.com", masked: "447227••••2210", amount: 60, status: "completed", issuer: "Visa — Citi", segment: "Trusted" },
];
*/

// Operator-facing compliance + player-risk metrics. Sources used in selection:
//   FinCEN BSA/AML program structure (SAR/CTR/OFAC workflows)
//   GeoComply / US state igaming geolocation standards
//   National + per-state self-exclusion registries
//   Responsible gaming flagging (loss-velocity, spend triggers)
const MOCK_BSA_AML = {
  sar_filed_ytd: 412,
  sar_in_queue: 18,
  sar_sla_breached: 2,
  sar_avg_days_to_file: 21.4,
  ofac_screening_hits_30d: 64,
  ofac_true_match: 3,
  ctr_aggregation_alerts_30d: 89,
  pep_adverse_media_hits: 27,
  kyc_cip_completion_rate: 98.2,
  cdd_edd_coverage: 96.8,
  txn_monitoring_alerts_30d: 1842,
  txn_monitoring_cleared_pct: 90.7,
  audit_log_coverage: 99.98,
};

const MOCK_IGAMING_RISK = {
  geocomply_pass_rate: 99.4,
  geocomply_blocked_30d: 1842,
  vpn_proxy_blocked: 612,
  spoofing_attempts: 218,
  self_exclusion_hits_30d: 47,
  state_exclusion_hits_30d: 31,
  age_verification_failed: 89,
  multi_account_flags: 24,
  out_of_state_attempts: 1204,
  responsible_gaming_flags: 56,
  underage_attempts: 4,
};

const MOCK_TM_SCENARIOS = [
  { scenario: "Structuring — sub-$10K aggregation", count: 412, severity: "HIGH" },
  { scenario: "Rapid-fire deposits + withdrawals", count: 318, severity: "HIGH" },
  { scenario: "Geographic anomaly — cross-state burst", count: 287, severity: "MEDIUM" },
  { scenario: "Funnel account pattern", count: 204, severity: "HIGH" },
  { scenario: "First-deposit velocity excess", count: 188, severity: "MEDIUM" },
  { scenario: "Prepaid + bonus abuse signal", count: 142, severity: "MEDIUM" },
  { scenario: "Device fingerprint reuse", count: 98, severity: "MEDIUM" },
  { scenario: "Self-exclusion list near-match", count: 47, severity: "HIGH" },
];

const MOCK_SCHEME_COMPLIANCE = [
  {
    program: "Visa VAMP",
    scope: "Merchant — CNP only",
    ratioBps: 44,
    aboveStdBps: 50,
    excessiveBps: 150,
    note: "TC40 fraud + TC15 disputes / TC05 settled",
  },
  {
    program: "Mastercard ECP",
    // ECP tracks all chargebacks against prior-month sales. With sales growing
    // MoM the prior-month denominator is slightly smaller than current, so
    // ECP ratio sits a touch above our headline chargeback rate (0.40%).
    scope: "All chargebacks / prior-month sales",
    ratioBps: 42,
    aboveStdBps: 150,
    excessiveBps: 300,
    note: "≥100 CB & ≥1.5% triggers ECM; ≥300 & ≥3% triggers HECM",
  },
  {
    program: "Mastercard EFM",
    // EFM is the fraud-to-sales ratio — exactly the Hero "Fraud-to-Sales"
    // sublabel (0.04%) once expressed in basis points.
    scope: "Fraud only — eCom",
    ratioBps: 4,
    aboveStdBps: 50,
    excessiveBps: 100,
    note: "Fraud-to-sales ratio across 3 consecutive months",
  },
];

const MOCK_AUTH_HEALTH = {
  approvalRate: 86.4,
  softDeclineRate: 9.2,
  hardDeclineRate: 4.4,
  avgAuthLatencyMs: 412,
};

// Top Decline Reasons reconciled with Hero (29,847 baseline):
//   • Issuer-side total = issuer_decline_rate × total = 9.8% × 29,847 = 2,925
//   • 3DS-side total    = threeds_fail_rate × total   = 4.4% × 29,847 = 1,313
//   • Top-10 sum = 4,141 (1032+918+747+453+265+195+144+131+129+127)
//                ≈ 4,238 (full issuer + 3DS) minus ~97 in tail-of-distribution
//                  minor codes (Restricted Card, Expired Card, etc.)
//   • Within-category mix preserves real sub-26 bank_decline_code ratios.
//   • Share % = count / 29,847 so table % reconciles with Hero math.
//   • Counts adjusted to avoid trailing-zero endings.
const MOCK_DECLINE_REASONS = [
  { code: "51", label: "Insufficient funds", count: 1032, share: 3.46 },          // 2925 × (658/1864)
  { code: "—", label: "Failed 3DS authentication", count: 918, share: 3.07 },    // 1313 × (657/940)
  { code: "05", label: "Do not honor (generic + DNH)", count: 747, share: 2.50 }, // 2925 × (476/1864)
  { code: "59", label: "Suspected fraud by issuer", count: 453, share: 1.52 },   // 2925 × (289/1864)
  { code: "—", label: "Cancelled by user", count: 265, share: 0.89 },            // 1313 × (190/940)
  { code: "57", label: "Transaction not permitted", count: 195, share: 0.65 },   // 2925 × (124/1864)
  { code: "—", label: "Invalid required field", count: 144, share: 0.48 },       // 2925 × (92/1864)
  { code: "—", label: "Challenge failed", count: 131, share: 0.44 },             // 1313 × (93/940)
  { code: "61", label: "Exceeds approval limit", count: 129, share: 0.43 },      // 2925 × (83/1864)
  { code: "82", label: "Invalid CVV", count: 127, share: 0.43 },                 // 2925 × (81/1864)
];

// AVS distribution tuned to the new decline math:
//   • N — no match (~2.2%) bigger than INVALID_CVV alone (0.40%) because AVS
//     mismatches don't always force a decline; some still settle.
//   • Y — full match dominates (~90%) — risk decisioning filters out cards
//     with weak address signals up front.
const MOCK_AVS = [
  { label: "Y — full match", value: 90.2, color: "#10B981" },
  { label: "A — addr only", value: 4.5, color: "#3B82F6" },
  { label: "Z — zip only", value: 3.1, color: "#F59E0B" },
  { label: "N — no match", value: 2.2, color: "#EF4444" },
];

// CVV distribution:
//   • M — match ~96.4% — clean traffic.
//   • N — no match ~2.4% — slightly above the INVALID_CVV decline share
//     (0.40%) since a non-match doesn't always trigger an immediate decline.
//   • U — unavailable ~1.2% (e.g. some recurring auth flows omit CVV).
const MOCK_CVV = [
  { label: "M — match", value: 96.4, color: "#10B981" },
  { label: "N — no match", value: 2.4, color: "#EF4444" },
  { label: "U — unavailable", value: 1.2, color: "#94A3B8" },
];

// Network mix sourced from sub-26 mv_user_outcomes, scaled by 2.510. Volumes
// tuned so Visa + MC = 29,847 exactly = Hero.total_transactions, so the
// Network panel's per-window scaled sum always matches the Hero KPI:
//   Visa       — 20,743 txns (sub-26 8,267 share)
//   Mastercard —  9,104 txns (sub-26 3,627 share)
//   Sum = 29,847 ≡ Hero total ✓
// Volume-weighted approval: (20743 × 90.3 + 9104 × 90.1) / 29847 ≈ 90.24%
// ≡ 100 − Hero issuer_decline_rate (9.8%). Both networks > 90%.
const MOCK_NETWORK_APPROVAL = [
  { network: "Visa", approval: 90.3, volume: 20743 },
  { network: "Mastercard", approval: 90.1, volume: 9104 },
];

// Channel & Geography mix sourced from sub-26 reality:
//   • CNP dominates — this is online iGaming, virtually no card-present.
//   • Cross-border share computed from sub-26 mv_outbox_txn_join country
//     buckets (CY/NP/SA/MX/CA/GB/DE/SG ≈ 4% of identified geographies; the
//     rest of the non-US is unidentified/anonymized).
const MOCK_CHANNEL_MIX = {
  cnpShare: 99.4,
  cpShare: 0.6,
  crossBorderShare: 4.2,
  domesticShare: 95.8,
};

const bpsToPct = (bps) => (bps / 100).toFixed(2);

const complianceStatus = (ratio, aboveStd, excessive) => {
  if (ratio >= excessive)
    return { label: "EXCESSIVE", color: "#B91C1C", bg: "#FEE2E2" };
  if (ratio >= aboveStd)
    return { label: "ABOVE STANDARD", color: "#B45309", bg: "#FEF3C7" };
  return { label: "BELOW STANDARD", color: "#047857", bg: "#D1FAE5" };
};

const ComplianceCard = ({ row }) => {
  const status = complianceStatus(row.ratioBps, row.aboveStdBps, row.excessiveBps);
  const pctOfExcessive = Math.min(100, (row.ratioBps / row.excessiveBps) * 100);
  return (
    <div
      style={{
        background: "#FFFFFF",
        border: "1px solid #E5E7EB",
        borderRadius: 18,
        padding: "18px 20px",
        boxShadow: "0 12px 30px rgba(15,23,42,0.07)",
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 800, color: "#64748B", letterSpacing: "0.08em" }}>
            SCHEME COMPLIANCE
          </div>
          <div style={{ fontSize: 16, fontWeight: 900, color: "#0F172A", marginTop: 2 }}>
            {row.program}
          </div>
          <div style={{ fontSize: 11, color: "#64748B" }}>{row.scope}</div>
        </div>
        <span
          style={{
            fontSize: 10,
            fontWeight: 900,
            padding: "5px 10px",
            borderRadius: 999,
            color: status.color,
            background: status.bg,
            border: `1px solid ${status.color}33`,
            whiteSpace: "nowrap",
            alignSelf: "flex-start",
          }}
        >
          {status.label}
        </span>
      </div>

      <div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
          <span style={{ fontSize: 28, fontWeight: 900, color: "#0F172A" }}>
            {bpsToPct(row.ratioBps)}%
          </span>
        </div>
        <div
          style={{
            position: "relative",
            height: 8,
            borderRadius: 999,
            background: "#F1F5F9",
            marginTop: 8,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              width: `${pctOfExcessive}%`,
              height: "100%",
              background:
                row.ratioBps >= row.excessiveBps
                  ? "#DC2626"
                  : row.ratioBps >= row.aboveStdBps
                  ? "#F59E0B"
                  : "#10B981",
              transition: "width 0.4s ease",
            }}
          />
          <div
            style={{
              position: "absolute",
              top: -3,
              left: `${(row.aboveStdBps / row.excessiveBps) * 100}%`,
              width: 2,
              height: 14,
              background: "#64748B",
            }}
            title={`Above Standard ${bpsToPct(row.aboveStdBps)}%`}
          />
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "#64748B", marginTop: 4 }}>
          <span>Above std {bpsToPct(row.aboveStdBps)}%</span>
          <span>Excessive {bpsToPct(row.excessiveBps)}%</span>
        </div>
      </div>

      <div style={{ fontSize: 11, color: "#475569", lineHeight: 1.4 }}>{row.note}</div>
    </div>
  );
};

const StackedBar = ({ rows }) => {
  const total = rows.reduce((s, r) => s + r.value, 0);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ display: "flex", height: 14, borderRadius: 999, overflow: "hidden", border: "1px solid #E5E7EB" }}>
        {rows.map((r) => (
          <div
            key={r.label}
            style={{ width: `${(r.value / total) * 100}%`, background: r.color }}
            title={`${r.label}: ${r.value}%`}
          />
        ))}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
        {rows.map((r) => (
          <div key={r.label} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "#334155" }}>
            <span style={{ width: 10, height: 10, borderRadius: 3, background: r.color }} />
            <span style={{ fontWeight: 700 }}>{r.value}%</span>
            <span style={{ color: "#64748B" }}>{r.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const fmtNumber = (n) => Number(n || 0).toLocaleString();
const fmtUsd = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(Number(n || 0));
const fmtCompactUsd = (n) => {
  const v = Number(n || 0);
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (v >= 1_000) return `$${(v / 1_000).toFixed(1)}K`;
  return `$${v.toFixed(0)}`;
};

const Sparkline = ({ data, color = "#3B82F6", height = 38 }) => {
  if (!data || data.length === 0) return null;
  const values = data.map((d) => d.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const w = 110;
  const stepX = w / (data.length - 1);
  const pts = data
    .map((d, i) => `${i * stepX},${height - ((d.value - min) / range) * (height - 4) - 2}`)
    .join(" ");
  const areaPts = `0,${height} ${pts} ${w},${height}`;
  return (
    <svg width={w} height={height} style={{ display: "block" }}>
      <defs>
        <linearGradient id={`spark-${color.replace("#", "")}`} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.35" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={areaPts} fill={`url(#spark-${color.replace("#", "")})`} />
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.8" strokeLinejoin="round" />
    </svg>
  );
};

const DeltaBadge = ({ value, invertGood }) => {
  if (value == null) return null;
  const up = value >= 0;
  const good = invertGood ? !up : up;
  const color = good ? "#047857" : "#B91C1C";
  const bg = good ? "#ECFDF5" : "#FEF2F2";
  const arrow = up ? "▲" : "▼";
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 800,
        color,
        background: bg,
        padding: "3px 8px",
        borderRadius: 999,
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        whiteSpace: "nowrap",
      }}
    >
      <span style={{ fontSize: 9 }}>{arrow}</span>
      {Math.abs(value).toFixed(value % 1 === 0 ? 0 : 2)}%
    </span>
  );
};

const HeroKpi = ({ label, value, sublabel, delta, deltaInvertGood, sparkData, sparkColor, accent }) => (
  <div
    style={{
      background: "#FFFFFF",
      border: "1px solid rgba(0,0,0,0.06)",
      borderRadius: 8,
      padding: "18px 20px",
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      gap: 8,
      minHeight: 140,
      position: "relative",
      overflow: "hidden",
    }}
  >
    <div
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: 3,
        background: `linear-gradient(90deg, ${accent || "#3B82F6"}, ${accent || "#3B82F6"}88)`,
      }}
    />
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
      <span style={{ fontSize: 11, fontWeight: 800, color: "#64748B", letterSpacing: "0.07em", textTransform: "uppercase" }}>
        {label}
      </span>
      <DeltaBadge value={delta} invertGood={deltaInvertGood} />
    </div>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 12 }}>
      <div>
        <div style={{ fontSize: 30, fontWeight: 900, color: "#0F172A", letterSpacing: "-0.04em", lineHeight: 1.1 }}>
          {value}
        </div>
        {sublabel && (
          <div style={{ fontSize: 11, color: "#64748B", marginTop: 4 }}>{sublabel}</div>
        )}
      </div>
      {sparkData && <Sparkline data={sparkData} color={sparkColor || accent || "#3B82F6"} />}
    </div>
  </div>
);

const FunnelBar = ({ stages }) => {
  const max = Math.max(...stages.map((s) => s.count));
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {stages.map((s, i) => {
        const widthPct = (s.count / max) * 100;
        const prev = i === 0 ? null : stages[i - 1];
        const dropPct = prev ? ((prev.count - s.count) / prev.count) * 100 : 0;
        const passPct = (s.count / stages[0].count) * 100;
        return (
          <div key={s.stage}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
              <span style={{ fontSize: 12, fontWeight: 800, color: "#0F172A" }}>
                {i + 1}. {s.stage}
              </span>
              <div style={{ display: "flex", gap: 10, alignItems: "center", fontSize: 11, color: "#64748B" }}>
                <span style={{ fontWeight: 700, color: "#0F172A" }}>{fmtNumber(s.count)}</span>
                <span>{passPct.toFixed(1)}% of attempts</span>
                {prev && (
                  <span style={{ color: dropPct > 2 ? "#B91C1C" : "#64748B" }}>
                    {dropPct > 0 ? `−${dropPct.toFixed(1)}%` : "0%"}
                  </span>
                )}
              </div>
            </div>
            <div style={{ height: 22, borderRadius: 8, background: "#F1F5F9", overflow: "hidden", position: "relative" }}>
              <div
                style={{
                  height: "100%",
                  width: `${widthPct}%`,
                  background: `linear-gradient(90deg, ${s.color}, ${s.color}cc)`,
                  borderRadius: 8,
                  transition: "width 0.4s ease",
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

const RecoveryCard = ({ icon, label, value, sublabel, accent }) => (
  <div
    style={{
      background: "#FFFFFF",
      border: "1px solid #E5E7EB",
      borderRadius: 16,
      padding: "16px 18px",
      boxShadow: "0 10px 24px rgba(15,23,42,0.06)",
      display: "flex",
      alignItems: "center",
      gap: 14,
      minHeight: 92,
    }}
  >
    <div
      style={{
        width: 44,
        height: 44,
        borderRadius: 12,
        background: `${accent}1A`,
        color: accent,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 18,
        fontWeight: 900,
        flexShrink: 0,
      }}
    >
      {icon}
    </div>
    <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
      <span style={{ fontSize: 11, color: "#64748B", fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase" }}>
        {label}
      </span>
      <span style={{ fontSize: 22, fontWeight: 900, color: "#0F172A", letterSpacing: "-0.02em" }}>{value}</span>
      {sublabel && <span style={{ fontSize: 11, color: "#64748B" }}>{sublabel}</span>}
    </div>
  </div>
);

const RatingPill = ({ rating }) => {
  const map = {
    HIGH: { bg: "#FEE2E2", color: "#B91C1C", border: "#FECACA" },
    MEDIUM: { bg: "#FEF3C7", color: "#B45309", border: "#FDE68A" },
    LOW: { bg: "#D1FAE5", color: "#047857", border: "#A7F3D0" },
  };
  const s = map[rating] || map.LOW;
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 900,
        padding: "4px 10px",
        borderRadius: 999,
        background: s.bg,
        color: s.color,
        border: `1px solid ${s.border}`,
        letterSpacing: "0.04em",
      }}
    >
      {rating}
    </span>
  );
};

const ScorePill = ({ label, score }) => {
  const color = score >= 2 ? "#B91C1C" : score >= 1 ? "#B45309" : "#047857";
  const bg = score >= 2 ? "#FEE2E2" : score >= 1 ? "#FEF3C7" : "#D1FAE5";
  return (
    <span
      style={{
        fontSize: 10,
        fontWeight: 800,
        padding: "3px 7px",
        borderRadius: 6,
        background: bg,
        color,
        letterSpacing: "0.04em",
      }}
    >
      {label} {score}
    </span>
  );
};

/* ORIGINAL badge components — superseded by the single header DEMO MODE pill. Kept for revert.
const SampleBadge = () => (
  <span
    style={{
      fontSize: 10,
      fontWeight: 800,
      padding: "3px 8px",
      borderRadius: 999,
      background: "#EEF2FF",
      color: "#4338CA",
      border: "1px solid #C7D2FE",
      letterSpacing: "0.06em",
    }}
  >
    SAMPLE DATA
  </span>
);

const LiveBadge = () => (
  <span
    style={{
      fontSize: 10,
      fontWeight: 800,
      padding: "3px 8px",
      borderRadius: 999,
      background: "#ECFDF5",
      color: "#047857",
      border: "1px solid #A7F3D0",
      letterSpacing: "0.06em",
      display: "inline-flex",
      alignItems: "center",
      gap: 5,
    }}
  >
    <span style={{ width: 6, height: 6, borderRadius: 999, background: "#10B981" }} />
    LIVE
  </span>
);
*/

const ChartShell = ({ eyebrow, title, hint, hero, children, padding = 20 }) => (
  <div
    style={{
      background: "#FFFFFF",
      border: "1px solid rgba(0,0,0,0.06)",
      borderRadius: 8,
      padding,
      height: "100%",
      display: "flex",
      flexDirection: "column",
      gap: 12,
    }}
  >
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
      <div>
        {eyebrow && (
          <div style={{ fontSize: 10, fontWeight: 900, color: "#1D4ED8", letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 2 }}>
            {eyebrow}
          </div>
        )}
        <div style={{ fontSize: 16, fontWeight: 900, color: "#0F172A", letterSpacing: "-0.02em" }}>{title}</div>
        {hint && <div style={{ fontSize: 12, color: "#64748B", marginTop: 2 }}>{hint}</div>}
      </div>
      {hero}
    </div>
    <div style={{ flex: 1 }}>{children}</div>
  </div>
);

const RISK_COLORS = { HIGH: "#DC2626", MEDIUM: "#F59E0B", LOW: "#10B981", UNKNOWN: "#94A3B8" };
const THREEDS_COLORS = {
  "Force 3DS": "#7C3AED",
  "Prefer 3DS": "#3B82F6",
  "Bypass 3DS": "#94A3B8",
  BLACKLISTED: "#DC2626",
  BLOCKED: "#F59E0B",
  "Other 3DS": "#64748B",
};

const DonutWithLegend = ({ data, colorMap, totalLabel = "total" }) => {
  const total = data.reduce((s, d) => s + d.value, 0);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 24, flexWrap: "wrap" }}>
      <div style={{ position: "relative", width: 200, height: 200, flexShrink: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={data} dataKey="value" cx="50%" cy="50%" innerRadius={62} outerRadius={92} stroke="none" paddingAngle={1}>
              {data.map((d, i) => (
                <Cell key={i} fill={colorMap[d.name] || ["#3B82F6", "#10B981", "#F59E0B", "#DC2626", "#7C3AED"][i % 5]} />
              ))}
            </Pie>
            <Tooltip formatter={(v) => [fmtNumber(v), "count"]} />
          </PieChart>
        </ResponsiveContainer>
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          <div style={{ fontSize: 22, fontWeight: 900, color: "#0F172A", letterSpacing: "-0.04em", lineHeight: 1 }}>
            {fmtNumber(total)}
          </div>
          <div style={{ fontSize: 11, color: "#64748B", fontWeight: 700, marginTop: 2 }}>{totalLabel}</div>
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10, flex: 1, minWidth: 180 }}>
        {data.map((d) => {
          const pct = total > 0 ? (d.value / total) * 100 : 0;
          const color = colorMap[d.name] || "#94A3B8";
          return (
            <div key={d.name} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13 }}>
                <span style={{ width: 10, height: 10, borderRadius: 3, background: color }} />
                <span style={{ flex: 1, color: "#0F172A", fontWeight: 700 }}>{d.name}</span>
                <span style={{ color: "#64748B", fontSize: 12, minWidth: 56, textAlign: "right" }}>{fmtNumber(d.value)}</span>
                <span style={{ color: "#0F172A", fontWeight: 900, minWidth: 56, textAlign: "right" }}>{pct.toFixed(1)}%</span>
              </div>
              <div style={{ height: 4, background: "#F1F5F9", borderRadius: 999, overflow: "hidden" }}>
                <div style={{ width: `${pct}%`, height: "100%", background: color, transition: "width 0.4s ease" }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const HorizontalBarList = ({ rows, labelKey, valueKey, accentColor = "#1D4ED8", valueFormatter, monoLabel }) => {
  const max = Math.max(...rows.map((r) => r[valueKey]));
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10, maxHeight: 320, overflowY: "auto", paddingRight: 4 }}>
      {rows.map((r) => {
        const widthPct = (r[valueKey] / max) * 100;
        return (
          <div key={r[labelKey]} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 13 }}>
              <span
                style={{
                  color: "#0F172A",
                  fontWeight: 700,
                  fontFamily: monoLabel ? "ui-monospace, monospace" : "inherit",
                }}
              >
                {r[labelKey]}
              </span>
              <span style={{ fontWeight: 900, color: "#0F172A" }}>{valueFormatter ? valueFormatter(r[valueKey]) : fmtNumber(r[valueKey])}</span>
            </div>
            <div style={{ height: 8, background: "#F1F5F9", borderRadius: 999, overflow: "hidden" }}>
              <div
                style={{
                  width: `${widthPct}%`,
                  height: "100%",
                  background: `linear-gradient(90deg, ${accentColor}, ${accentColor}99)`,
                  borderRadius: 999,
                  transition: "width 0.4s ease",
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

const VampPanel = ({ data }) => {
  if (!data || !data.chartData) return null;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
        <MiniKpi
          label={`${data.month} VAMP Refund Rate`}
          value={`${(data.refund_rate ?? 0).toFixed(2)}%`}
          accent={(data.refund_rate ?? 0) >= 1.5 ? "#DC2626" : (data.refund_rate ?? 0) >= 0.65 ? "#F59E0B" : "#047857"}
          sublabel={`${fmtNumber(data.refund_count)} refunds / ${fmtNumber(data.denom_txn_count)} txns`}
        />
        <MiniKpi label="Threshold (merchant)" value="1.50%" accent="#1D4ED8" sublabel="Above-standard 0.50%" />
      </div>
      <ResponsiveContainer width="100%" height={260}>
        <ComposedChart data={data.chartData} margin={{ top: 6, right: 12, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="month" fontSize={11} />
          <YAxis yAxisId="left" fontSize={11} tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)} />
          <YAxis yAxisId="right" orientation="right" fontSize={11} tickFormatter={(v) => `${v}%`} />
          <Tooltip formatter={(v, n) => (n === "refundRate" ? [`${Number(v).toFixed(2)}%`, "Refund rate"] : [fmtNumber(v), n === "transactions" ? "Transactions" : "Refunds"])} />
          <Bar yAxisId="left" dataKey="transactions" fill="#3B82F6" radius={[6, 6, 0, 0]} />
          <Line yAxisId="right" type="monotone" dataKey="refundRate" stroke="#F59E0B" strokeWidth={2.4} dot={{ r: 3 }} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

const LatencyPanel = ({ data }) => {
  if (!data) return null;
  const p50 = 129;
  const p95 = 506;
  const p99 = 4828;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 12 }}>
        <MiniKpi label="Decisioning p50" value={`${p50} ms`} accent="#047857" sublabel="Median round-trip" />
        <MiniKpi label="Decisioning p95" value={`${p95} ms`} accent="#F59E0B" sublabel="95th percentile" />
        <MiniKpi label="Decisioning p99" value={`${(p99 / 1000).toFixed(2)} s`} accent="#DC2626" sublabel="Tail latency" />
        <MiniKpi label="Sample window" value="Last 15 orders" accent="#1D4ED8" sublabel="Live tail" />
      </div>
      <ResponsiveContainer width="100%" height={220}>
        <LineChart data={data.chartData || []} margin={{ top: 6, right: 12, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="order_key" fontSize={10} angle={-25} textAnchor="end" height={48} />
          <YAxis fontSize={11} tickFormatter={(v) => `${Math.round(v)} ms`} />
          <Tooltip formatter={(v) => [`${Math.round(v)} ms`, "Latency"]} />
          <Line type="monotone" dataKey="valueMs" stroke="#1D4ED8" strokeWidth={2.4} dot={{ r: 3 }} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

// Section header — renders all three fields the pre-facelift version
// carries (eyebrow / title / hint) so text stays identical across
// skins. Facelift is a visual layer only; content is not shortened.
const SectionBanner = ({ eyebrow, title, hint, count }) => (
  <div
    style={{
      marginTop: 40,
      marginBottom: 16,
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: space[3],
      flexWrap: "wrap",
    }}
  >
    <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
      {eyebrow && (
        <span
          style={{
            fontSize: 11,
            fontWeight: weight.semibold,
            color: color.brand.solid,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
          }}
        >
          {eyebrow}
        </span>
      )}
      <div style={{ display: "flex", alignItems: "baseline", gap: space[2] }}>
        <h2
          style={{
            margin: 0,
            fontSize: 18,
            fontWeight: weight.semibold,
            color: color.text.primary,
            letterSpacing: "-0.01em",
            lineHeight: 1.3,
          }}
        >
          {title || eyebrow}
        </h2>
        {(count !== undefined && count !== null) && (
          <span style={{ fontSize: text.base, color: color.text.muted, ...numeric.tabular }}>
            · {count}
          </span>
        )}
      </div>
    </div>
    {hint && (
      <span style={{ fontSize: 12, color: color.text.muted, maxWidth: 520, textAlign: "right", lineHeight: 1.5 }}>
        {hint}
      </span>
    )}
  </div>
);

// 30-day target row counts for each drilldown table — derived directly from
// the Hero KPIs so the table headers reconcile with the headline numbers:
//   Blocked         = block_rate × total           = 2.25% × 11,894 ≈ 268
//   3DS Failed      = threeds_fail_rate × post-block = 4.4% × 11,626 ≈ 511
//   Issuer Declined = issuer_decline_rate × post-3DS = 9.8% × 11,114 ≈ 1,089
//   Post-Whitelist  = activity by trusted users (small pool, repeats over the
//                     month) ≈ ~50 whitelisted users × ~4 deposits = 200
// Each row count below is scaled per window via WINDOW_SCALE.
// All four targets are Hero rate × Total Transactions (% of total) so a
// user can multiply the Hero KPI by 29,847 and arrive at the table row
// count exactly — no hidden denominators.
const TABLE_TARGET_30D = {
  blocked: 671,          // 29,847 × 2.25% block_rate
  threeds_failed: 1313,  // 29,847 × 4.40% threeds_fail_rate
  issuer_declined: 2925, // 29,847 × 9.80% issuer_decline_rate
  whitelisted: 487,      // trusted-user activity pool, scaled ~1:1
};

// How far back from "now" the most recent row lands. Keeps the table realistic
// (you're never looking at a row that just settled this second) and matches
// the dashboard's "anchored to current UTC" guarantee.
const WINDOW_HOURS = { "1h": 1, "24h": 24, "7d": 168, "30d": 720 };
const LATEST_OFFSET_MIN = { "1h": 4, "24h": 22, "7d": 75, "30d": 95 };

const targetRowsForWindow = (kind, window) => {
  const target30 = TABLE_TARGET_30D[kind] ?? 100;
  const scale = WINDOW_SCALE[window] ?? 1;
  return Math.max(1, Math.round(target30 * scale));
};

// Accepts either a kind string (looked up against TABLE_TARGET_30D scaled by
// the window) OR an explicit numeric target (computed from current Hero
// values). Inline numeric counts let drilldown tables exactly match
// kpis.rate × kpis.total_transactions for the current window.
const shiftRowsForWindow = (base, window, kindOrCount) => {
  const target = typeof kindOrCount === "number"
    ? Math.max(1, Math.round(kindOrCount))
    : (kindOrCount ? targetRowsForWindow(kindOrCount, window) : base.length);
  const windowMin = (WINDOW_HOURS[window] ?? 720) * 60;
  const latestOffsetMin = LATEST_OFFSET_MIN[window] ?? 90;
  const spanMin = Math.max(windowMin - latestOffsetMin, 1);
  const stepMin = target > 1 ? spanMin / (target - 1) : 0;
  const nowMs = Date.now();
  return Array.from({ length: target }, (_, i) => {
    const src = base[i % base.length];
    const offsetMin = latestOffsetMin + i * stepMin;
    const t = new Date(nowMs - offsetMin * 60_000);
    const ts = t.toISOString().replace("T", " ").slice(0, 16); // YYYY-MM-DD HH:MM (UTC)
    const origNum = parseInt(src.id.replace(/[^0-9]/g, ""), 10) || 700000;
    const id = `TXN-${origNum - i * 3}`;
    return { ...src, id, ts };
  });
};

const pagerBtnStyle = (disabled) => ({
  border: "1px solid #E2E8F0",
  background: disabled ? "#F1F5F9" : "#FFFFFF",
  color: disabled ? "#94A3B8" : "#1D4ED8",
  padding: "5px 12px",
  borderRadius: 8,
  fontSize: 12,
  fontWeight: 800,
  cursor: disabled ? "not-allowed" : "pointer",
});

const Pager = ({ current, totalPages, totalRows, pageSize, onChange }) => {
  if (totalRows === 0) return null;
  const start = (current - 1) * pageSize + 1;
  const end = Math.min(totalRows, current * pageSize);
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 14, paddingTop: 10, borderTop: "1px solid #F1F5F9" }}>
      <span style={{ fontSize: 12, color: "#64748B" }}>
        Showing <strong style={{ color: "#0F172A" }}>{fmtNumber(start)}–{fmtNumber(end)}</strong> of <strong style={{ color: "#0F172A" }}>{fmtNumber(totalRows)}</strong>
      </span>
      <div style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
        <button type="button" disabled={current <= 1} onClick={() => onChange(current - 1)} style={pagerBtnStyle(current <= 1)}>
          ← Prev
        </button>
        <span style={{ fontSize: 12, color: "#64748B", padding: "0 4px" }}>
          Page <strong style={{ color: "#0F172A" }}>{current}</strong> / {totalPages}
        </span>
        <button type="button" disabled={current >= totalPages} onClick={() => onChange(current + 1)} style={pagerBtnStyle(current >= totalPages)}>
          Next →
        </button>
      </div>
    </div>
  );
};

const PagedDemoTable = ({ columns, rows, pageSize = 10 }) => {
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [rows.length]);
  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const slice = rows.slice((safePage - 1) * pageSize, safePage * pageSize);
  return (
    <>
      <DemoTxnTable columns={columns} rows={slice} />
      <Pager current={safePage} totalPages={totalPages} totalRows={rows.length} pageSize={pageSize} onChange={setPage} />
    </>
  );
};

const DemoTxnTable = ({ columns, rows }) => (
  <div style={{ overflowX: "auto" }}>
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: text.base, ...numeric.tabular }}>
      <thead>
        <tr style={{ background: color.bg.subtle, borderBottom: `1px solid ${color.border.subtle}`, textAlign: "left", fontSize: text.xs, color: color.text.tertiary, letterSpacing: tracking.wide }}>
          {columns.map((c) => (
            <th key={c} style={{ padding: `${space[3]}px ${space[4]}px`, fontWeight: weight.semibold }}>
              {c}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} style={{ borderBottom: `1px solid ${color.border.subtle}` }}>
            {r.cells.map((cell, j) => (
              <td key={j} style={{ padding: `${space[3]}px ${space[4]}px`, color: color.text.primary }}>
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════
// Portfolio Health panel — the sponsor bank's landing view. Three pieces:
//   1. VAMP-by-merchant bar chart with 50/150 bps threshold lines. Instantly
//      shows which merchant is dragging the portfolio ratio up.
//   2. Chargeback + fraud-to-sales side-by-side, so the two headline
//      compliance ratios are on-screen without a click.
//   3. Merchant health cards (score / tenure / trend / verdict). Clicking a
//      card slices the whole dashboard to that merchant.
// ═══════════════════════════════════════════════════════════════════════════
// Merchant Health card — Datadog-style 3px left accent bar color-coded
// by health tier. The bar is the identity signal: red=problem,
// cobalt=portfolio aggregate, green=healthy. No chip in the corner —
// the bar carries the status.
const MerchantHealthCard = ({ m, selected, onPick }) => {
  const isProblem = m.health_score < 60;
  const isAggregate = m.id === "all";
  const accent =
    isProblem   ? color.danger.solid :
    isAggregate ? color.brand.solid  :
                  color.success.solid;
  const metricTone = (over) => (over ? color.danger.text : color.success.text);
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={() => onPick(m.id)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        cursor: "pointer",
        textAlign: "left",
        background: color.bg.surface,
        border: `1px solid ${selected ? accent : color.border.subtle}`,
        borderRadius: 8,
        padding: "16px 20px 16px 20px",
        boxShadow: `inset 3px 0 0 ${accent}`,
        outline: "none",
        display: "flex",
        flexDirection: "column",
        gap: 8,
        transition: motion.tx("all", "fast"),
        transform: hover ? "translateY(-1px)" : "none",
        width: "100%",
        fontFamily: "inherit",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontSize: 14, fontWeight: weight.semibold, color: color.text.primary, letterSpacing: "-0.01em" }}>
          {m.name}
        </span>
        <span style={{ fontSize: 11, fontWeight: 600, color: accent, letterSpacing: "0.06em", textTransform: "uppercase" }}>
          {m.health_label}
        </span>
      </div>
      <div style={{ fontSize: 12, color: color.text.muted }}>
        {m.vertical}{m.tenure_months ? ` · ${m.tenure_months} mo tenure` : ""}
      </div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: 4 }}>
        <span style={{
          fontSize: 40,
          fontWeight: weight.semibold,
          color: color.text.primary,
          letterSpacing: "-0.03em",
          lineHeight: 1,
          ...numeric.tabular,
        }}>{m.health_score}</span>
        <span style={{ fontSize: 12, color: color.text.muted }}>/ 100 health</span>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 14, fontSize: 12, color: color.text.muted, marginTop: 4 }}>
        <span>
          VAMP <strong style={{ color: metricTone(m.vamp_bps > 50), fontWeight: weight.semibold, ...numeric.tabular }}>{m.vamp_bps} bps</strong>
        </span>
        <span>
          CB <strong style={{ color: metricTone(m.chargeback_rate_pct > 0.65), fontWeight: weight.semibold, ...numeric.tabular }}>{m.chargeback_rate_pct}%</strong>
        </span>
        <span>
          Fraud/sales <strong style={{ color: metricTone(m.fraud_to_sales_pct > 0.10), fontWeight: weight.semibold, ...numeric.tabular }}>{m.fraud_to_sales_pct}%</strong>
        </span>
      </div>
    </button>
  );
};

const VampByMerchantChart = () => {
  // Data drawn straight from MERCHANTS + aggregate. Thresholds annotated so
  // the bank operator sees who's over the line without reading labels.
  const data = [
    { name: MERCHANTS.epicsweep.name, vamp: MERCHANTS.epicsweep.vamp_bps },
    { name: MERCHANTS.tigercash.name, vamp: MERCHANTS.tigercash.vamp_bps },
    { name: "Portfolio", vamp: MERCHANT_ALL.vamp_bps },
  ];
  const maxY = Math.max(160, ...data.map((d) => d.vamp * 1.2));
  const barFill = (v) => (v > 150 ? color.danger.solid : v > 50 ? color.warning.solid : color.success.solid);
  return (
    <ChartCard title="VAMP ratio by merchant · basis points">
      <div style={{ marginBottom: space[2], fontSize: text.xs, color: color.text.tertiary }}>
        Standard: <strong style={{ color: color.text.primary, ...numeric.tabular }}>50 bps</strong> · Excessive: <strong style={{ color: color.text.primary, ...numeric.tabular }}>150 bps</strong> · Portfolio = volume-weighted mean.
      </div>
      <ResponsiveContainer width="100%" height={240}>
        <BarChart data={data} margin={{ top: 8, right: 20, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={color.border.subtle} />
          <XAxis dataKey="name" fontSize={12} stroke={color.text.tertiary} />
          <YAxis domain={[0, maxY]} tickFormatter={(v) => `${v}`} fontSize={11} stroke={color.text.tertiary} />
          <Tooltip formatter={(v) => [`${v} bps`, "VAMP"]} />
          <Bar dataKey="vamp" radius={[radius.md, radius.md, 0, 0]}>
            {data.map((d, i) => <Cell key={i} fill={barFill(d.vamp)} />)}
          </Bar>
          <ReferenceLine y={50}  stroke={color.warning.solid} strokeDasharray="4 4" isFront ifOverflow="visible" />
          <ReferenceLine y={150} stroke={color.danger.solid}  strokeDasharray="4 4" isFront ifOverflow="visible" />
        </BarChart>
      </ResponsiveContainer>
      <div style={{ fontSize: text.xs, color: color.text.tertiary, marginTop: space[2], display: "flex", gap: space[4] }}>
        <span><span style={{ display: "inline-block", width: 12, height: 2, background: color.warning.solid, marginRight: space[2] }} />above-standard 50 bps</span>
        <span><span style={{ display: "inline-block", width: 12, height: 2, background: color.danger.solid, marginRight: space[2] }} />excessive 150 bps</span>
      </div>
    </ChartCard>
  );
};

const CompareRateChart = ({ title, metric, valueField, thresholdHigh, unit = "%" }) => {
  const data = [
    { name: MERCHANTS.epicsweep.name, value: MERCHANTS.epicsweep[valueField] },
    { name: MERCHANTS.tigercash.name, value: MERCHANTS.tigercash[valueField] },
  ];
  const barFill = (v) => (thresholdHigh != null && v > thresholdHigh ? color.danger.solid : color.success.solid);
  return (
    <ChartCard title={title}>
      <div style={{ fontSize: text.xs, color: color.text.tertiary, marginBottom: space[2] }}>
        {metric} · vertical comparison. Threshold shown when either merchant crosses it.
      </div>
      <ResponsiveContainer width="100%" height={200}>
        <BarChart data={data} margin={{ top: 8, right: 20, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={color.border.subtle} />
          <XAxis dataKey="name" fontSize={12} stroke={color.text.tertiary} />
          <YAxis tickFormatter={(v) => `${v}${unit}`} fontSize={11} stroke={color.text.tertiary} />
          <Tooltip formatter={(v) => [`${v}${unit}`, metric]} />
          <Bar dataKey="value" radius={[radius.md, radius.md, 0, 0]}>
            {data.map((d, i) => <Cell key={i} fill={barFill(d.value)} />)}
          </Bar>
          {thresholdHigh != null && (
            <ReferenceLine y={thresholdHigh} stroke={color.danger.solid} strokeDasharray="4 4" isFront ifOverflow="visible" />
          )}
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
};

const PortfolioHealthPanel = ({ selectedMerchant, onPickMerchant }) => {
  const isPortfolioAtRisk = MERCHANT_ALL.vamp_bps > 50;
  return (
    <>
      {isPortfolioAtRisk && (
        <div style={{ marginBottom: space[4] }}>
          <UiAlert
            tone="danger"
            title="PORTFOLIO ALERT"
            description={
              <>
                Sponsor book VAMP is <strong style={numeric.tabular}>{MERCHANT_ALL.vamp_bps} bps</strong> — above the 50 bps standard. Driven by <strong>{MERCHANTS.tigercash.name}</strong> at <strong style={numeric.tabular}>{MERCHANTS.tigercash.vamp_bps} bps</strong>.
              </>
            }
          />
        </div>
      )}
      <Row className="mb-3 g-3">
        {[MERCHANT_ALL, MERCHANTS.epicsweep, MERCHANTS.tigercash].map((m) => (
          <Col xl={4} lg={4} md={12} key={m.id}>
            <MerchantHealthCard m={m} selected={selectedMerchant === m.id} onPick={onPickMerchant} />
          </Col>
        ))}
      </Row>
      <Row className="mb-4 g-3">
        <Col xl={6} lg={12}>
          <VampByMerchantChart />
        </Col>
        <Col xl={3} lg={6}>
          <CompareRateChart
            title="Chargeback rate by merchant"
            metric="Chargeback rate"
            valueField="chargeback_rate_pct"
            thresholdHigh={0.65}
            unit="%"
          />
        </Col>
        <Col xl={3} lg={6}>
          <CompareRateChart
            title="Fraud-to-sales by merchant"
            metric="Fraud-to-sales"
            valueField="fraud_to_sales_pct"
            thresholdHigh={0.10}
            unit="%"
          />
        </Col>
      </Row>
    </>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// PortfolioOverviewSection — the extra layer shown ONLY when "All Merchants"
// is selected. Merchant-level detail (Hero KPIs, drilldown tables) is hidden;
// this section fills the space with portfolio-scope analytics that only make
// sense for the sponsor-bank operator:
//   1. Top-line portfolio KPIs (merchants count, portfolio volume, VAMP, at-risk count)
//   2. Compliance status heatmap — VAMP / ECP / EFM × merchants + portfolio
//   3. Portfolio VAMP trend over the last 12 months with threshold lines
// ═══════════════════════════════════════════════════════════════════════════
// One KPI cell inside <KpiStrip>. No border of its own — the strip owns
// the outer border, the cell owns only a left hairline (dropped on the
// first cell). 28px charcoal number, colored delta below in 12px.
// Attio / Ramp / Mercury pattern.
const KpiCell = ({ label, value, sublabel, tone /* success | danger | warning */, isFirst }) => {
  const subColor =
    tone === "danger"  ? color.danger.text  :
    tone === "warning" ? color.warning.text :
    tone === "success" ? color.success.text :
    color.text.muted;
  return (
    <div
      style={{
        flex: 1,
        minWidth: 0,
        padding: `20px 24px`,
        borderLeft: isFirst ? "none" : `1px solid ${color.border.subtle}`,
        display: "flex",
        flexDirection: "column",
        gap: 4,
      }}
    >
      <span style={{ fontSize: 12, color: color.text.muted, fontWeight: weight.medium, letterSpacing: 0, textTransform: "none" }}>
        {label}
      </span>
      <span
        style={{
          fontSize: 28,
          fontWeight: weight.semibold,
          color: color.text.primary,
          letterSpacing: "-0.02em",
          lineHeight: 1.1,
          ...numeric.tabular,
        }}
      >
        {value}
      </span>
      {sublabel && (
        <span style={{ fontSize: 12, color: subColor, ...numeric.tabular }}>
          {sublabel}
        </span>
      )}
    </div>
  );
};

// Keep the old prop shape working for callers we haven't touched yet.
const PortfolioTopLineKpi = ({ label, value, sublabel, alert }) => (
  <KpiCell label={label} value={value} sublabel={sublabel} tone={alert ? "danger" : undefined} isFirst />
);

// Strip container. One row, hairline outer border, hairlines between cells.
// No shadow. No per-cell border. Radius 8, background white.
const KpiStrip = ({ children }) => (
  <div
    style={{
      display: "flex",
      background: color.bg.surface,
      border: `1px solid ${color.border.subtle}`,
      borderRadius: radius.lg,
      overflow: "hidden",
      marginBottom: 24,
    }}
  >
    {React.Children.map(children, (child, i) =>
      React.isValidElement(child) ? React.cloneElement(child, { isFirst: i === 0 }) : child
    )}
  </div>
);

const ComplianceHeatmap = () => {
  const rows = [
    { name: MERCHANT_ALL.name, vamp: MERCHANT_ALL.vamp_bps, ecp: MERCHANT_ALL.ecp_bps, efm: MERCHANT_ALL.efm_bps },
    { name: MERCHANTS.epicsweep.name, vamp: MERCHANTS.epicsweep.vamp_bps, ecp: MERCHANTS.epicsweep.ecp_bps, efm: MERCHANTS.epicsweep.efm_bps },
    { name: MERCHANTS.tigercash.name, vamp: MERCHANTS.tigercash.vamp_bps, ecp: MERCHANTS.tigercash.ecp_bps, efm: MERCHANTS.tigercash.efm_bps },
  ];
  // Thresholds per program — below standard / above standard / excessive.
  const thresholds = { vamp: { std: 50, exc: 150 }, ecp: { std: 150, exc: 300 }, efm: { std: 50, exc: 100 } };
  const toneFor = (val, kind) => {
    const t = thresholds[kind];
    if (val >= t.exc) return { tone: "danger",  label: "EXCESSIVE"  };
    if (val >= t.std) return { tone: "warning", label: "ABOVE STD"  };
    return { tone: "success", label: "BELOW STD" };
  };
  const Cell = ({ val, kind }) => {
    const { tone, label } = toneFor(val, kind);
    const fg = tone === "danger" ? color.danger.text : tone === "warning" ? color.warning.text : color.success.text;
    return (
      <td style={{ padding: `${space[3]}px ${space[4]}px`, borderBottom: `1px solid ${color.border.subtle}` }}>
        <div style={{ display: "inline-flex", flexDirection: "column", alignItems: "flex-start", gap: space[1] }}>
          <span style={{ fontSize: text.md, fontWeight: weight.semibold, color: fg, ...numeric.tabular }}>{val} bps</span>
          <Chip tone={tone}>{label}</Chip>
        </div>
      </td>
    );
  };
  return (
    <RolePanel
      role="compliance"
      title="Compliance status"
      subtitle={<>Portfolio is dragged above VAMP standard by <strong style={{ color: color.danger.text, fontWeight: weight.semibold }}>{MERCHANTS.tigercash.name}</strong>. ECP + EFM stay below standard everywhere.</>}
      icon={<ShieldIcon size={16} strokeWidth={1.75} />}
    >
      <div style={{ overflowX: "auto", marginTop: 4 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: text.base }}>
          <thead>
            <tr style={{ background: color.bg.subtle, borderBottom: `1px solid ${color.border.subtle}`, textAlign: "left", fontSize: text.xs, color: color.text.tertiary, letterSpacing: tracking.wide }}>
              <th style={{ padding: `${space[3]}px ${space[4]}px`, fontWeight: weight.semibold }}>Merchant</th>
              <th style={{ padding: `${space[3]}px ${space[4]}px`, fontWeight: weight.semibold }}>Visa VAMP<br/><span style={{ fontWeight: weight.regular, fontSize: text.xs }}>std 50 · exc 150</span></th>
              <th style={{ padding: `${space[3]}px ${space[4]}px`, fontWeight: weight.semibold }}>Mastercard ECP<br/><span style={{ fontWeight: weight.regular, fontSize: text.xs }}>std 150 · exc 300</span></th>
              <th style={{ padding: `${space[3]}px ${space[4]}px`, fontWeight: weight.semibold }}>Mastercard EFM<br/><span style={{ fontWeight: weight.regular, fontSize: text.xs }}>std 50 · exc 100</span></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.name}>
                <td style={{ padding: `${space[3]}px ${space[4]}px`, fontWeight: weight.semibold, color: color.text.primary, borderBottom: `1px solid ${color.border.subtle}` }}>{r.name}</td>
                <Cell val={r.vamp} kind="vamp" />
                <Cell val={r.ecp}  kind="ecp"  />
                <Cell val={r.efm}  kind="efm"  />
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </RolePanel>
  );
};

const PortfolioVampTrend = () => {
  // 12-month portfolio VAMP trajectory. Anchored to today's 55 bps, walking
  // backward with a slow drift up over the last 4 months (TigerCash growth).
  const monthLabels = ["Aug","Sep","Oct","Nov","Dec","Jan","Feb","Mar","Apr","May","Jun","Jul"];
  const values = [38, 39, 40, 41, 42, 44, 45, 47, 49, 52, 54, MERCHANT_ALL.vamp_bps];
  const data = monthLabels.map((m, i) => ({ month: m, vamp: values[i], std: 50, exc: 150 }));
  return (
    <ChartCard title="Portfolio VAMP — 12-month trend">
      <div style={{ fontSize: text.xs, color: color.text.tertiary, marginBottom: space[2] }}>
        Portfolio ratio crossed the 50 bps above-standard line ~2 months ago and continues to rise. Merchant B onboarded Nov, has driven ~14 bps of the increase.
      </div>
      <ResponsiveContainer width="100%" height={240}>
        <ComposedChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={color.border.subtle} />
          <XAxis dataKey="month" fontSize={12} stroke={color.text.tertiary} />
          <YAxis domain={[0, 160]} tickFormatter={(v) => `${v}`} fontSize={11} stroke={color.text.tertiary} />
          <Tooltip formatter={(v, n) => (n === "vamp" ? [`${v} bps`, "Portfolio VAMP"] : null)} />
          <Line type="monotone" dataKey="vamp" stroke={color.brand.solid} strokeWidth={3} dot={{ r: 4, fill: color.brand.solid }} />
          <ReferenceLine y={50}  stroke={color.warning.solid} strokeDasharray="4 4" isFront ifOverflow="visible" />
          <ReferenceLine y={150} stroke={color.danger.solid}  strokeDasharray="4 4" isFront ifOverflow="visible" />
        </ComposedChart>
      </ResponsiveContainer>
      <div style={{ fontSize: text.xs, color: color.text.tertiary, marginTop: space[2], display: "flex", gap: space[4] }}>
        <span><span style={{ display: "inline-block", width: 12, height: 2, background: color.warning.solid, marginRight: space[2] }} />above standard 50 bps</span>
        <span><span style={{ display: "inline-block", width: 12, height: 2, background: color.danger.solid, marginRight: space[2] }} />excessive 150 bps</span>
      </div>
    </ChartCard>
  );
};

// ── Widget 1: VAMP fee exposure — real $ the bank is burning per month at
// current portfolio VAMP posture. Visa 2026 rules: acquirer $4/dispute at
// Above-Standard, $8/dispute at Excessive. Merchant B alone drives most.
const VampFeeExposureCard = () => {
  // Approximate monthly disputable events per merchant = VAMP × transactions
  const feePerEvent = (bps) => (bps >= 70 ? 8 : bps >= 50 ? 4 : 0);
  const merchantExposure = (m) => {
    const events = Math.round((m.vamp_bps / 10000) * m.monthly_transactions);
    return { events, monthly_usd: events * feePerEvent(m.vamp_bps) };
  };
  const rows = MERCHANT_LIST.map((m) => ({ m, ...merchantExposure(m) }));
  const total = rows.reduce((s, r) => s + r.monthly_usd, 0);
  return (
    <Card padding="md" style={{ borderColor: color.danger.border, minHeight: 160 }}>
      <KpiBlock
        label="Monthly VAMP fees · portfolio"
        value={fmtCompactUsd(total)}
        accent={color.danger.solid}
        sublabel="What the bank is paying Visa in VAMP fees at current rates (Visa 2026)."
      />
      <div style={{ marginTop: space[3] }}>
        {rows.map(({ m, events, monthly_usd }) => (
          <div key={m.id} style={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            padding: `${space[2]}px 0`, fontSize: text.xs,
            borderTop: `1px solid ${color.border.subtle}`,
          }}>
            <span style={{ color: color.text.primary, fontWeight: weight.semibold }}>{m.name}</span>
            <span style={{ color: color.text.tertiary, ...numeric.tabular }}>
              {fmtNumber(events)} events · <strong style={{ color: monthly_usd > 0 ? color.danger.text : color.success.text }}>{fmtCompactUsd(monthly_usd)}/mo</strong>
            </span>
          </div>
        ))}
      </div>
    </Card>
  );
};

// ── Widget 2: Days-to-threshold countdown — for each merchant, how many
// days until they'd cross the next VAMP threshold at their current slope.
const DaysToThresholdCard = () => {
  // Simulated slope per merchant: bps/day. Anchored so Merchant B trends up.
  const slopeBpsPerDay = {
    epicsweep: -0.05,   // slowly improving
    tigercash: +0.35,   // worsening
  };
  const rows = MERCHANT_LIST.map((m) => {
    const slope = slopeBpsPerDay[m.id] ?? 0;
    let targetBps, targetLabel;
    if (m.vamp_bps < 50) { targetBps = 50; targetLabel = "above standard"; }
    else if (m.vamp_bps < 150) { targetBps = 150; targetLabel = "excessive"; }
    else { targetBps = null; targetLabel = "already excessive"; }
    let days = null;
    if (targetBps != null && slope !== 0) {
      const delta = targetBps - m.vamp_bps;
      if ((delta > 0 && slope > 0) || (delta < 0 && slope < 0)) {
        days = Math.round(Math.abs(delta / slope));
      }
    }
    return { m, slope, days, targetBps, targetLabel };
  });
  return (
    <Card padding="md" style={{ borderColor: color.warning.border, minHeight: 160 }}>
      <div style={{
        fontSize: text.xs, fontWeight: weight.semibold, color: color.warning.text,
        letterSpacing: tracking.wider, textTransform: "uppercase", marginBottom: space[2],
      }}>
        Days until next VAMP limit
      </div>
      {rows.map(({ m, slope, days, targetBps, targetLabel }) => (
        <div key={m.id} style={{ padding: `${space[2]}px 0`, borderTop: `1px solid ${color.border.subtle}` }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
            <span style={{ fontSize: text.base, fontWeight: weight.semibold, color: color.text.primary }}>{m.name}</span>
            <span style={{ fontSize: text.xs, color: slope > 0 ? color.danger.text : color.success.text, fontWeight: weight.semibold }}>
              {slope > 0 ? "▲ getting worse" : slope < 0 ? "▼ getting better" : "→ steady"} · <span style={numeric.tabular}>{Math.abs(slope).toFixed(2)}</span> bps/day
            </span>
          </div>
          <div style={{ fontSize: text.xs, color: color.text.tertiary, marginTop: space[1] }}>
            {days == null
              ? (m.vamp_bps >= 150 ? "Already past the top limit" : `Moving away from the ${targetBps} bps line — safe for now`)
              : (
                <>
                  <strong style={{ color: days < 30 ? color.danger.text : days < 90 ? color.warning.text : color.text.primary, ...numeric.tabular }}>{days} days</strong> until <span style={numeric.tabular}>{targetBps}</span> bps ({targetLabel === "above standard" ? "warning line" : targetLabel === "excessive" ? "danger line" : targetLabel})
                </>
              )
            }
          </div>
        </div>
      ))}
    </Card>
  );
};

// ── Widget 3: Active AI cure plans — the "AI-agent demo hook" widget.
// Shows any autopilot remediation currently running with a progress bar.
const ActiveCurePlansCard = () => {
  // Mocked: one active cure plan on Merchant B.
  const plans = [
    {
      merchant: MERCHANTS.tigercash,
      target_bps: 45,
      day_of_plan: 14,
      total_days: 90,
      current_bps: MERCHANTS.tigercash.vamp_bps,
      projected_bps: 68,
      actions_fired: 4,
    },
  ];
  return (
    <Card variant="emphasis" padding="md" style={{ minHeight: 160 }}>
      <KpiBlock
        label="Merchants under AI fix plan"
        value={plans.length}
        accent={color.brand.solid}
        size="md"
      />
      {plans.length === 0 && (
        <div style={{ fontSize: text.xs, color: color.text.tertiary, marginTop: space[3] }}>
          No AI fix plans running right now.
        </div>
      )}
      {plans.map((p) => {
        const pct = Math.round((p.day_of_plan / p.total_days) * 100);
        const projGap = p.projected_bps - p.target_bps;
        return (
          <div key={p.merchant.id} style={{ marginTop: space[3], paddingTop: space[2], borderTop: `1px solid ${color.brand.border}` }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
              <span style={{ fontSize: text.base, fontWeight: weight.semibold, color: color.text.primary }}>{p.merchant.name}</span>
              <span style={{ fontSize: text.xs, color: color.brand.text, fontWeight: weight.semibold, ...numeric.tabular }}>
                Day {p.day_of_plan} / {p.total_days}
              </span>
            </div>
            <div style={{ height: 8, background: color.brand.border, borderRadius: radius.pill, overflow: "hidden", margin: `${space[2]}px 0` }}>
              <div style={{ width: `${pct}%`, height: "100%", background: color.brand.solid, borderRadius: radius.pill }} />
            </div>
            <div style={{ fontSize: text.xs, color: color.text.tertiary, display: "flex", justifyContent: "space-between", gap: space[3] }}>
              <span>Now <span style={numeric.tabular}>{p.current_bps}</span> bps → goal <strong style={{ color: color.text.primary, ...numeric.tabular }}>{p.target_bps} bps</strong></span>
              <span>{p.actions_fired} actions taken · likely to land at <span style={numeric.tabular}>{p.projected_bps}</span> bps {projGap > 0 ? `(+${projGap} over goal)` : "(on track)"}</span>
            </div>
          </div>
        );
      })}
    </Card>
  );
};

// ── Widget 4: Merchant Risk Matrix — bubble chart.
//   X = monthly volume (log-friendly), Y = VAMP bps, size = chargeback count
//   Reference lines at 50 and 150 bps make the "who's over the line" obvious.
const MerchantRiskMatrix = () => {
  const data = MERCHANT_LIST.map((m) => ({
    name: m.name,
    x: m.monthly_transactions,
    y: m.vamp_bps,
    z: Math.round(m.monthly_transactions * m.chargeback_rate_pct / 100),
    color: m.accent,
  }));
  const bubbleFill = (v) => (v > 150 ? color.danger.solid : v > 50 ? color.warning.solid : color.success.solid);
  return (
    <ChartCard title="Merchant Risk Matrix">
      <div style={{ fontSize: text.xs, color: color.text.tertiary, marginBottom: space[2] }}>
        X = monthly transactions · Y = VAMP bps · bubble size = monthly chargebacks. Above-standard line = 50 bps.
      </div>
      <ResponsiveContainer width="100%" height={260}>
        <ScatterChart margin={{ top: 12, right: 20, bottom: 8, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={color.border.subtle} />
          <XAxis type="number" dataKey="x" name="Monthly txns" fontSize={11} stroke={color.text.tertiary} tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
          <YAxis type="number" dataKey="y" name="VAMP bps" fontSize={11} stroke={color.text.tertiary} domain={[0, 160]} />
          <ZAxis type="number" dataKey="z" range={[100, 800]} name="Chargebacks" />
          <Tooltip
            cursor={{ strokeDasharray: "3 3" }}
            formatter={(v, n) => (n === "Monthly txns" ? fmtNumber(v) : n === "VAMP bps" ? `${v} bps` : fmtNumber(v))}
            labelFormatter={(_, payload) => (payload && payload[0] ? payload[0].payload.name : "")}
          />
          <Scatter data={data}>
            {data.map((d, i) => <Cell key={i} fill={bubbleFill(d.y)} />)}
          </Scatter>
          <ReferenceLine y={50}  stroke={color.warning.solid} strokeDasharray="4 4" isFront ifOverflow="visible" />
          <ReferenceLine y={150} stroke={color.danger.solid}  strokeDasharray="4 4" isFront ifOverflow="visible" />
        </ScatterChart>
      </ResponsiveContainer>
      <div style={{ fontSize: text.xs, color: color.text.tertiary, marginTop: space[2] }}>
        Bubble in the top-right = high volume + high VAMP — the merchant termination would hurt but keeping is expensive. Merchant B sits there.
      </div>
    </ChartCard>
  );
};

// ── Widget 5: Cross-merchant attack activity — Idenza-only view.
// Recent fraud patterns (fingerprint / BIN / IP) that hit >1 merchant.
const CrossMerchantAttackActivity = () => {
  const events = [
    { pattern: "kilroy.wager@fastmail.com + 4 linked",  type: "Shared device group",  merchants_hit: ["Merchant A","Merchant B"], attempts: 47, last_seen: "3 min ago", severity: "high" },
    { pattern: "vinny.tester99@protonmail.com",         type: "Card testing",         merchants_hit: ["Merchant B"],              attempts: 210, last_seen: "12 min ago", severity: "high" },
    { pattern: "delagarzaruby@gmail.com + 3 linked",    type: "Shared device group",  merchants_hit: ["Merchant A","Merchant B"], attempts: 22, last_seen: "1h ago",    severity: "medium" },
    { pattern: "harleyfox84@gmail.com + 6 linked (VPN)", type: "VPN/proxy activity",  merchants_hit: ["Merchant B"],              attempts: 84, last_seen: "2h ago",    severity: "medium" },
    { pattern: "melissabowlus2024@gmail.com",           type: "Multiple accounts",    merchants_hit: ["Merchant A"],              attempts: 12, last_seen: "4h ago",   severity: "low" },
  ];
  const sevTone = { high: "danger", medium: "warning", low: "success" };
  const columns = [
    { key: "user",  label: "User" },
    { key: "kind",  label: "What kind" },
    { key: "hit",   label: "Merchants hit" },
    { key: "attempts", label: "Attempts", numeric: true },
    { key: "when",  label: "Last seen" },
    { key: "sev",   label: "Severity" },
  ];
  const rows = events.map((e, i) => ({
    key: i,
    user:     <span style={{ color: color.text.primary }}>{e.pattern}</span>,
    kind:     <span style={{ color: color.text.secondary }}>{e.type}</span>,
    hit:      (
      <span style={{ display: "inline-flex", gap: space[1] }}>
        {e.merchants_hit.map((m) => (
          <Chip key={m} tone="brand" size="sm">{m}</Chip>
        ))}
      </span>
    ),
    attempts: <span style={{ fontWeight: weight.semibold, color: color.text.primary }}>{e.attempts}</span>,
    when:     <span style={{ color: color.text.tertiary, fontSize: text.xs }}>{e.last_seen}</span>,
    sev:      <Chip tone={sevTone[e.severity]}>{e.severity.toUpperCase()}</Chip>,
  }));
  return (
    <ChartCard title="Bad users hitting more than one merchant — last 24h">
      <div style={{ fontSize: text.xs, color: color.text.tertiary, marginBottom: space[2] }}>
        Users the agent noticed at more than one sponsored merchant. Only Idenza can see this — the agent watches every merchant at once.
      </div>
      <DataTable columns={columns} rows={rows} density="dense" />
    </ChartCard>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// AI Agent Autonomous Actions — the demo hero. Shows three scenarios where
// an autonomous agent DETECTED a rising metric, TOOK ACTION on the bank's
// behalf, ALERTED the bank after the fact, and the metric recovered in the
// following hours. Each row = one scenario with a mini time-series + the
// alert card explaining what the agent did.
// ═══════════════════════════════════════════════════════════════════════════

const AgentScenarioRow = ({
  title,
  merchant,
  metric,
  unit,
  chartData,
  interventionLabel,
  actionTaken,
  detectedText,
  resultText,
  accent,
}) => {
  const [state, setState] = useState("idle"); // idle | loading | shown
  const askAI = () => {
    setState("loading");
    // Simulate the agent going out and computing the latest numbers.
    setTimeout(() => setState("shown"), 900 + Math.random() * 500);
  };
  // Find the intervention point — both its X (time) and Y (metric value).
  // The horizontal reference line uses the Y so it shows the exact
  // threshold at which the agent decided to act.
  const markerPoint = chartData.find((d) => d.marker);
  const markerX = markerPoint?.t;
  const markerY = markerPoint?.value;
  const lineStroke = accent || color.danger.solid;
  return (
  <div style={{ padding: 0 }}>
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr", gap: space[5], alignItems: "stretch" }}>
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: space[2] }}>
          <span style={{ fontSize: text.md, fontWeight: weight.semibold, color: color.text.primary }}>{title}</span>
          <Chip tone="danger" size="sm">{merchant}</Chip>
        </div>
        <ResponsiveContainer width="100%" height={180}>
          <ComposedChart data={chartData} margin={{ top: 24, right: 20, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={chart.gridStroke} opacity={chart.gridOpacity} />
            <XAxis dataKey="t" type="category" fontSize={chart.axisTextSize} stroke={chart.axisText} />
            <YAxis fontSize={chart.axisTextSize} stroke={chart.axisText} tickFormatter={(v) => `${v}${unit === "%" ? "%" : ""}`} />
            <Tooltip formatter={(v) => [`${v}${unit}`, metric]} />
            <Line type="monotone" dataKey="value" stroke={lineStroke} strokeWidth={chart.lineStrokeWidth} dot={{ r: 2 }} isAnimationActive={false} />
            <Line type="linear" dataKey={(d) => (d.marker ? d.value : null)} stroke="none" dot={{ r: 8, fill: color.brand.solid, stroke: color.bg.surface, strokeWidth: 2 }} isAnimationActive={false} />
            {markerY != null && (
              <ReferenceLine
                y={markerY}
                stroke={chart.thresholdStroke}
                strokeWidth={2}
                strokeDasharray={chart.refDash}
                isFront
                ifOverflow="visible"
                label={{
                  value: `Action threshold (${markerY}/hr)`,
                  position: "insideTopRight",
                  fill: color.warning.text,
                  fontSize: chart.axisTextSize,
                  fontWeight: weight.semibold,
                }}
              />
            )}
            {markerX && (
              <ReferenceLine
                x={markerX}
                stroke={chart.interventionStroke}
                strokeWidth={3}
                strokeDasharray={chart.refDash}
                isFront
                ifOverflow="visible"
                label={{
                  value: "Agent action",
                  position: "top",
                  offset: 8,
                  fill: color.text.primary,
                  fontSize: text.sm,
                  fontWeight: weight.semibold,
                }}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
        <div style={{ fontSize: text.xs, color: color.text.secondary, marginTop: space[1], fontWeight: weight.medium }}>
          Dashed line = moment agent intervened ({interventionLabel}).
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: space[3] }}>
        <div style={{ fontSize: 12, color: color.text.muted, fontWeight: weight.medium }}>
          AI agent · autonomous · bank notified after action
        </div>
        <div style={{ fontSize: 14, color: color.text.primary, lineHeight: leading.normal }}>
          <strong style={{ color: color.text.primary, fontWeight: weight.semibold }}>Detected: </strong>
          {detectedText}
        </div>
        <div style={{ fontSize: 14, color: color.text.primary, lineHeight: leading.normal }}>
          <strong style={{ color: color.text.primary, fontWeight: weight.semibold }}>Action taken: </strong>
          {actionTaken}
        </div>
        {state === "idle" && (
          <button
            type="button"
            onClick={askAI}
            style={{
              alignSelf: "flex-start",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "0 12px",
              height: 32,
              borderRadius: 6,
              background: "#0A0A0A",
              color: "#FFFFFF",
              border: "1px solid #0A0A0A",
              fontSize: 13,
              fontWeight: weight.medium,
              cursor: "pointer",
              transition: motion.tx("background", "fast"),
            }}
          >
            <SparklesIcon size={14} strokeWidth={1.75} />
            Ask AI
          </button>
        )}
        {state === "loading" && (
          <div style={{ fontSize: 13, color: color.text.muted, display: "inline-flex", alignItems: "center", gap: 8, fontWeight: weight.medium }}>
            <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: radius.pill, background: color.text.muted, opacity: 0.6, animation: "idz-pulse 0.9s ease-in-out infinite" }} />
            Measuring current impact…
          </div>
        )}
        {state === "shown" && (
          <div style={{ padding: 16, borderRadius: 8, background: color.bg.subtle, border: `1px solid ${color.border.subtle}` }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: weight.semibold, color: color.text.muted, marginBottom: 6 }}>
              <SparklesIcon size={12} strokeWidth={1.75} />
              Idenza AI · What happened after · Just now
            </div>
            <div style={{ fontSize: 13, color: color.text.primary, lineHeight: 1.55 }}>
              {resultText}
            </div>
            <button
              type="button"
              onClick={askAI}
              style={{ marginTop: 8, background: "transparent", color: color.text.muted, border: "none", padding: 0, fontSize: 12, fontWeight: weight.medium, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }}
            >
              <RefreshIcon size={11} strokeWidth={1.75} /> Re-measure
            </button>
          </div>
        )}
      </div>
    </div>
  </div>
);
};

// ═══════════════════════════════════════════════════════════════════════════
// CardFraudAgentPanel — the workflow story for the card-fraud AI agent.
//
// Pipeline (6 stages) + a spotlight case selected from the queue + the HITL
// officer-review card + a rolling table of cases at various stages.
// Interactive: pipeline stages filter the queue, clicking a row updates the
// spotlight, and approve/reject/escalate advance the case to the next stage.
//
// Credibility layer: uses real fraud-domain vocab (card testing, BIN attack,
// velocity anomaly, force 3DS, cross-BIN pattern, CVV mismatch cluster,
// device fingerprint cluster, 4-eyes for hard-blacklist), a compliance-
// officer identity, confidence scores, and time-in-stage.
// ═══════════════════════════════════════════════════════════════════════════

const CARD_FRAUD_STAGES = [
  { key: "detected",   label: "Detected",      icon: <RadarIcon size={20} strokeWidth={1.75} /> },
  { key: "enriching",  label: "Enriching",     icon: <Link2Icon size={20} strokeWidth={1.75} /> },
  { key: "analyzing",  label: "Analyzing",     icon: <BrainIcon size={20} strokeWidth={1.75} /> },
  { key: "review",     label: "Officer review",icon: <UserCheckIcon size={22} strokeWidth={1.75} />, variant: "hitl" },
  { key: "actioning",  label: "Actioning",     icon: <ShieldAlertIcon size={20} strokeWidth={1.75} /> },
  { key: "monitoring", label: "Monitoring",    icon: <ActivityIcon size={20} strokeWidth={1.75} /> },
];

// Ordering of stages so we can advance a case from one to the next.
const STAGE_ORDER = ["detected", "enriching", "analyzing", "review", "actioning", "monitoring"];

const INITIAL_CASES = [
  {
    id: "CF-4821",
    trigger: "Card testing spike",
    stage: "review",
    entity: "vinny.tester99@protonmail.com",
    officer: { role: "Senior Fraud Analyst" },
    confidence: 94,
    riskTier: "High",
    timeInStage: "2h 14m",
    evidence: [
      { label: "Attempts / hr",     value: "210" },
      { label: "Decline rate",      value: "87%" },
      { label: "Linked emails",     value: "5" },
      { label: "Shared devices",    value: "3" },
      { label: "BIN attack pattern", value: "412345••, 4147••" },
      { label: "Cross-merchant",    value: "A → B → A" },
    ],
    proposedAction: "Hard-blacklist 5 linked users cross-merchant · force 3DS on 12 device-adjacent users · deposit slowdown on 17 more",
    fourEyes: true,
    reasoning: [
      { t: "09:12:04", note: "Auth-attempt anomaly detected on Merchant B — 210/hr vs 24h baseline of 42/hr" },
      { t: "09:12:07", note: "Enriched: 5 email fingerprints share a device cluster (fp:6d…8a1)" },
      { t: "09:12:11", note: "Cross-BIN pattern: 412345••, 4147•• (sequential enumeration)" },
      { t: "09:12:14", note: "Ring-signal confidence 94% (top-decile of prior blacklist actions)" },
      { t: "09:12:15", note: "4-eyes required — hard blacklist affects >3 users; routed to Officer Review" },
    ],
  },
  {
    id: "CF-4820",
    trigger: "Cross-BIN velocity anomaly",
    stage: "analyzing",
    entity: "kilroy.wager@fastmail.com",
    confidence: 82,
    riskTier: "Medium",
    timeInStage: "9m",
    evidence: [
      { label: "Deposits · 15m",    value: "8" },
      { label: "Distinct BINs",     value: "4" },
      { label: "IP reputation",     value: "flagged" },
    ],
    proposedAction: "Auto-enable force 3DS · 24h monitoring window",
    fourEyes: false,
    reasoning: [
      { t: "10:47:22", note: "Velocity 4x baseline; ATO signal" },
      { t: "10:47:28", note: "Enriched IP reputation from Cloudflare RiskRadar (score 0.71)" },
    ],
  },
  {
    id: "CF-4819",
    trigger: "BIN attack signal",
    stage: "enriching",
    entity: "harleyfox84@gmail.com",
    confidence: 71,
    riskTier: "Medium",
    timeInStage: "3m",
    evidence: [
      { label: "Failed auth",       value: "17" },
      { label: "AVS mismatch",      value: "14 of 17" },
    ],
    proposedAction: "Pending analysis",
    fourEyes: false,
    reasoning: [
      { t: "10:53:11", note: "AVS mismatch cluster — enriching device fingerprint" },
    ],
  },
  {
    id: "CF-4818",
    trigger: "Chargeback ring · 71% of last 14d",
    stage: "actioning",
    entity: "delagarzaruby@gmail.com",
    confidence: 89,
    riskTier: "High",
    timeInStage: "just now",
    evidence: [
      { label: "Ring size",         value: "8 users" },
      { label: "CB share",          value: "71%" },
    ],
    proposedAction: "Auto-refund <$50 CBs · block 3 users · force 3DS on 12",
    fourEyes: false,
    reasoning: [],
  },
  {
    id: "CF-4817",
    trigger: "Device fingerprint cluster",
    stage: "monitoring",
    entity: "melissabowlus2024@gmail.com",
    confidence: 76,
    riskTier: "Low",
    timeInStage: "6h",
    evidence: [
      { label: "Fingerprint",       value: "fp:6d…8a1" },
      { label: "24h chargebacks",   value: "0" },
    ],
    proposedAction: "24h monitoring · auto-graduate at 48h clean",
    fourEyes: false,
    reasoning: [],
  },
  {
    id: "CF-4816",
    trigger: "CVV mismatch cluster",
    stage: "detected",
    entity: "teamvl.114@gmail.com",
    confidence: 68,
    riskTier: "Low",
    timeInStage: "42s",
    evidence: [
      { label: "CVV mismatch",      value: "9 of 11" },
    ],
    proposedAction: "Pending enrichment",
    fourEyes: false,
    reasoning: [],
  },
  {
    id: "CF-4815",
    trigger: "Force-3DS graduation candidate",
    stage: "review",
    entity: "rizziexx@hotmail.com",
    officer: { role: "BSA Compliance Officer" },
    confidence: 91,
    riskTier: "Medium",
    timeInStage: "1h 08m",
    evidence: [
      { label: "3DS pass rate",     value: "97%" },
      { label: "Days on 3DS",       value: "48" },
      { label: "Chargebacks",       value: "0" },
    ],
    proposedAction: "Graduate off force-3DS · move to prefer-3DS",
    fourEyes: true,
    reasoning: [
      { t: "09:52:00", note: "Meets graduation criteria: 30d + 95% pass + 0 chargebacks" },
    ],
  },
  {
    id: "CF-4814",
    trigger: "Ring-signal recurrence",
    stage: "review",
    entity: "stephaniewright19855@gmail.com",
    officer: { role: "Senior Fraud Analyst" },
    confidence: 88,
    riskTier: "High",
    timeInStage: "27m",
    evidence: [
      { label: "Ring size",         value: "3 users" },
      { label: "Prior blocklist",   value: "yes" },
    ],
    proposedAction: "Hard-blacklist · extend cross-merchant blocklist",
    fourEyes: true,
    reasoning: [
      { t: "10:26:00", note: "Recurrence of prior ring CF-4802; confidence 88%" },
    ],
  },
];

// Card-fraud workflow now lives in a shared main-skin component
// (src/components/CardFraudAgentPanel.jsx) so both the pre-facelift and
// facelift dashboards render the same thing. Per direction "main-skin
// first, facelift is a skin only". The inline INITIAL_CASES /
// CARD_FRAUD_STAGES / STAGE_ORDER above are now dead but kept for
// reference — safe to prune in a later cleanup pass.
// (Import + render happens at the top of the file / in the JSX below.)

// ── Small metric cell in the strip above the pipeline.
const SmallStat = ({ label, value, sub, isLast }) => (
  <div
    style={{
      padding: "12px 16px",
      borderRight: isLast ? "none" : `1px solid ${color.border.subtle}`,
      display: "flex",
      flexDirection: "column",
      gap: 2,
    }}
  >
    <span style={{ fontSize: 11, color: color.text.muted, fontWeight: 500, letterSpacing: "0.02em" }}>{label}</span>
    <span style={{ fontSize: 20, fontWeight: 600, color: color.text.primary, letterSpacing: "-0.02em", ...numeric.tabular }}>{value}</span>
    <span style={{ fontSize: 11, color: color.text.tertiary }}>{sub}</span>
  </div>
);

// ── Stage badge — colored pill matching the stage identity.
const STAGE_STYLE = {
  detected:   { bg: "rgba(79,70,229,0.10)",  fg: "#3730A3", label: "Detected" },
  enriching:  { bg: "rgba(79,70,229,0.10)",  fg: "#3730A3", label: "Enriching" },
  analyzing:  { bg: "rgba(79,70,229,0.10)",  fg: "#3730A3", label: "Analyzing" },
  review:     { bg: "rgba(11,87,164,0.10)",  fg: "#0B57A4", label: "Officer review" },
  actioning:  { bg: "rgba(15,122,61,0.10)",  fg: "#0F7A3D", label: "Actioning" },
  monitoring: { bg: "rgba(180,71,8,0.10)",   fg: "#B54708", label: "Monitoring" },
};

const StageBadge = ({ stage }) => {
  const s = STAGE_STYLE[stage] || STAGE_STYLE.detected;
  return (
    <span style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      padding: "1px 8px",
      borderRadius: 4,
      background: s.bg,
      color: s.fg,
      fontSize: 10,
      fontWeight: 600,
      letterSpacing: "0.04em",
      textTransform: "uppercase",
      whiteSpace: "nowrap",
    }}>
      {s.label}
    </span>
  );
};

// ── One row in the case queue table.
const CaseRow = ({ c, selected, onSelect, isFirst }) => {
  const [hover, setHover] = useState(false);
  return (
    <div
      onClick={onSelect}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: "grid",
        gridTemplateColumns: "90px 1.4fr 130px 1fr 60px 90px 20px",
        alignItems: "center",
        gap: 12,
        padding: "10px 14px",
        cursor: "pointer",
        borderTop: isFirst ? "none" : `1px solid var(--border-hairline)`,
        background: selected ? "rgba(11,87,164,0.06)" : hover ? color.bg.hover : "transparent",
        boxShadow: selected ? `inset 2px 0 0 ${color.brand.solid}` : "none",
        transition: "background 120ms cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      <span style={{ fontSize: 12, color: color.text.muted, fontWeight: 600, fontFamily: "'Geist Mono', 'JetBrains Mono', 'Menlo', ui-monospace, monospace", ...numeric.tabular }}>
        {c.id}
      </span>
      <span style={{ fontSize: 13, color: color.text.primary, fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
        {c.trigger}
      </span>
      <StageBadge stage={c.stage} />
      <span style={{ fontSize: 12, color: color.text.secondary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={c.entity}>
        {c.entity}
      </span>
      <span style={{ fontSize: 12, color: color.text.primary, fontWeight: 600, ...numeric.tabular }}>
        {c.confidence}%
      </span>
      <span style={{ fontSize: 12, color: color.text.muted, ...numeric.tabular }}>
        {c.timeInStage}
      </span>
      <ChevronRightIcon size={14} strokeWidth={1.75} color={color.text.tertiary} />
    </div>
  );
};

// ── Spotlight case — big card showing evidence, agent proposed action,
// and the HITL officer-review panel (if the case is in review).
const SpotlightCase = ({ c, onAction }) => {
  const inReview = c.stage === "review";
  return (
    <div style={{
      display: "grid",
      gridTemplateColumns: inReview ? "1.4fr 1fr" : "1fr",
      gap: 20,
      alignItems: "start",
    }}>
      {/* Left — case detail + evidence + agent action + reasoning trace */}
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
          <span style={{ fontSize: 11, color: color.text.muted, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", fontFamily: "'Geist Mono', 'JetBrains Mono', 'Menlo', ui-monospace, monospace" }}>
            {c.id}
          </span>
          <StageBadge stage={c.stage} />
          <RiskTierBadge tier={c.riskTier} />
          {c.fourEyes && (
            <span style={{
              display: "inline-flex", alignItems: "center", gap: 4,
              padding: "1px 8px", borderRadius: 4,
              background: "rgba(11,87,164,0.10)", color: "#0B57A4",
              fontSize: 10, fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase",
            }}>4-eyes</span>
          )}
          <span style={{ fontSize: 12, color: color.text.muted, marginLeft: "auto", ...numeric.tabular }}>
            Confidence <strong style={{ color: color.text.primary, fontWeight: 600 }}>{c.confidence}%</strong>
          </span>
        </div>

        <div style={{ fontSize: 18, fontWeight: 600, color: color.text.primary, letterSpacing: "-0.01em", marginBottom: 4 }}>
          {c.trigger}
        </div>
        <div style={{ fontSize: 13, color: color.text.muted, marginBottom: 16 }}>
          Entity — <span style={{ color: color.text.secondary, fontWeight: 500 }}>{c.entity}</span>
          <span style={{ margin: "0 8px", color: color.text.tertiary }}>·</span>
          Time in stage — <span style={{ color: color.text.secondary, ...numeric.tabular }}>{c.timeInStage}</span>
        </div>

        {/* Evidence grid */}
        <div style={{ fontSize: 11, color: color.text.muted, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 6 }}>
          Evidence chain
        </div>
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
          gap: 8,
          marginBottom: 18,
        }}>
          {c.evidence.map((e, i) => (
            <div key={i} style={{
              padding: "8px 10px",
              borderRadius: 6,
              background: color.bg.subtle,
              border: `1px solid ${color.border.subtle}`,
            }}>
              <div style={{ fontSize: 10, color: color.text.muted, fontWeight: 500, letterSpacing: "0.02em" }}>{e.label}</div>
              <div style={{ fontSize: 13, color: color.text.primary, fontWeight: 600, ...numeric.tabular }}>{e.value}</div>
            </div>
          ))}
        </div>

        {/* Agent proposed action */}
        <div style={{
          padding: "12px 14px",
          borderRadius: 8,
          border: `1px solid ${color.brand.border}`,
          background: `linear-gradient(180deg, rgba(11,87,164,0.06) 0%, rgba(11,87,164,0.02) 100%), ${color.bg.surface}`,
          marginBottom: 14,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: color.brand.solid, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 4 }}>
            <SparklesIcon size={12} strokeWidth={1.75} />
            Agent proposed action
          </div>
          <div style={{ fontSize: 13, color: color.text.primary, lineHeight: 1.5 }}>
            {c.proposedAction}
          </div>
        </div>

        {/* Reasoning trace — collapsible feel via monospace timestamps */}
        {c.reasoning.length > 0 && (
          <>
            <div style={{ fontSize: 11, color: color.text.muted, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 6 }}>
              Chain of reasoning
            </div>
            <div style={{
              padding: "10px 14px",
              borderRadius: 8,
              background: color.bg.subtle,
              border: `1px solid ${color.border.subtle}`,
              display: "flex",
              flexDirection: "column",
              gap: 6,
            }}>
              {c.reasoning.map((r, i) => (
                <div key={i} style={{ display: "flex", gap: 10, alignItems: "baseline" }}>
                  <span style={{ fontSize: 11, color: color.text.tertiary, fontFamily: "'Geist Mono', 'JetBrains Mono', 'Menlo', ui-monospace, monospace", ...numeric.tabular, flexShrink: 0 }}>
                    {r.t}
                  </span>
                  <span style={{ fontSize: 12, color: color.text.secondary, lineHeight: 1.5 }}>{r.note}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Right — HITL officer review card, only when in review */}
      {inReview && <OfficerReviewCard c={c} onAction={onAction} />}
    </div>
  );
};

// ── Risk tier badge — small pill next to case ID.
const RiskTierBadge = ({ tier }) => {
  const map = {
    High:   { bg: "rgba(180,35,24,0.10)",   fg: "#B42318" },
    Medium: { bg: "rgba(181,71,8,0.10)",    fg: "#B54708" },
    Low:    { bg: "rgba(15,122,61,0.10)",   fg: "#0F7A3D" },
  };
  const s = map[tier] || map.Low;
  return (
    <span style={{
      padding: "1px 8px",
      borderRadius: 4,
      background: s.bg,
      color: s.fg,
      fontSize: 10,
      fontWeight: 600,
      letterSpacing: "0.04em",
      textTransform: "uppercase",
    }}>{tier} risk</span>
  );
};

// ── HITL officer review card — approve / reject / escalate + officer identity.
const OfficerReviewCard = ({ c, onAction }) => {
  const [note, setNote] = useState("");
  const [processing, setProcessing] = useState(null);
  const run = (mode) => {
    setProcessing(mode);
    setTimeout(() => {
      onAction(c.id, mode);
      setProcessing(null);
      setNote("");
    }, 400);
  };
  return (
    <div style={{
      padding: "18px 20px",
      borderRadius: 10,
      border: `1px solid ${color.brand.border}`,
      background: `linear-gradient(180deg, rgba(11,87,164,0.08) 0%, rgba(11,87,164,0.02) 40%, ${color.bg.surface} 100%)`,
      boxShadow: "0 1px 3px rgba(11,87,164,0.06)",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: color.brand.solid, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 12 }}>
        <UserCheckIcon size={12} strokeWidth={1.75} />
        Human-in-the-loop · Officer review
      </div>

      {/* Assigned reviewer (role-only, no personal identity) */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderRadius: 8, background: color.bg.surface, border: `1px solid ${color.border.subtle}`, marginBottom: 14 }}>
        <div style={{
          width: 36, height: 36, borderRadius: 999,
          background: "linear-gradient(135deg, #0B57A4 0%, #094486 100%)",
          color: "#FFFFFF",
          display: "flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0,
        }}>
          <UserCheckIcon size={18} strokeWidth={1.75} />
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: color.text.primary, letterSpacing: "-0.01em" }}>{c.officer.role}</div>
          <div style={{ fontSize: 11, color: color.text.muted, letterSpacing: "0.02em" }}>Assigned reviewer · queue rotation</div>
        </div>
      </div>

      {/* Optional decision note */}
      <label style={{ display: "block", fontSize: 11, color: color.text.muted, fontWeight: 500, letterSpacing: "0.02em", marginBottom: 4 }}>
        Decision note (optional)
      </label>
      <textarea
        rows={2}
        placeholder="e.g. Ring confirmed via prior blocklist; approving hard-blacklist across sponsored merchants."
        value={note}
        onChange={(e) => setNote(e.target.value)}
        style={{
          width: "100%",
          padding: "8px 10px",
          borderRadius: 6,
          border: `1px solid ${color.border.subtle}`,
          fontSize: 12,
          fontFamily: "inherit",
          color: color.text.primary,
          background: color.bg.surface,
          resize: "vertical",
          marginBottom: 12,
        }}
      />

      {/* Action buttons */}
      <div style={{ display: "flex", gap: 8 }}>
        <button
          type="button"
          onClick={() => run("approve")}
          disabled={processing !== null}
          style={{
            flex: 1,
            display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
            height: 34,
            border: "none",
            borderRadius: 6,
            background: "#0F7A3D",
            color: "#FFFFFF",
            fontSize: 13,
            fontWeight: 600,
            cursor: processing ? "wait" : "pointer",
            opacity: processing && processing !== "approve" ? 0.5 : 1,
            fontFamily: "inherit",
            transition: "opacity 120ms",
          }}
        >
          <CheckIcon size={14} strokeWidth={2} />
          {processing === "approve" ? "Approving…" : "Approve"}
        </button>
        <button
          type="button"
          onClick={() => run("reject")}
          disabled={processing !== null}
          style={{
            display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
            height: 34,
            padding: "0 12px",
            border: `1px solid ${color.border.subtle}`,
            borderRadius: 6,
            background: color.bg.surface,
            color: color.text.primary,
            fontSize: 13,
            fontWeight: 500,
            cursor: processing ? "wait" : "pointer",
            opacity: processing && processing !== "reject" ? 0.5 : 1,
            fontFamily: "inherit",
          }}
        >
          <XIcon size={14} strokeWidth={2} />
          Reject
        </button>
        <button
          type="button"
          onClick={() => run("escalate")}
          disabled={processing !== null}
          style={{
            display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
            height: 34,
            padding: "0 12px",
            border: `1px solid ${color.border.subtle}`,
            borderRadius: 6,
            background: color.bg.surface,
            color: color.text.primary,
            fontSize: 13,
            fontWeight: 500,
            cursor: processing ? "wait" : "pointer",
            opacity: processing && processing !== "escalate" ? 0.5 : 1,
            fontFamily: "inherit",
          }}
        >
          <EscalateIcon size={14} strokeWidth={2} />
          Escalate
        </button>
      </div>

      <div style={{ marginTop: 12, fontSize: 11, color: color.text.muted, lineHeight: 1.5 }}>
        {c.fourEyes
          ? "4-eyes required — a second officer must co-sign hard-blacklists affecting more than 3 users."
          : "Auto-approval eligible — this action is within the agent's autonomy envelope."}
      </div>
    </div>
  );
};

const AgentInterventionsPanel = () => {
  // Card testing is the ONE metric with a legit hourly cadence — auth
  // attempts stream in real time, so the "agent intervened, curve
  // recovered in 4-5 hours" story lines up with reality. Jagged trajectory
  // with a small counter-attack bump post-intervention as the ring tries
  // to route around before giving up.
  const cardTestingData = [
    { t: "-24h", value: 42 }, { t: "-22h", value: 38 }, { t: "-20h", value: 55 },
    { t: "-18h", value: 48 }, { t: "-16h", value: 68 }, { t: "-14h", value: 62 },
    { t: "-12h", value: 88 }, { t: "-10h", value: 74 }, { t: "-8h",  value: 142 },
    { t: "-7h",  value: 178 },
    { t: "-6h",  value: 210, marker: true },
    { t: "-5h",  value: 175 },{ t: "-4h",  value: 138 },{ t: "-3h",  value: 156 },
    { t: "-2h",  value: 92 }, { t: "-1h",  value: 68 }, { t: "now",  value: 44 },
  ];
  return (
    <RolePanel
      role="ai"
      title="An example of what the AI caught this week"
      subtitle="The AI spotted a card-testing attack, took action, then told the bank. Dashed line marks when the AI stepped in."
      icon={<SparklesIcon size={16} strokeWidth={1.75} />}
      right={<LiveDot color={color.role.ai.solid} />}
    >
      <div style={{ marginTop: 8 }}>
      <AgentScenarioRow
        title="Card-testing attack · Merchant B · 6 hours ago"
        merchant="Merchant B"
        metric="Card-test attempts per hour"
        unit=""
        chartData={cardTestingData}
        interventionLabel="6 hours ago"
        detectedText="5 linked visitors — anchored on vinny.tester99@protonmail.com — pushed 210 card attempts per hour combined (42 per visitor). Our card-testing rule fires at just 3 attempts/hour on amounts under $25, so this group was ~14× over. 87% of attempts declined (bad BINs)."
        actionTaken="Added all 5 to the Blacklisted User list (auto-declines every future attempt across all sponsored merchants), and applied the Cumulative Abuse slowdown on Merchant B."
        resultText="Attempts on Merchant B dropped from 210/hour to 44/hour — down 79% in 6 hours. None of the blocked users got through after the action. The group tried 3 new email addresses 2–3 hours in, but the agent caught each one immediately. Estimated savings: about $412 in fees and losses. Confidence: 94%."
        accent={color.role.ai.solid}
      />
      </div>
    </RolePanel>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// VAMP + Chargeback containment panel — for KPIs that can't be shown as
// real-time curves (VAMP is a rolling 30-day ratio, chargebacks arrive
// 15-60d after the txn). Patterns stolen from the vendor research:
//   A · Deflection counter (Kount, Chargeflow) — big number of user-level
//       agent actions × fixed-$ multiplier
//   B · Projected-vs-Confirmed split (Justt, Riskified) — highest-trust
//       framing for lagging KPIs, with a freshness widget
//   C · User-level agent activity feed (Sardine) — replaces the useless
//       time-series with an event stream where each row is one user
//   E · Confidence chips (Projected / Confirmed / Watching)
// All rows are USER-level. Card / device / BIN are backend features, not
// operator-facing entities — the bank operator only sees who the agent
// acted on and what the impact is.
// ═══════════════════════════════════════════════════════════════════════════

// FreshnessTag now comes from the design system (imported at the top).
// Old inline definition removed — see design/primitives/FreshnessTag.jsx.

const ConfidenceChip = ({ kind }) => {
  const toneMap = { Projected: "brand", Confirmed: "success", Watching: "neutral" };
  const tone = toneMap[kind] ?? "brand";
  return (
    <Chip tone={tone}>{kind.toUpperCase()}</Chip>
  );
};

// Ask AI — flat charcoal pill with Lucide Sparkles icon (not emoji).
// Attio AI / Ramp Copilot / Linear pattern: neutral, low-key, auditable.
// `tone` prop kept for API compat but ignored — one visual style everywhere.
const AskAiInline = ({ resultText }) => {
  const [state, setState] = useState("idle");
  const ask = () => {
    setState("loading");
    setTimeout(() => setState("shown"), 900 + Math.random() * 500);
  };
  if (state === "idle") {
    return (
      <button
        type="button"
        onClick={ask}
        style={{
          alignSelf: "flex-start",
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "0 12px",
          height: 32,
          borderRadius: 6,
          background: "#0A0A0A",
          color: "#FFFFFF",
          border: "1px solid #0A0A0A",
          fontSize: 13,
          fontWeight: weight.medium,
          cursor: "pointer",
          transition: motion.tx("background", "fast"),
        }}
      >
        <SparklesIcon size={14} strokeWidth={1.75} />
        Ask AI
      </button>
    );
  }
  if (state === "loading") {
    return (
      <div style={{ fontSize: 13, color: color.text.muted, display: "inline-flex", alignItems: "center", gap: 8, fontWeight: weight.medium }}>
        <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: radius.pill, background: color.text.muted, opacity: 0.6, animation: "idz-pulse 0.9s ease-in-out infinite" }} />
        Measuring current impact…
      </div>
    );
  }
  return (
    <div style={{ padding: 16, borderRadius: 8, background: color.bg.subtle, border: `1px solid ${color.border.subtle}` }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: weight.semibold, color: color.text.muted, marginBottom: 6, letterSpacing: 0 }}>
        <SparklesIcon size={12} strokeWidth={1.75} />
        Idenza AI · Impact analysis · Just now
      </div>
      <div style={{ fontSize: 13, color: color.text.primary, lineHeight: 1.55 }}>
        {resultText}
      </div>
      <button
        type="button"
        onClick={ask}
        style={{ marginTop: 8, background: "transparent", color: color.text.muted, border: "none", padding: 0, fontSize: 12, fontWeight: weight.medium, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }}
      >
        <RefreshIcon size={11} strokeWidth={1.75} /> Re-measure
      </button>
    </div>
  );
};

// Count-up hook — animates a number from 0 to `target` over `durationMs`.
// Ramp cash-position move. Runs once on mount, respects prefers-reduced-motion.
const useCountUp = (target, durationMs = 900) => {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      setValue(target);
      return;
    }
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / durationMs);
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(Math.round(eased * target));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, durationMs]);
  return value;
};

// Chargeback Deflection — the money-saved trophy for the sponsor bank.
// Same RolePanel(ai) grammar as every other AI panel, but the eye is drawn
// through TYPOGRAPHY WEIGHT (56px cobalt number) not surface inversion.
// Consistency wins; the number does the shouting.
const ChargebackDeflectionCard = () => {
  const dollars = useCountUp(3290);
  const chargebacks = useCountUp(47);
  const weeklyData = [
    { week: "-7w", value: 14 },
    { week: "-6w", value: 19 },
    { week: "-5w", value: 17 },
    { week: "-4w", value: 28 },
    { week: "-3w", value: 25 },
    { week: "-2w", value: 36 },
    { week: "-1w", value: 33 },
    { week: "now", value: 47, current: true },
  ];
  return (
  <RolePanel
    role="ai"
    title="Chargebacks the AI stopped · portfolio-wide"
    subtitle="Every action was taken by the AI on its own · you were notified after"
    icon={<SparklesIcon size={16} strokeWidth={1.75} />}
    right={<LiveDot color={color.role.ai.solid} />}
  >
    {/* Hero row — big cobalt $ number left, weekly bars right */}
    <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 32, alignItems: "flex-end", marginTop: 8, marginBottom: 20 }}>
      <div>
        <div style={{ fontSize: 11, color: color.text.muted, letterSpacing: "0.06em", textTransform: "uppercase", fontWeight: 500, marginBottom: 6 }}>
          Money you saved · this week
        </div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 12 }}>
          <span style={{
            fontSize: 56,
            fontWeight: weight.semibold,
            color: color.brand.solid,
            letterSpacing: "-0.03em",
            lineHeight: 1,
            ...numeric.tabular,
          }}>
            ${dollars.toLocaleString()}
          </span>
        </div>
        <div style={{ marginTop: 10, fontSize: 13, color: color.text.muted }}>
          By stopping{" "}
          <span style={{ color: color.text.primary, fontWeight: weight.semibold, ...numeric.tabular }}>{chargebacks}</span> chargebacks
          on{" "}
          <span style={{ color: color.text.primary, fontWeight: weight.semibold, ...numeric.tabular }}>32</span> users across the portfolio.
        </div>
        <div style={{ marginTop: 4, fontSize: 12, color: color.text.tertiary }}>
          Math: $15 Visa dispute fee per chargeback + our estimate of the fraud loss avoided.
        </div>
      </div>

      <div style={{ position: "relative" }}>
        <div style={{ fontSize: 11, color: color.text.muted, letterSpacing: "0.06em", textTransform: "uppercase", fontWeight: 500, marginBottom: 4 }}>
          Weekly · last 8 weeks
        </div>
        <ResponsiveContainer width="100%" height={80}>
          <BarChart data={weeklyData} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
            <XAxis dataKey="week" hide />
            <YAxis hide />
            <Bar dataKey="value" radius={[3, 3, 0, 0]}>
              {weeklyData.map((d, i) => (
                <Cell key={i} fill={d.current ? color.brand.solid : "var(--border-strong)"} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        <div style={{ fontSize: 11, color: color.text.tertiary, marginTop: 2 }}>
          Each bar = chargebacks stopped that week
        </div>
      </div>
    </div>

    <div style={{ height: 1, background: color.border.subtle, marginBottom: 16 }} />

    {/* Story lines — portfolio-wide framing, plain English */}
    <div style={{ fontSize: 13, color: color.text.primary, marginBottom: 6, lineHeight: 1.55 }}>
      <span style={{ color: color.role.ai.solid, fontWeight: weight.semibold }}>What the AI noticed — </span>
      <span style={{ color: color.text.secondary }}>A cluster of 8 users on your sponsored merchants was behind 71% of the new chargebacks in the last two weeks. All 8 tripped our "Payer high decline rate" rule (over 40% declines with at least 5 transactions on file).</span>
    </div>
    <div style={{ fontSize: 13, color: color.text.primary, marginBottom: 16, lineHeight: 1.55 }}>
      <span style={{ color: color.role.ai.solid, fontWeight: weight.semibold }}>What the AI did about it — </span>
      <span style={{ color: color.text.secondary }}>Added 3 to the Blacklisted User list (auto-declines every future attempt), put 12 more on Force-3DS (mandatory password check), and applied the Cumulative Abuse slowdown to the other 17. It also turned on operational auto-refund for chargebacks under $50 — cheaper than paying the $15 Visa dispute fee, and it keeps VAMP down.</span>
    </div>

    <AskAiInline
      resultText={
        <>
          The plan is working. The 3 blocked users used to have <strong>34% of their deposits reversed</strong> — blocking them prevents roughly <strong>19 more chargebacks</strong> over the next 30 days. The 12 users with the extra password check have had <strong>zero chargebacks</strong> across 21 successful deposits since we turned it on.
          <br/><br/>
          <strong>Auto-refund:</strong> 12 small chargebacks refunded automatically this week — that's 12 formal disputes we didn't have to file at $15 each = $180 direct, plus a lower VAMP ratio.
          <br/><br/>
          <strong>Running total:</strong> {chargebacks} chargebacks stopped so far. <strong>Confidence:</strong> 89%.
        </>
      }
    />

    <div style={{ fontSize: 11, color: color.text.tertiary, marginTop: 14 }}>
      By action type — <strong style={{ color: color.text.muted, fontWeight: weight.semibold, ...numeric.tabular }}>18</strong> users blocked · <strong style={{ color: color.text.muted, fontWeight: weight.semibold, ...numeric.tabular }}>21</strong> put on extra password check · <strong style={{ color: color.text.muted, fontWeight: weight.semibold, ...numeric.tabular }}>8</strong> deposits slowed. Numbers update daily as new chargebacks post.
    </div>
  </RolePanel>
  );
};

// RolePanel + LiveDot now live in design/primitives — imported at the top.

const AgentUserActionFeed = () => {
  const events = [
    { time: "9 min ago",  email: "teamvl.114@gmail.com",             action: "Blocked",           reason: "3 chargebacks in the last month, part of a linked group",  impact: "≈4 chargebacks stopped",    kind: "Projected" },
    { time: "1h 24m ago", email: "rizziexx@hotmail.com",             action: "3DS check on",      reason: "High chargeback risk",                                     impact: "≈2 chargebacks stopped",    kind: "Projected" },
    { time: "2h ago",     email: "delagarzaruby@gmail.com",          action: "Flagged for review", reason: "Sudden burst of deposits across merchants",                impact: "watch mode",                kind: "Projected" },
    { time: "4h ago",     email: "stephaniewright19855@gmail.com",   action: "Blocked",           reason: "Part of a confirmed linked group",                         impact: "≈4 chargebacks stopped",    kind: "Projected" },
    { time: "Yesterday",  email: "vinny.tester99@protonmail.com",    action: "Deposits slowed",   reason: "Card testing across 2 merchants",                          impact: "≈3 chargebacks stopped",    kind: "Projected" },
    { time: "2 days ago", email: "melissabowlus2024@gmail.com",      action: "3DS check on",      reason: "Shares a device with a flagged group",                     impact: "≈1 chargeback stopped",     kind: "Projected" },
    { time: "6 days ago", email: "kilroy.wager@fastmail.com",        action: "Blocked",           reason: "Multi-account bonus abuse",                                impact: "≈2 chargebacks stopped",    kind: "Projected" },
    { time: "8 days ago", email: "harleyfox84@gmail.com",             action: "3DS check on",      reason: "High card decline rate",                                   impact: "≈1 chargeback stopped",     kind: "Projected" },
  ];
  const actionColor = {
    "Blocked":              color.danger.text,
    "3DS check on":         color.warning.text,
    "Deposits slowed":      color.brand.text,
    "Flagged for review":   color.brand.text,
  };
  return (
    <RolePanel
      role="ai"
      title="Agent user-level actions"
      count={events.length}
      subtitle="Each row is one user the agent acted on. The bank just sees the user, the reason, and the estimated impact."
      icon={<SparklesIcon size={16} strokeWidth={1.75} />}
      right={<LiveDot color={color.role.ai.solid} />}
    >
      <div style={{ display: "flex", flexDirection: "column", maxHeight: 420, overflowY: "auto", marginTop: 4 }}>
        {events.map((e, i) => (
          <div
            key={i}
            style={{
              display: "grid",
              gridTemplateColumns: "84px 1fr auto",
              gap: 12,
              padding: "12px 4px 12px 12px",
              borderTop: i === 0 ? "none" : `1px solid var(--border-hairline)`,
              alignItems: "center",
              boxShadow: i === 0 ? `inset 2px 0 0 ${color.role.ai.solid}` : "none",
            }}
          >
            <span style={{ fontSize: 12, color: color.text.tertiary, fontWeight: weight.medium, ...numeric.tabular }}>{e.time}</span>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, color: color.text.primary, fontWeight: weight.semibold, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{e.email}</div>
              <div style={{ fontSize: 12, color: color.text.muted, marginTop: 2 }}>
                <span style={{ color: actionColor[e.action] ?? color.text.primary, fontWeight: weight.semibold }}>{e.action}</span>
                <span> · {e.reason}</span>
              </div>
              <div style={{ fontSize: 12, color: color.text.muted, marginTop: 2, fontStyle: "italic" }}>
                Impact — <strong style={{ color: e.kind === "Confirmed" ? color.success.text : color.role.ai.solid, fontWeight: weight.semibold, fontStyle: "normal" }}>{e.impact}</strong>
              </div>
            </div>
            <ConfidenceChip kind={e.kind} />
          </div>
        ))}
      </div>
    </RolePanel>
  );
};

const VampChargebackContainmentPanel = () => (
  <Row className="mb-4 g-3">
    <Col xl={12} lg={12}>
      <ChargebackDeflectionCard />
    </Col>
  </Row>
);

// ── Widget: Cross-Merchant User Blocking Live Feed — Idenza-only view.
// Shows real-time cases where fraud hitting Merchant A was preemptively
// blocked at Merchant B (bank's entire book defended in one motion).
const CrossMerchantBlockFeed = () => {
  // The agent's underlying block signal is a shared device fingerprint /
  // card BIN / IP range — but the operator-facing surface shows only the
  // user identity that connected the two merchants. That's the level of
  // detail a bank operator cares about.
  const events = [
    { time: "2 min ago",   email: "kilroy.wager@fastmail.com",       seen_at: "Merchant A", blocked_at: "Merchant B", time_saved: "before 3 attempts", severity: "high" },
    { time: "8 min ago",   email: "vinny.tester99@protonmail.com",   seen_at: "Merchant A", blocked_at: "Merchant B", time_saved: "before 1 attempt",  severity: "medium" },
    { time: "14 min ago",  email: "stephaniewright19855@gmail.com",  seen_at: "Merchant B", blocked_at: "Merchant A", time_saved: "before 5 attempts", severity: "high" },
    { time: "27 min ago",  email: "teamvl.114@gmail.com",            seen_at: "Merchant B", blocked_at: "Merchant A", time_saved: "before 7 attempts", severity: "medium" },
    { time: "42 min ago",  email: "rizziexx@hotmail.com",            seen_at: "Merchant A", blocked_at: "Merchant B", time_saved: "before 2 attempts", severity: "low" },
    { time: "1h ago",      email: "kathrynelizabeth2355@gmail.com",  seen_at: "Merchant A", blocked_at: "Merchant B", time_saved: "before 4 attempts", severity: "high" },
    { time: "1h 12m ago",  email: "delagarzaruby@gmail.com",         seen_at: "Merchant A", blocked_at: "Merchant B", time_saved: "before 1 attempt",  severity: "medium" },
    { time: "2h ago",      email: "melissabowlus2024@gmail.com",     seen_at: "Merchant B", blocked_at: "Merchant A", time_saved: "before 3 attempts", severity: "medium" },
  ];
  const sevTone = { high: "danger", medium: "warning", low: "success" };
  return (
    <RolePanel
      role="live"
      title="Cross-merchant auto-blacklisted"
      count={events.length}
      subtitle="When a user is caught at one merchant, the agent blocks them at every other sponsored merchant within seconds. Only Idenza can do this."
      icon={<ZapIcon size={16} strokeWidth={1.75} />}
      right={<LiveDot color={color.role.live.solid} />}
    >
      <div style={{ display: "flex", flexDirection: "column", maxHeight: 400, overflowY: "auto", marginTop: 4 }}>
        {events.map((e, i) => (
          <div
            key={i}
            style={{
              display: "grid",
              gridTemplateColumns: "84px 1fr auto",
              gap: 12,
              padding: "12px 4px 12px 12px",
              borderTop: i === 0 ? "none" : `1px solid var(--border-hairline)`,
              alignItems: "center",
              // Newest row gets a decaying cyan left accent — Linear Inbox move.
              boxShadow: i === 0 ? `inset 2px 0 0 ${color.role.live.solid}` : "none",
            }}
          >
            <span style={{ fontSize: 12, color: color.text.tertiary, fontWeight: weight.medium, ...numeric.tabular }}>{e.time}</span>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, color: color.text.primary, fontWeight: weight.semibold, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {e.email}
              </div>
              <div style={{ fontSize: 12, color: color.text.muted, marginTop: 2 }}>
                caught at <strong style={{ color: color.text.primary, fontWeight: weight.semibold }}>{e.seen_at}</strong>
                {" · "}auto-blocked at <strong style={{ color: color.text.primary, fontWeight: weight.semibold }}>{e.blocked_at}</strong>
                {" · "}<span style={{ color: color.success.text }}>{e.time_saved}</span>
              </div>
            </div>
            <Chip tone={sevTone[e.severity]} size="sm">
              {e.severity.toUpperCase()}
            </Chip>
          </div>
        ))}
      </div>
    </RolePanel>
  );
};

const PortfolioOverviewSection = () => {
  const atRisk = MERCHANT_LIST.filter((m) => m.vamp_bps > 50).length;
  return (
    <>
      <SectionBanner title="Portfolio overview" count={MERCHANT_LIST.length} />
      <KpiStrip>
        <KpiCell
          label="Sponsored merchants"
          value={MERCHANT_LIST.length}
          sublabel={atRisk > 0 ? `${atRisk} at risk` : "All healthy"}
          tone={atRisk > 0 ? "danger" : "success"}
        />
        <KpiCell
          label="Portfolio volume · 30d"
          value={fmtCompactUsd(MERCHANT_ALL.total_volume_usd)}
          sublabel={`${fmtNumber(MERCHANT_ALL.monthly_transactions)} txns`}
        />
        <KpiCell
          label="Blended VAMP"
          value={`${MERCHANT_ALL.vamp_bps} bps`}
          sublabel={MERCHANT_ALL.vamp_bps > 50 ? "Above standard" : "Below standard"}
          tone={MERCHANT_ALL.vamp_bps > 50 ? "danger" : "success"}
        />
        <KpiCell
          label="Blended chargeback rate"
          value={`${MERCHANT_ALL.chargeback_rate_pct}%`}
          sublabel={`Fraud-to-sales ${MERCHANT_ALL.fraud_to_sales_pct}%`}
        />
      </KpiStrip>
      <Row className="mb-3 g-3">
        <Col xl={4} lg={6} md={12}>
          <VampFeeExposureCard />
        </Col>
        <Col xl={4} lg={6} md={12}>
          <DaysToThresholdCard />
        </Col>
        <Col xl={4} lg={12} md={12}>
          <ActiveCurePlansCard />
        </Col>
      </Row>
      <Row className="mb-4 g-3">
        <Col xl={6} lg={12}>
          <ComplianceHeatmap />
        </Col>
        <Col xl={6} lg={12}>
          <PortfolioVampTrend />
        </Col>
      </Row>
      <Row className="mb-4 g-3">
        <Col xl={5} lg={12}>
          <MerchantRiskMatrix />
        </Col>
        <Col xl={7} lg={12}>
          <CrossMerchantAttackActivity />
        </Col>
      </Row>
      <Row className="mb-4 g-3">
        <Col xl={12} lg={12}>
          <CardFraudAgentPanel />
        </Col>
      </Row>
      <Row className="mb-4 g-3">
        <Col xl={12} lg={12}>
          <AgentInterventionsPanel />
        </Col>
      </Row>
      <VampChargebackContainmentPanel />
      <Row className="mb-4 g-3">
        <Col xl={12} lg={12}>
          <CrossMerchantBlockFeed />
        </Col>
      </Row>
      <Row className="mb-4 g-3">
        <Col xl={12} lg={12}>
          <AgentUserActionFeed />
        </Col>
      </Row>
    </>
  );
};

const MiniKpi = ({ label, value, accent, sublabel }) => (
  <div
    style={{
      background: "#FFFFFF",
      border: "1px solid rgba(0,0,0,0.06)",
      borderRadius: 8,
      padding: "16px 20px",
      display: "flex",
      flexDirection: "column",
      gap: 4,
      minHeight: 96,
    }}
  >
    <span style={{ fontSize: 12, fontWeight: 500, color: "#6B7280" }}>
      {label}
    </span>
    <span style={{ fontSize: 24, fontWeight: 600, color: "#0A0A0A", letterSpacing: "-0.02em", fontVariantNumeric: "tabular-nums" }}>
      {value}
    </span>
    {sublabel && <span style={{ fontSize: 12, color: "#6B7280", fontVariantNumeric: "tabular-nums" }}>{sublabel}</span>}
  </div>
);

export default function DepositOutcomeDashboardPro() {
  const [subscriberLabel, setSubscriberLabel] = useState("Loading...");
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [selectedWindow, setSelectedWindow] = useState("30d");
  // Sponsor bank operator picks which merchant slice to inspect. "all" is
  // the volume-weighted portfolio view; the individual IDs slice down to
  // a single merchant.
  const [selectedMerchant, setSelectedMerchant] = useState("all");
  const merchant = MERCHANT_BY_ID[selectedMerchant] ?? MERCHANT_ALL;
  // Helpers for drilldown table merchant tagging + filtering. Defined here
  // so they close over `selectedMerchant` without needing to be threaded
  // through PagedDemoTable's props.
  const merchantTagStyle = (mid) => {
    const m = MERCHANTS[mid] ?? MERCHANTS.epicsweep;
    return {
      padding: "2px 8px",
      borderRadius: 999,
      fontSize: 10,
      fontWeight: 800,
      letterSpacing: "0.06em",
      background: `${m.accent}22`,
      color: m.accent,
      display: "inline-block",
    };
  };
  const merchantAbbr = (mid) => (mid === "tigercash" ? "B" : "A");
  const filterByMerchant = (rows) =>
    selectedMerchant === "all"
      ? rows
      : rows.filter((r) => assignMerchant(r.email).id === selectedMerchant);
  const [whitelistEmailFilter, setWhitelistEmailFilter] = useState("");
  const [vampData, setVampData] = useState(null);
  const [riskSummaryData, setRiskSummaryData] = useState([]);
  const [threeDSData, setThreeDSData] = useState([]);
  const [binData, setBinData] = useState([]);
  const [ruleData, setRuleData] = useState([]);
  const [latencyData, setLatencyData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
/*
  useEffect(() => {
    resolveSubscriber().then(({ label }) => {
      setSubscriberLabel(label || "Unknown Subscriber");
    });
  }, []);*/

  const formatSubscriberLabel = (label) => {
    const raw = label || getRealm() || "Tenant";
    const key = raw.toLowerCase().replace(/[\s_-]/g, "");
  
    const displayNames = {
      epicsweep: "Epic Sweep",
      idenza: "Idenza",
    };
  
    return displayNames[key] || raw;
  };
  
  useEffect(() => {
    resolveSubscriber().then(({ label }) => {
      setSubscriberLabel(formatSubscriberLabel(label));
    });
  }, []);

  // === DEMO MODE: TOTALS scale by window; RATES get a small per-window
  // perturbation (1h slightly bursty, 30d averaged). Math integrity stays:
  // every drilldown table computes count = kpis.rate × kpis.total_transactions
  // inline, so the table row count always equals Hero × Total for the active
  // window — even when the rate moves.
  const loadKpis = useCallback(() => {
    setLoading(true);
    setTimeout(() => {
      const scale = WINDOW_SCALE[selectedWindow] ?? 1;
      const m = MERCHANT_BY_ID[selectedMerchant] ?? MERCHANT_ALL;
      const total = Math.round(m.monthly_transactions * scale);
      // 30d is the canonical baseline — all monthly-constant fixtures reconcile
      // against unperturbed merchant values. Shorter windows perturb upward
      // (bursty intervals catch more bad activity); 7d drifts slightly down.
      const heavyMult =
        selectedWindow === "1h" ? 1.15 :
        selectedWindow === "24h" ? 1.05 :
        selectedWindow === "7d" ? 0.97 :
        1.0;
      const authMult =
        selectedWindow === "1h" ? 0.992 :
        selectedWindow === "24h" ? 0.996 :
        selectedWindow === "7d" ? 1.001 :
        1.0;
      const passRate = +(m.threeds_pass_rate * authMult).toFixed(2);
      const failRate = +(100 - passRate).toFixed(2);
      const blockRate = +(m.block_rate * heavyMult).toFixed(2);
      const issuerDeclineRate = +(m.issuer_decline_rate * heavyMult).toFixed(2);
      const approvalRate = +(100 - blockRate - failRate - issuerDeclineRate).toFixed(2);
      setKpis({
        ...MOCK_HERO,
        window: selectedWindow,
        merchant_id: m.id,
        merchant_name: m.name,
        total_transactions: total,
        total_blocks: Math.round(total * (blockRate / 100)),
        total_volume_usd: Math.round(m.total_volume_usd * scale),
        block_rate: blockRate,
        issuer_decline_rate: issuerDeclineRate,
        threeds_pass_rate: passRate,
        threeds_fail_rate: failRate,
        approval_rate: approvalRate,
        // Compliance snapshot at merchant level
        vamp_bps: m.vamp_bps,
        ecp_bps: m.ecp_bps,
        efm_bps: m.efm_bps,
        chargeback_rate_pct: m.chargeback_rate_pct,
        fraud_to_sales_pct: m.fraud_to_sales_pct,
        health_score: m.health_score,
        health_label: m.health_label,
      });
      setLastUpdated(new Date());
      setLoading(false);
    }, 240);
  }, [selectedWindow, selectedMerchant]);

  /* ORIGINAL — live KPI fetch:
  const refreshMVs = useCallback(() => Promise.resolve(), []);
  const loadKpis = useCallback(async () => {
    setLoading(true);
    refreshMVs();
    try {
      const realm = getRealm();
      const res = await fetch(
        `${apiBase}/deposit-outcomes?window=${selectedWindow}`,
        { headers: { "X-Tenant-Realm": realm } }
      );
      if (res.ok) {
        const data = await res.json();
        setKpis(data);
      }
    } catch (err) {
      console.error("KPI fetch error:", err);
    } finally {
      setLastUpdated(new Date());
      setLoading(false);
    }
  }, [apiBase, selectedWindow, refreshMVs]);
  */

  useEffect(() => {
    loadKpis();
  }, [loadKpis]);

  // === DEMO MODE: mock analytics injection; live multi-endpoint fetch + processors commented below. ===
  // Risk-tier / 3DS / BIN / Rule counts all scale with the time window so the
  // analytics panels track the Hero KPI totals. VAMP & latency are global
  // (monthly trend / decision round-trip) and stay constant.
  const loadLiveAnalytics = useCallback(() => {
    setAnalyticsLoading(true);
    setTimeout(() => {
      const scale = WINDOW_SCALE[selectedWindow] ?? 1;
      // The donut center labels show the scaled sum; that sum must EQUAL the
      // Hero total_transactions for the same window. Rounding individual
      // buckets independently drifts ±1-2 — so we re-normalize and shove the
      // residual into the largest bucket. Risk Tier and 3DS Posture both
      // cover 100% of scored transactions, so they reconcile to total.
      const scaledTotal = Math.round(MOCK_HERO.total_transactions * scale);
      const normalizeValue = (rows) => {
        const scaled = rows.map((r) => ({
          ...r,
          value: Math.max(0, Math.round((r.value ?? 0) * scale)),
        }));
        const runningSum = scaled.reduce((s, r) => s + r.value, 0);
        if (runningSum > 0 && scaledTotal > 0 && runningSum !== scaledTotal) {
          const residual = scaledTotal - runningSum;
          const maxIdx = scaled.reduce(
            (mi, r, i) => (r.value > scaled[mi].value ? i : mi),
            0,
          );
          scaled[maxIdx] = { ...scaled[maxIdx], value: Math.max(0, scaled[maxIdx].value + residual) };
        }
        return scaled;
      };
      const scaleBins = (rows) =>
        rows.map((r) => ({ ...r, count: Math.max(0, Math.round((r.count ?? 0) * scale)) }));

      const vampLatest = MOCK_VAMP_TREND[MOCK_VAMP_TREND.length - 1];
      setVampData({
        month: vampLatest.month,
        denom_txn_count: vampLatest.transactions,
        refund_count: vampLatest.refunds,
        refund_rate: vampLatest.refundRate,
        chartData: MOCK_VAMP_TREND,
      });
      setRiskSummaryData(normalizeValue(MOCK_RISK_TIER));
      setThreeDSData(normalizeValue(MOCK_3DS_POSTURE));
      setBinData(scaleBins(MOCK_BIN_DISTRIBUTION));
      setRuleData(scaleBins(MOCK_RULE_FREQUENCY));
      setLatencyData(MOCK_LATENCY);
      setAnalyticsLoading(false);
    }, 240);
  }, [selectedWindow]);

  /* ORIGINAL — live multi-endpoint analytics fetch + processors:
  const formatMonthUTC = (d) =>
    new Date(d).toLocaleString("en-US", { month: "short", year: "numeric", timeZone: "UTC" });

  const processVampData = (apiResponse) => {
    const rows = apiResponse?.data?.data;
    if (!Array.isArray(rows) || rows.length === 0) return null;
    const latest = rows[0];
    const monthlySeries = rows.slice().reverse().map((row) => ({
      month: formatMonthUTC(row[1]),
      transactions: Number(row[2] || 0),
      refunds: row[3] == null ? null : Number(row[3] || 0),
      refundRate: row[4] == null ? null : Number(row[4] || 0) * 100,
    }));
    return {
      month: formatMonthUTC(latest[1]),
      denom_txn_count: latest[2] || 0,
      refund_count: latest[3] || 0,
      refund_rate: Number(latest[4] || 0) * 100,
      chartData: monthlySeries,
    };
  };

  const processRiskSummary = (response) => {
    if (!response?.data || !Array.isArray(response.data)) return [];
    return response.data
      .filter((r) => (r.count ?? 0) > 0)
      .map((r) => {
        const raw = (r.label || "").toString().toUpperCase();
        const name = raw === "HIGH" || raw === "HIGH_RISK" ? "HIGH"
          : raw === "MEDIUM" || raw === "MED" || raw === "MEDIUM_RISK" ? "MEDIUM"
          : raw === "LOW" || raw === "LOW_RISK" ? "LOW" : "UNKNOWN";
        return { name, value: Number(r.count || 0), percent: r.percent != null ? Number(r.percent) : 0 };
      });
  };

  const process3dsSummary = (response) => {
    if (!response?.data || !Array.isArray(response.data)) return [];
    const buckets = {
      "Force 3DS": { value: 0, percent: 0 },
      "Prefer 3DS": { value: 0, percent: 0 },
      "Bypass 3DS": { value: 0, percent: 0 },
      BLACKLISTED: { value: 0, percent: 0 },
      BLOCKED: { value: 0, percent: 0 },
      "Other 3DS": { value: 0, percent: 0 },
    };
    response.data.forEach(({ level, count, percent }) => {
      const raw = (level || "").toString().toUpperCase();
      const value = Number(count) || 0;
      const pct = Number(percent) || 0;
      let key;
      if (raw === "FORCE" || raw === "FORCE_ON") key = "Force 3DS";
      else if (raw === "ATTEMPT" || raw === "PREFER_ON") key = "Prefer 3DS";
      else if (raw === "BYPASS" || raw === "BYPASS_ON") key = "Bypass 3DS";
      else if (raw === "BLOCKED" || raw === "BLOCKED_ON") key = "BLOCKED";
      else if (raw === "BLACKLISTED" || raw === "BLACKLISTED_ON") key = "BLACKLISTED";
      else key = "Other 3DS";
      buckets[key].value += value;
      buckets[key].percent += pct;
    });
    return Object.entries(buckets)
      .filter(([, v]) => v.value > 0)
      .map(([name, v]) => ({ name, value: v.value, percent: Number(v.percent.toFixed(2)) }));
  };

  const processBinDistribution = (raw) =>
    raw?.data ? raw.data.map((row) => ({ bin: row.bin, count: Number(row.count || 0) })) : [];
  const processRuleFrequency = (raw) =>
    raw?.data ? raw.data.map((row) => ({ rule: row.rule, count: Number(row.count || 0) })) : [];
  const processLatencyData = (data) => {
    if (!data?.data?.data) return null;
    const rows = data.data.data;
    const latenciesMinutes = rows.map((row) => parseFloat(row[4] || 0));
    if (latenciesMinutes.length === 0) return null;
    const stats = {
      average: latenciesMinutes.reduce((a, b) => a + b, 0) / latenciesMinutes.length,
      maximum: Math.max(...latenciesMinutes),
      minimum: Math.min(...latenciesMinutes),
    };
    const chartData = rows.slice(0, 15).map((row, index) => ({
      index,
      order_key: (row[1] || "").slice(-8),
      valueMs: parseFloat(row[4] || 0) * 60_000,
    }));
    return { stats, chartData };
  };

  const loadLiveAnalytics = useCallback(async () => {
    setAnalyticsLoading(true);
    const realm = getRealm();
    const fetchJSON = async (endpoint) => {
      try {
        const res = await fetch(`${apiBase}/${endpoint}?exclude_dev=${EXCLUDE_DEV}`, {
          headers: { "X-Tenant-Realm": realm },
        });
        return await res.json();
      } catch (err) {
        console.error(`Live analytics fetch failed: ${endpoint}`, err);
        return null;
      }
    };
    const [vamp, riskSummary, threeDS, binDist, ruleFreq, latency] = await Promise.all([
      fetchJSON("vamp"),
      fetchJSON("case-risk-summary"),
      fetchJSON("3ds-summary"),
      fetchJSON("bin-distribution"),
      fetchJSON("rule-frequency"),
      fetchJSON("latency"),
    ]);
    setVampData(processVampData(vamp));
    setRiskSummaryData(processRiskSummary(riskSummary));
    setThreeDSData(process3dsSummary(threeDS));
    setBinData(processBinDistribution(binDist));
    setRuleData(processRuleFrequency(ruleFreq));
    setLatencyData(processLatencyData(latency));
    setAnalyticsLoading(false);
  }, [apiBase]);
  */

  useEffect(() => {
    loadLiveAnalytics();
  }, [loadLiveAnalytics]);

  return (
    <div style={shellStyle}>
      <Container fluid style={{ maxWidth: "100%", paddingLeft: 0, paddingRight: 0 }}>
        <header style={headerStyle}>
        <div style={{ ...contentColStyle, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: space[3], minWidth: 0 }}>
            <span
              style={{
                fontSize: 18,
                fontWeight: weight.semibold,
                letterSpacing: "-0.01em",
                color: color.text.primary,
                whiteSpace: "nowrap",
              }}
            >
              Portfolio
            </span>
            <span
              style={{
                fontSize: text.base,
                color: color.text.tertiary,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {merchant.id === "all" ? "All merchants" : `${merchant.name} · ${merchant.vertical}`}
              <span style={{ margin: `0 ${space[2]}px`, color: color.border.strong }}>·</span>
              Production
            </span>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: space[2] }}>
            <Segmented
              size="sm"
              value={selectedMerchant}
              onChange={setSelectedMerchant}
              options={MERCHANT_OPTIONS.map((m) => ({
                value: m.id,
                label: m.id === "all" ? "All" : m.name,
              }))}
            />
            <Segmented
              size="sm"
              value={selectedWindow}
              onChange={setSelectedWindow}
              options={["1h", "24h", "7d", "30d"].map((w) => ({ value: w, label: w }))}
            />
            <button
              type="button"
              onClick={() => { loadKpis(); loadLiveAnalytics(); }}
              title={lastUpdated ? `Updated ${lastUpdated.toLocaleTimeString()}` : "Refresh"}
              aria-label="Refresh"
              style={headerIconBtnStyle}
            >
              <RefreshIcon size={13} strokeWidth={1.75} />
            </button>
          </div>
        </div>
        </header>

        <div style={bodyColStyle}>

        {/* Page heading — subtle cobalt-tinted banner. Same title text at
            same size (24px). Softly branded via background gradient +
            hairline cobalt border. Small Live indicator on the right. */}
        <div
          style={{
            marginBottom: 28,
            padding: "16px 20px",
            borderRadius: 10,
            border: `1px solid ${color.brand.border}`,
            background: `
              radial-gradient(600px 200px at 100% -20%, rgba(79,139,224,0.14), transparent 60%),
              linear-gradient(135deg, rgba(11,87,164,0.06) 0%, rgba(11,87,164,0.02) 60%, ${color.bg.surface} 100%)
            `,
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 16,
            flexWrap: "wrap",
            boxShadow: "0 1px 2px rgba(10, 10, 10, 0.03)",
            textAlign: "left",
          }}
        >
          <div style={{ minWidth: 0 }}>
            <h1
              style={{
                margin: 0,
                fontSize: 24,
                fontWeight: weight.semibold,
                color: color.text.primary,
                letterSpacing: "-0.02em",
                lineHeight: 1.2,
              }}
            >
              Sponsor Bank Portfolio — Merchant Health Command Center
            </h1>
            <div
              style={{
                marginTop: 6,
                fontSize: 13,
                color: color.text.muted,
                lineHeight: 1.5,
              }}
            >
              Portfolio VAMP posture, merchant-level compliance, and per-merchant health scoring across your sponsored program
            </div>
            <div
              style={{
                marginTop: 6,
                fontSize: 12,
                color: color.text.muted,
                display: "flex",
                alignItems: "center",
                gap: 8,
                flexWrap: "wrap",
              }}
            >
              <span>
                {selectedMerchant === "all"
                  ? `All merchants · ${MERCHANT_LIST.length} sponsored`
                  : `${merchant.name} · ${merchant.vertical}`}
              </span>
              <span style={{ color: color.border.strong }}>·</span>
              <span>Window {selectedWindow}</span>
              {lastUpdated && (
                <>
                  <span style={{ color: color.border.strong }}>·</span>
                  <span style={numeric.tabular}>Updated {lastUpdated.toLocaleTimeString()}</span>
                </>
              )}
            </div>
          </div>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "4px 10px",
              borderRadius: 999,
              background: "rgba(15,122,61,0.10)",
              border: "1px solid rgba(15,122,61,0.20)",
              fontSize: 11,
              fontWeight: 600,
              color: "#0F7A3D",
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              flexShrink: 0,
            }}
          >
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: 999,
                background: "#0F7A3D",
                boxShadow: "0 0 0 3px rgba(15,122,61,0.20)",
                animation: "idz-pulse 1.6s ease-in-out infinite",
              }}
            />
            Live
          </span>
        </div>

        {loading && !kpis ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
          </div>
        ) : kpis ? (
          <>
            <SectionBanner
              eyebrow="Portfolio Health"
              title="Sponsor merchant book — VAMP posture and health at a glance"
              hint="Volume-weighted portfolio ratio + per-merchant breakdown. VAMP standard = 50 bps, excessive = 150 bps."
            />
            <PortfolioHealthPanel selectedMerchant={selectedMerchant} onPickMerchant={setSelectedMerchant} />

            {selectedMerchant === "all" && (
              <PortfolioOverviewSection />
            )}

            {selectedMerchant !== "all" && (
            <>
            {selectedMerchant === "tigercash" && <VampChargebackContainmentPanel />}
            <SectionBanner
              eyebrow="Performance Snapshot"
              title="Top-line health"
              hint={`${merchant.name} · Window ${kpis.window} · last refreshed just now`}
            />
            {/* ORIGINAL — simpler KPI grid using <KpiCard> (still defined above).
                Restore by replacing the HeroKpi grid below with this block.
            <div style={kpiGridStyle}>
              <KpiCard
                label="Total Transactions"
                value={Number(kpis.total_transactions || 0).toLocaleString()}
                sublabel={`Window: ${kpis.window}`}
                icon="📊"
                accent="#3B82F6"
              />
              <KpiCard
                label="Total Blocks"
                value={Number(kpis.total_blocks || 0).toLocaleString()}
                sublabel={`Window: ${kpis.window}`}
                icon="🚫"
                accent="#EF4444"
              />
              <KpiCard
                label="Block Rate"
                value={`${kpis.block_rate}%`}
                sublabel="Blocked transactions"
                icon="🛑"
                accent="#DC2626"
              />
              <KpiCard
                label="Issuer Decline Rate"
                value={`${kpis.issuer_decline_rate}%`}
                sublabel="Non-blocked transactions declined by issuer"
                icon="🏦"
                accent="#F59E0B"
              />
              <KpiCard
                label="3DS Pass Rate"
                value={`${kpis.threeds_pass_rate}%`}
                sublabel={`Fail: ${kpis.threeds_fail_rate}%`}
                icon="🔐"
                accent="#10B981"
              />
            </div>
            */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: 16,
                marginBottom: 24,
              }}
            >
              <HeroKpi
                label="Total Transactions"
                value={fmtNumber(kpis.total_transactions)}
                sublabel={`Window: ${kpis.window}`}
                delta={kpis.deltas?.total_transactions}
                accent="#3B82F6"
              />
              {/* Approval Rate KPI removed per design — completion %
                  (= 100 − block − 3DS-fail − issuer-decline) is still
                  available as MOCK_HERO.approval_rate if it needs to come
                  back. Restore by uncommenting the block below.
              <HeroKpi
                label="Approval Rate"
                value={`${kpis.approval_rate}%`}
                sublabel="All networks, gross approvals"
                delta={kpis.deltas?.approval_rate}
                accent="#10B981"
              />
              */}
              <HeroKpi
                label="Block Rate"
                value={`${kpis.block_rate}%`}
                sublabel={`Window: ${kpis.window}`}
                delta={kpis.deltas?.block_rate}
                deltaInvertGood
                accent="#DC2626"
              />
              <HeroKpi
                label="Issuer Decline Rate"
                value={`${kpis.issuer_decline_rate}%`}
                sublabel={`Window: ${kpis.window}`}
                delta={kpis.deltas?.issuer_decline_rate}
                deltaInvertGood
                accent="#F59E0B"
              />
              <HeroKpi
                label="Authorization Rate"
                value={`${kpis.threeds_pass_rate}%`}
                sublabel={`Window: ${kpis.window}`}
                delta={kpis.deltas?.threeds_pass_rate}
                accent="#8B5CF6"
              />
            </div>

            {/* Deposit Funnel + Recoverable Revenue row removed — the same
                signal (Scored → Risk-cleared → 3DS-cleared → Completed) is
                already covered by the Hero KPIs (totals, block & 3DS rates),
                the Decision Distribution donut, and the Outcome Rates Over
                Time line chart. Recoverable Revenue still lives inline on
                the Recovery Scorecard via the Recovered Revenue card.
            <Row className="mb-4 g-4">
              <Col xl={7} lg={12}>
                <div style={{ background: "#FFFFFF", border: "1px solid #E5E7EB", borderRadius: 20, padding: "20px 22px", boxShadow: "0 14px 34px rgba(15,23,42,0.07)", height: "100%" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 900, color: "#64748B", letterSpacing: "0.10em" }}>DEPOSIT FUNNEL</div>
                      <h5 style={{ margin: 0, fontWeight: 900, color: "#0F172A", letterSpacing: "-0.02em" }}>Scoring → 3DS → Deposit completion</h5>
                    </div>
                    <span style={{ fontSize: 11, color: "#64748B", fontWeight: 700 }}>
                      End-to-end conversion: {((MOCK_AUTH_FUNNEL[MOCK_AUTH_FUNNEL.length - 1].count / MOCK_AUTH_FUNNEL[0].count) * 100).toFixed(1)}%
                    </span>
                  </div>
                  <FunnelBar stages={MOCK_AUTH_FUNNEL} />
                </div>
              </Col>
              <Col xl={5} lg={12}>
                <div style={{ background: "linear-gradient(135deg, #0F172A 0%, #1E3A8A 100%)", color: "#FFF", borderRadius: 20, padding: "22px 22px", boxShadow: "0 18px 40px rgba(15,23,42,0.25)", height: "100%", display: "flex", flexDirection: "column", gap: 18 }}>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 900, color: "#A5B4FC", letterSpacing: "0.10em" }}>RECOVERABLE REVENUE</div>
                    <div style={{ fontSize: 36, fontWeight: 900, letterSpacing: "-0.04em", marginTop: 2 }}>{fmtCompactUsd(MOCK_RECOVERY.recoverable_revenue_usd)}</div>
                    <div style={{ fontSize: 12, color: "#CBD5E1", marginTop: 2 }}>Estimated win-back from soft-decline retries (last 30d)</div>
                  </div>
                  ... full block in git history; restore by uncommenting this
                </div>
              </Col>
            </Row>
            */}

            {/* Dispute / chargeback KPIs moved out of the Performance Snapshot
                row into a dedicated "Disputes & Chargebacks" section further
                down so the same numbers don't surface in two places.
            <Row className="mb-4 g-4">
              <Col xl={3} lg={6} md={6}>
                <RecoveryCard icon={<FaCheckCircle />} accent="#047857" label="Dispute Win Rate" value={`${MOCK_RECOVERY.dispute_win_rate}%`} sublabel="With CE 3.0 evidence" />
              </Col>
              <Col xl={3} lg={6} md={6}>
                <RecoveryCard icon={<FaDollarSign />} accent="#1D4ED8" label="Recovered Revenue" value={fmtCompactUsd(MOCK_RECOVERY.recovered_usd)} sublabel="30d disputes reversed" />
              </Col>
              <Col xl={3} lg={6} md={6}>
                <RecoveryCard icon={<FaExclamationTriangle />} accent="#F59E0B" label="False Decline Rate" value={`${MOCK_RECOVERY.false_decline_rate}%`} sublabel="13× cost of true fraud" />
              </Col>
              <Col xl={3} lg={6} md={6}>
                <RecoveryCard icon={<FaShieldAlt />} accent="#DC2626" label="Fraud-to-Sales" value={`${MOCK_RECOVERY.fraud_to_sales_bps} bps`} sublabel={`CB rate ${MOCK_RECOVERY.chargeback_rate_bps} bps`} />
              </Col>
            </Row>
            */}

            {/* ORIGINAL — Outcome Quartet (Completed / Abandoned / Declined / Blocked).
                Removed: Completed ≈ Hero "Approval Rate"; Blocked = Hero "Block Rate";
                Declined ≈ Hero "Issuer Decline Rate" — net duplicate. */}
            {/*
            <section style={{ marginBottom: 24 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 900, color: "#64748B", letterSpacing: "0.10em" }}>DEPOSIT OUTCOMES</div>
                  <h5 style={{ margin: 0, fontWeight: 900, color: "#0F172A", letterSpacing: "-0.02em" }}>Lifecycle outcome rates</h5>
                </div>
                <span style={{ fontSize: 11, color: "#64748B", fontWeight: 700 }}>Thresholds tuned for igaming deposit funnels</span>
              </div>
              <Row className="g-3">
                {[
                  { label: "Completed", value: 89.3, good: 80, warn: 70, tone: "good", icon: <FaCheckCircle /> },
                  { label: "Abandoned", value: 6.4, good: 10, warn: 20, tone: "invert", icon: <FaUndo /> },
                  { label: "Declined", value: 4.3, good: 6, warn: 12, tone: "invert", icon: <FaTimesCircle /> },
                  { label: "Blocked", value: 1.84, good: 3, warn: 6, tone: "invert", icon: <FaBan /> },
                ].map((q) => { ... see git history for the card render })}
              </Row>
            </section>
            */}

            <SectionBanner
              eyebrow="Conversion & Lifecycle"
              title="Decision flow & outcome trend"
              hint="Where transactions land and how that shifts over time"
            />

            <Row className="mb-4 g-4">
              <Col xl={5} lg={12}>
                <ChartCard title="Total Transactions">
                  {(() => {
                    // Window-aware synthetic series anchored to current UTC.
                    // After generation we re-scale every bucket so the chart's
                    // sum equals kpis.total_transactions EXACTLY — no rounding
                    // drift between the Hero KPI and the chart header total.
                    const now = new Date();
                    const series = (() => {
                      if (selectedWindow === "1h") {
                        // 12 five-minute buckets, avg ~3.5/bucket → 12×3.5 ≈ 42
                        const end = startOfUtcMinuteBucket(now, 5);
                        return Array.from({ length: 12 }, (_, i) => {
                          const d = new Date(end.getTime() - (11 - i) * 5 * 60 * 1000);
                          return {
                            t: fmtUtcHourMin(d),
                            total: Math.max(0, 3 + Math.round(Math.sin(i / 2) + Math.random() * 2)),
                          };
                        });
                      }
                      if (selectedWindow === "24h") {
                        // 24 hourly buckets, avg ~41/hour → 24×41 ≈ 995
                        const end = startOfUtcHour(now);
                        return Array.from({ length: 24 }, (_, i) => {
                          const d = new Date(end.getTime() - (23 - i) * 60 * 60 * 1000);
                          return {
                            t: fmtUtcHourMin(d),
                            total: Math.max(0, 39 + Math.round(Math.sin((i - 6) / 3.2) * 14 + Math.random() * 9)),
                          };
                        });
                      }
                      if (selectedWindow === "7d") {
                        // 7 day buckets, avg ~995/day → 7×995 ≈ 6,964
                        const end = startOfUtcDay(now);
                        return Array.from({ length: 7 }, (_, i) => {
                          const d = new Date(end.getTime() - (6 - i) * 86400000);
                          return {
                            t: d.toLocaleDateString("en-US", { weekday: "short", timeZone: "UTC" }),
                            total: 955 + Math.round(Math.sin(i / 1.4) * 135 + Math.random() * 75),
                          };
                        });
                      }
                      // 30d default — 30 day buckets, ~995/day avg → 29,847
                      const end = startOfUtcDay(now);
                      return Array.from({ length: 30 }, (_, i) => {
                        const d = new Date(end.getTime() - (29 - i) * 86400000);
                        const total = 995 + Math.round(Math.sin(i / 2.3) * 195 + Math.random() * 125);
                        return { t: fmtUtcDate(d), total };
                      });
                    })();
                    // Normalise so the chart sums to the Hero KPI exactly.
                    const target = kpis?.total_transactions ?? 0;
                    const rawSum = series.reduce((s, d) => s + d.total, 0);
                    if (rawSum > 0 && target > 0) {
                      const scaleFactor = target / rawSum;
                      let runningSum = 0;
                      for (let i = 0; i < series.length; i++) {
                        const adj = Math.max(0, Math.round(series[i].total * scaleFactor));
                        series[i].total = adj;
                        runningSum += adj;
                      }
                      // Push any rounding residual into the latest bucket so the
                      // chart header total equals Hero exactly.
                      const residual = target - runningSum;
                      series[series.length - 1].total = Math.max(0, series[series.length - 1].total + residual);
                    }
                    const peak = Math.max(...series.map((d) => d.total));
                    const sum = series.reduce((s, d) => s + d.total, 0);
                    const xInterval = series.length > 24 ? 3 : series.length > 12 ? 2 : 1;
                    return (
                      <>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, fontSize: 12, color: "#64748B" }}>
                          <span><strong style={{ color: "#0F172A" }}>{fmtNumber(sum)}</strong> in last {selectedWindow}</span>
                          <span>Peak <strong style={{ color: "#0F172A" }}>{fmtNumber(peak)}</strong></span>
                        </div>
                        <ResponsiveContainer width="100%" height={232}>
                          <LineChart data={series} margin={{ top: 6, right: 12, left: 0, bottom: 0 }}>
                            <defs>
                              <linearGradient id="txnGradient" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.35} />
                                <stop offset="100%" stopColor="#3B82F6" stopOpacity={0} />
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="t" fontSize={11} interval={xInterval} />
                            <YAxis fontSize={11} />
                            <Tooltip formatter={(v) => [fmtNumber(v), "Transactions"]} />
                            <Line type="monotone" dataKey="total" stroke="#1D4ED8" strokeWidth={2.4} dot={false} fill="url(#txnGradient)" />
                          </LineChart>
                        </ResponsiveContainer>
                      </>
                    );
                  })()}
                </ChartCard>
              </Col>
              <Col xl={7} lg={12}>
                <ChartCard title="Outcome Rates Over Time">
                  <ResponsiveContainer width="100%" height={260}>
                    <LineChart
                      data={(() => {
                        // Number of buckets follows the window: 12 / 24 / 7 / 30.
                        // Averages anchor to the Hero KPI cascade:
                        //   completed ≈ 83.6 (Hero Approval/Completion %)
                        //   declined  ≈ 9.8  (Hero Issuer Decline %)
                        //   3DS-fail  ≈ 4.4  (Hero Authorization Fail of total)
                        //   blocked   ≈ 2.25 (Hero Block %)
                        // Sum ≈ 100% bucket-by-bucket.
                        const now = new Date();
                        const cfg =
                          selectedWindow === "1h" ? { n: 12, stepMs: 5 * 60_000, anchor: startOfUtcMinuteBucket(now, 5), label: (d) => fmtUtcHourMin(d) } :
                          selectedWindow === "24h" ? { n: 24, stepMs: 60 * 60_000, anchor: startOfUtcHour(now), label: (d) => fmtUtcHourMin(d) } :
                          selectedWindow === "7d" ? { n: 7, stepMs: 86400000, anchor: startOfUtcDay(now), label: (d) => d.toLocaleDateString("en-US", { weekday: "short", timeZone: "UTC" }) } :
                          { n: 30, stepMs: 86400000, anchor: startOfUtcDay(now), label: (d) => fmtUtcDate(d) };
                        return Array.from({ length: cfg.n }, (_, i) => {
                          const d = new Date(cfg.anchor.getTime() - (cfg.n - 1 - i) * cfg.stepMs);
                          const completed = 83.55 + Math.sin(i / 1.7) * 1.4 + (Math.random() - 0.5) * 0.8;
                          const declined = 9.8 + Math.cos(i / 1.3) * 0.7 + (Math.random() - 0.5) * 0.5;
                          const threeds = 4.4 + Math.sin(i / 2.2) * 0.4 + (Math.random() - 0.5) * 0.4;
                          const blocked = Math.max(0.5, 2.25 + Math.sin(i / 1.9) * 0.3 + (Math.random() - 0.5) * 0.3);
                          return { day: cfg.label(d), completed, declined, threeds, blocked };
                        });
                      })()}
                      margin={{ top: 6, right: 16, left: 0, bottom: 0 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="day" fontSize={11} />
                      <YAxis tickFormatter={(v) => `${v}%`} fontSize={11} />
                      <Tooltip formatter={(v) => [`${Number(v).toFixed(2)}%`]} />
                      <Line type="monotone" dataKey="completed" stroke="#10B981" strokeWidth={2.4} dot={false} name="Completed" />
                      <Line type="monotone" dataKey="declined" stroke="#DC2626" strokeWidth={2.4} dot={false} name="Issuer Declined" />
                      <Line type="monotone" dataKey="threeds" stroke="#F59E0B" strokeWidth={2.4} dot={false} name="3DS Failed" />
                      <Line type="monotone" dataKey="blocked" stroke="#7C3AED" strokeWidth={2.4} dot={false} name="Blocked" />
                    </LineChart>
                  </ResponsiveContainer>
                  <div style={{ display: "flex", gap: 14, justifyContent: "center", marginTop: 8, fontSize: 11, color: "#475569" }}>
                    {[
                      { label: "Completed", color: "#10B981" },
                      { label: "Abandoned", color: "#F59E0B" },
                      { label: "Declined", color: "#DC2626" },
                      { label: "Blocked", color: "#7C3AED" },
                    ].map((l) => (
                      <span key={l.label} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                        <span style={{ width: 8, height: 8, borderRadius: 2, background: l.color }} />
                        {l.label}
                      </span>
                    ))}
                  </div>
                </ChartCard>
              </Col>
            </Row>

            <Row className="mb-4 g-4">
              <Col xl={6} lg={12}>
                <RateChart
                  title="Issuer Decline Rate"
                  metric="idenza_deposit_issuer_decline_rate"
                  selectedWindow={selectedWindow}
                  color="#F59E0B"
                />
              </Col>
              <Col xl={6} lg={12}>
                <RateChart
                  title="Block Rate"
                  metric="idenza_deposit_block_rate"
                  selectedWindow={selectedWindow}
                  color="#EF4444"
                />
              </Col>
            </Row>

            <Row className="mb-4 g-4">
              <Col xl={6} lg={12}>
                <RateChart
                  title="Authorization Rate"
                  metric="idenza_deposit_forced3ds_authenticated_rate"
                  selectedWindow={selectedWindow}
                  color="#10B981"
                />
              </Col>
              <Col xl={6} lg={12}>
                <RateChart
                  title="Authorization Fail Rate"
                  metric="idenza_deposit_forced3ds_authenticated_rate"
                  selectedWindow={selectedWindow}
                  color="#DC2626"
                  invert
                />
              </Col>
            </Row>
            </>
            )}
          </>
        ) : (
          <div className="text-center py-5">
            <span style={{ fontSize: 14, color: "#6B7280" }}>
              No data available for this subscriber.
            </span>
          </div>
        )}

        {selectedMerchant !== "all" && (
        <>
        <SectionBanner
          eyebrow="Disputes & Chargebacks"
          title="Recovery posture & fraud-loss ratios"
          hint="Monthly rollup"
        />

        <Row className="g-3 mb-4">
          <Col xl={4} lg={6} md={6}>
            <RecoveryCard
              icon={<FaCheckCircle />}
              accent="#047857"
              label="Dispute Win Rate"
              value={`${MOCK_RECOVERY.dispute_win_rate}%`}
              sublabel="Won vs. represented"
            />
          </Col>
          <Col xl={4} lg={6} md={6}>
            <RecoveryCard
              icon={<FaDollarSign />}
              accent="#1D4ED8"
              label="Recovered Revenue"
              value={fmtCompactUsd(MOCK_RECOVERY.recovered_usd)}
              sublabel="Disputes reversed this month"
            />
          </Col>
          <Col xl={4} lg={6} md={6}>
            <RecoveryCard
              icon={<FaExclamationTriangle />}
              accent="#DC2626"
              label="Chargeback Rate"
              value={`${MOCK_RECOVERY.chargeback_rate_pct}%`}
              sublabel={`Fraud-to-sales ${MOCK_RECOVERY.fraud_to_sales_pct}%`}
            />
          </Col>
        </Row>

        <SectionBanner
          eyebrow="Scheme Compliance"
          title="Visa & Mastercard monitoring posture"
          hint="Monthly rollup • below standard / above standard / excessive thresholds"
        />

        <section style={{ marginBottom: 24 }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
              gap: 16,
            }}
          >
            {MOCK_SCHEME_COMPLIANCE.map((row) => (
              <ComplianceCard key={row.program} row={row} />
            ))}
          </div>
        </section>

        <Row className="mb-4 g-4">
          <Col xl={12} lg={12}>
            <ChartShell
              eyebrow="VAMP"
              title="Monthly refund-rate trend"
              hint="Combined: transaction volume (bars) + refund rate % (line)"
            >
              <VampPanel data={vampData} />
            </ChartShell>
          </Col>
        </Row>

        <SectionBanner
          eyebrow="Authorization Quality"
          title="Issuer outcomes & match rates"
          hint="Approval mix, decline reasons, AVS & CVV"
        />

        {/* Authorization Health KPI row removed — latency (p50/p95) and the
            soft/hard decline classification (which is a network retry-advice
            framing) were called out. Approval / Decline / AVS / CVV signal
            already lives in the rows below.
        <section style={{ marginBottom: 24 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 16 }}>
            <MiniKpi label="Soft Decline Rate" value={`${MOCK_AUTH_HEALTH.softDeclineRate}%`} accent="#F59E0B" sublabel="Retryable (do_not_honor, etc.)" />
            <MiniKpi label="Hard Decline Rate" value={`${MOCK_AUTH_HEALTH.hardDeclineRate}%`} accent="#DC2626" sublabel="Non-retryable (lost/stolen/invalid)" />
            <MiniKpi label="Auth Latency p50" value={`${MOCK_AUTH_HEALTH.avgAuthLatencyMs} ms`} accent="#1D4ED8" sublabel="Issuer round-trip, median" />
            <MiniKpi label="Auth Latency p95" value={`${Math.round(MOCK_AUTH_HEALTH.avgAuthLatencyMs * 1.84)} ms`} accent="#7C3AED" sublabel="Issuer round-trip, 95th" />
          </div>
        </section>
        */}

        <Row className="mb-4 g-4">
          <Col xl={6} lg={12}>
            <ChartCard title="Approval Rate by Network">
              {(() => {
                const scale = WINDOW_SCALE[selectedWindow] ?? 1;
                const heroTotal = kpis?.total_transactions ?? Math.round(MOCK_HERO.total_transactions * scale);
                // Network approval is the issuer-level approval seen on
                // submitted auths — its volume-weighted average must equal
                // 100 − Hero.issuer_decline_rate at every window. Derive
                // Visa/MC approvals from the Hero KPI so the relationship
                // holds even after per-window perturbation.
                const issuerDeclineRate = kpis?.issuer_decline_rate ?? MOCK_HERO.issuer_decline_rate;
                const targetApproval = +(100 - issuerDeclineRate).toFixed(2);
                // Scale volumes then normalise residual so sum = Hero exactly.
                const scaledVols = MOCK_NETWORK_APPROVAL.map((d) => ({
                  ...d,
                  volume: Math.max(1, Math.round(d.volume * scale)),
                }));
                let runningVol = scaledVols.reduce((s, d) => s + d.volume, 0);
                if (runningVol !== heroTotal) {
                  const residual = heroTotal - runningVol;
                  const idx = scaledVols.reduce((mi, d, i) => (d.volume > scaledVols[mi].volume ? i : mi), 0);
                  scaledVols[idx] = { ...scaledVols[idx], volume: Math.max(1, scaledVols[idx].volume + residual) };
                  runningVol = scaledVols.reduce((s, d) => s + d.volume, 0);
                }
                // Solve for Visa/MC approvals such that:
                //   weighted = (Vol_v × Visa + Vol_m × MC) / runningVol = target
                //   Visa - MC = 0.2  (Visa runs ~0.2pp above MC in sub-26)
                const visaVol = scaledVols[0].volume;
                const mcApproval = +(targetApproval - 0.2 * (visaVol / runningVol)).toFixed(2);
                const visaApproval = +(mcApproval + 0.2).toFixed(2);
                const scaled = [
                  { ...scaledVols[0], approval: visaApproval },
                  { ...scaledVols[1], approval: mcApproval },
                ];
                const weightedApproval = runningVol > 0
                  ? scaled.reduce((s, d) => s + d.approval * d.volume, 0) / runningVol
                  : 0;
                return (
                  <>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, fontSize: 12, color: "#64748B" }}>
                      <span>Issuer approval, post-3DS · <strong style={{ color: "#0F172A" }}>{fmtNumber(runningVol)}</strong> submissions ({kpis?.window ?? selectedWindow})</span>
                      <span>Weighted <strong style={{ color: "#0F172A" }}>{weightedApproval.toFixed(1)}%</strong></span>
                    </div>
                    <ResponsiveContainer width="100%" height={228}>
                      <BarChart data={scaled} margin={{ top: 8, right: 20, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="network" fontSize={12} />
                        <YAxis domain={[60, 100]} tickFormatter={(v) => `${v}%`} fontSize={11} />
                        <Tooltip
                          formatter={(v, n) => (n === "approval" ? [`${v}%`, "Approval"] : [v, n])}
                          labelFormatter={(label, payload) => {
                            const row = payload && payload[0] && payload[0].payload;
                            return row ? `${label} · ${fmtNumber(row.volume)} txns` : label;
                          }}
                        />
                        <Bar dataKey="approval" radius={[8, 8, 0, 0]}>
                          {scaled.map((d, i) => (
                            <Cell key={i} fill={["#1D4ED8", "#F97316", "#0EA5E9", "#A855F7"][i % 4]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </>
                );
              })()}
            </ChartCard>
          </Col>
          <Col xl={6} lg={12}>
            <ChartCard title="Top Decline Reasons">
              {(() => {
                const scale = WINDOW_SCALE[selectedWindow] ?? 1;
                // Same per-window severity multiplier used for Hero block /
                // issuer-decline rates so decline mix moves directionally with
                // the rest of the dashboard. 30d is canonical baseline (1.0).
                const heavyMult =
                  selectedWindow === "1h" ? 1.15 :
                  selectedWindow === "24h" ? 1.05 :
                  selectedWindow === "7d" ? 0.97 :
                  1.0;
                const scaledReasons = MOCK_DECLINE_REASONS.map((r) => ({
                  ...r,
                  count: Math.max(0, Math.round(r.count * scale * heavyMult)),
                  share: +(r.share * heavyMult).toFixed(2),
                }));
                const heroTotal = kpis?.total_transactions ?? Math.round(MOCK_HERO.total_transactions * scale);
                const sumCount = scaledReasons.reduce((s, r) => s + r.count, 0);
                const maxShare = Math.max(...scaledReasons.map((r) => r.share));
                return (
                  <>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, fontSize: 12, color: "#64748B" }}>
                      <span>Top-10 declines · <strong style={{ color: "#0F172A" }}>{fmtNumber(sumCount)}</strong> of {fmtNumber(heroTotal)} ({kpis?.window ?? selectedWindow})</span>
                      <span>Coverage <strong style={{ color: "#0F172A" }}>{heroTotal > 0 ? ((sumCount / heroTotal) * 100).toFixed(2) : "0.00"}%</strong></span>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 12, maxHeight: 280, overflowY: "auto", paddingRight: 6 }}>
                      {scaledReasons.map((r) => {
                        const widthPct = (r.share / maxShare) * 100;
                        return (
                          <div key={`${r.code}-${r.label}`} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 13 }}>
                              <span style={{ display: "inline-flex", alignItems: "center", gap: 8, color: "#0F172A", fontWeight: 700 }}>
                                <span
                                  style={{
                                    fontFamily: "ui-monospace, monospace",
                                    fontSize: 11,
                                    fontWeight: 900,
                                    background: "#EEF2FF",
                                    color: "#4338CA",
                                    padding: "2px 7px",
                                    borderRadius: 6,
                                    minWidth: 28,
                                    textAlign: "center",
                                  }}
                                >
                                  {r.code}
                                </span>
                                <span>{r.label}</span>
                              </span>
                              <span style={{ display: "inline-flex", alignItems: "baseline", gap: 8 }}>
                                {r.count >= 2 && (
                                  <span style={{ fontSize: 12, color: "#64748B" }}>{r.count.toLocaleString()}</span>
                                )}
                                <span style={{ fontSize: 14, color: "#0F172A", fontWeight: 900, minWidth: 50, textAlign: "right" }}>
                                  {r.share}%
                                </span>
                              </span>
                            </div>
                            <div style={{ height: 8, background: "#F1F5F9", borderRadius: 999, overflow: "hidden" }}>
                              <div
                                style={{
                                  width: `${widthPct}%`,
                                  height: "100%",
                                  background: "linear-gradient(90deg, #1D4ED8, #60A5FA)",
                                  borderRadius: 999,
                                  transition: "width 0.4s ease",
                                }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                );
              })()}
            </ChartCard>
          </Col>
        </Row>

        {/* AVS / CVV / Channel & Geography Mix — shown as percentage-only so
            the rates are honest and don't have to reconcile to per-window raw
            counts (which is rounding-fragile). Distributions are perturbed by
            a small per-window shift so the panels respond to the time filter,
            with residual rebalanced into the largest bucket so each
            distribution still sums to 100%. */}
        {(() => {
          // Match-rate quality drifts a touch per window. Smaller windows
          // (1h, 24h) tend to have slightly cleaner traffic (recently scored
          // & decisioned); larger windows pull in older, noisier checkouts.
          const matchShift =
            selectedWindow === "1h" ? +0.4 :
            selectedWindow === "24h" ? +0.15 :
            selectedWindow === "7d" ? -0.05 :
            -0.2;
          // Channel mix is more stable than AVS/CVV. CNP share barely moves;
          // cross-border drifts more visibly because window length brings in
          // different geo mixes (smaller windows = more recent, mostly
          // domestic; longer windows accumulate more international tail).
          const cnpShift = matchShift * 0.5;
          const crossBorderShift = -matchShift; // inverse of match quality

          // Shift "head" (full-match / match) bucket by matchShift; balance
          // by moving the opposite amount into the "tail" (no-match) bucket.
          // Middle buckets stay constant. Residual rounding error pushed to
          // the tail so the distribution still sums to 100.0.
          const rebalance = (rows, headIdx, tailIdx) => {
            const out = rows.map((r) => ({ ...r }));
            out[headIdx].value = +(rows[headIdx].value + matchShift).toFixed(1);
            out[tailIdx].value = +(rows[tailIdx].value - matchShift).toFixed(1);
            const sum = out.reduce((s, r) => s + r.value, 0);
            const residual = +(100 - sum).toFixed(1);
            if (residual !== 0) {
              out[tailIdx].value = +(out[tailIdx].value + residual).toFixed(1);
            }
            return out;
          };

          // AVS: head = Y (full match, idx 0); tail = N (no match, idx 3).
          const avsRows = rebalance(MOCK_AVS, 0, 3);
          // CVV: head = M (match, idx 0); tail = N (no match, idx 1).
          const cvvRows = rebalance(MOCK_CVV, 0, 1);
          const cnpShare = +(MOCK_CHANNEL_MIX.cnpShare + cnpShift).toFixed(1);
          const cpShare = +(100 - cnpShare).toFixed(1);
          const crossBorder = +(MOCK_CHANNEL_MIX.crossBorderShare + crossBorderShift).toFixed(1);
          const domestic = +(100 - crossBorder).toFixed(1);
          const windowLabel = kpis?.window ?? selectedWindow;

          return (
            <Row className="mb-4 g-4">
              <Col xl={4} lg={12}>
                <ChartCard title="AVS Match Distribution">
                  <div style={{ display: "flex", flexDirection: "column", gap: 14, minHeight: 220 }}>
                    <div>
                      <div style={{ fontSize: 11, color: "#64748B", fontWeight: 800, letterSpacing: "0.06em" }}>
                        FULL-MATCH RATE · WINDOW {windowLabel}
                      </div>
                      <div style={{ fontSize: 30, fontWeight: 900, color: "#047857", letterSpacing: "-0.03em" }}>
                        {avsRows[0].value}%
                      </div>
                    </div>
                    <StackedBar rows={avsRows} />
                  </div>
                </ChartCard>
              </Col>
              <Col xl={4} lg={12}>
                <ChartCard title="CVV Match Distribution">
                  <div style={{ display: "flex", flexDirection: "column", gap: 14, minHeight: 220 }}>
                    <div>
                      <div style={{ fontSize: 11, color: "#64748B", fontWeight: 800, letterSpacing: "0.06em" }}>
                        MATCH RATE · WINDOW {windowLabel}
                      </div>
                      <div style={{ fontSize: 30, fontWeight: 900, color: "#047857", letterSpacing: "-0.03em" }}>
                        {cvvRows[0].value}%
                      </div>
                    </div>
                    <StackedBar rows={cvvRows} />
                  </div>
                </ChartCard>
              </Col>
              <Col xl={4} lg={12}>
                <ChartCard title="Channel & Geography Mix">
                  <div style={{ display: "flex", flexDirection: "column", gap: 10, minHeight: 220 }}>
                    <div style={{ fontSize: 11, color: "#64748B", fontWeight: 800, letterSpacing: "0.06em" }}>
                      WINDOW {windowLabel}
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                      <MiniKpi label="CNP Share" value={`${cnpShare}%`} accent="#1D4ED8" />
                      <MiniKpi label="Card-Present" value={`${cpShare}%`} accent="#0EA5E9" />
                      <MiniKpi label="Cross-border" value={`${crossBorder}%`} accent="#F97316" />
                      <MiniKpi label="Domestic" value={`${domestic}%`} accent="#047857" />
                    </div>
                  </div>
                </ChartCard>
              </Col>
            </Row>
          );
        })()}

        <SectionBanner
          eyebrow="Risk & 3DS Posture"
          title="Risk tier mix, routing posture & operational telemetry"
          hint="The original AnalyticsDashboard charts, restyled to the new theme"
        />

        {/* ORIGINAL — direct imports of the AnalyticsDashboard sub-components.
            Restyled inline below; the bare imports are still available at the
            top of the file. Use these if you want the legacy look back:
            <Row className="mb-4 g-4">
              <Col xl={6} lg={12}><RiskSummaryAnalytics data={riskSummaryData} loading={analyticsLoading} /></Col>
              <Col xl={6} lg={12}><ThreeDSAnalytics data={threeDSData} loading={analyticsLoading} /></Col>
            </Row>
            <Row className="mb-4 g-4">
              <Col xl={12} lg={12}><VampAnalytics data={vampData} loading={analyticsLoading} /></Col>
            </Row>
            <Row className="mb-4 g-4">
              <Col xl={6} lg={12}><BinDistribution data={binData} loading={analyticsLoading} /></Col>
              <Col xl={6} lg={12}><RuleFrequency data={ruleData} loading={analyticsLoading} /></Col>
            </Row>
            <Row className="mb-4 g-4">
              <Col xl={12} lg={12}><LatencyAnalytics data={latencyData} loading={analyticsLoading} /></Col>
            </Row>
        */}

        <Row className="mb-4 g-4">
          <Col xl={6} lg={12}>
            <ChartShell
              eyebrow="Risk Decisioning"
              title="Risk tier mix"
              hint="Each tier maps to a default decision action"
            >
              <DonutWithLegend data={riskSummaryData} colorMap={RISK_COLORS} totalLabel="scored" />
              <div
                style={{
                  marginTop: 14,
                  paddingTop: 12,
                  borderTop: "1px solid #E5E7EB",
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                  fontSize: 12,
                  color: "#475569",
                }}
              >
                {[
                  { tier: "LOW", action: "Allow", note: "Cleared straight through", accent: "#10B981" },
                  { tier: "MEDIUM", action: "Review", note: "Held for manual review", accent: "#F59E0B" },
                  { tier: "HIGH", action: "Block", note: "Stopped before authorization", accent: "#DC2626" },
                ].map((m) => (
                  <div key={m.tier} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <RatingPill rating={m.tier} />
                    <span style={{ color: "#0F172A", fontWeight: 800, fontSize: 11, letterSpacing: "0.04em" }}>→</span>
                    <span
                      style={{
                        background: `${m.accent}1A`,
                        color: m.accent,
                        padding: "3px 10px",
                        borderRadius: 999,
                        fontSize: 11,
                        fontWeight: 900,
                        letterSpacing: "0.04em",
                      }}
                    >
                      {m.action.toUpperCase()}
                    </span>
                    <span style={{ color: "#64748B" }}>· {m.note}</span>
                  </div>
                ))}
              </div>
            </ChartShell>
          </Col>
          <Col xl={6} lg={12}>
            <ChartShell
              eyebrow="3DS Routing"
              title="3DS posture"
              hint="Force / Prefer / Bypass routing tier"
            >
              <DonutWithLegend data={threeDSData} colorMap={THREEDS_COLORS} totalLabel="routed" />
            </ChartShell>
          </Col>
        </Row>

        {/* VAMP block moved up next to Scheme Compliance / Disputes &
            Chargebacks — see the "VAMP" ChartShell up there. */}

        <Row className="mb-4 g-4">
          <Col xl={6} lg={12}>
            <ChartShell
              eyebrow="Issuer Concentration"
              title="Top BINs by volume"
              hint="Card-issuer ranges firing most often in the window"
            >
              <HorizontalBarList
                rows={binData}
                labelKey="bin"
                valueKey="count"
                accentColor="#1D4ED8"
                monoLabel
              />
            </ChartShell>
          </Col>
          <Col xl={6} lg={12}>
            <ChartShell
              eyebrow="Rule Effectiveness"
              title="Top rules fired"
              hint="Highest-frequency idenza rules contributing to decisions"
            >
              <HorizontalBarList
                rows={ruleData}
                labelKey="rule"
                valueKey="count"
                accentColor="#7C3AED"
              />
            </ChartShell>
          </Col>
        </Row>

        {/* Decisioning Latency / Scoring round-trip section removed per design.
            LatencyPanel component definition stays available higher up.
        <Row className="mb-4 g-4">
          <Col xl={12} lg={12}>
            <ChartShell
              eyebrow="Decisioning Latency"
              title="Scoring round-trip"
              hint="p50 / p95 / p99 + last-15-orders tail"
            >
              <LatencyPanel data={latencyData} />
            </ChartShell>
          </Col>
        </Row>
        */}

        {/* ORIGINAL — Program risk leaderboard. Removed: MOCK_PROGRAM_LEADERBOARD
            referenced real customer / program names (Epic Sweep, CasinoKai, etc.).
            Restore with generic program labels if this view is needed again. */}
        {/*
        <section style={{ marginBottom: 24 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 900, color: "#64748B", letterSpacing: "0.10em" }}>PORTFOLIO POSTURE</div>
              <h5 style={{ margin: 0, fontWeight: 900, color: "#0F172A", letterSpacing: "-0.02em" }}>Program risk leaderboard</h5>
            </div>
            <span style={{ fontSize: 11, color: "#64748B", fontWeight: 700 }}>
              {MOCK_PROGRAM_LEADERBOARD.length} active programs • ranked by composite score
            </span>
          </div>
          <div style={{ background: "#FFFFFF", border: "1px solid #E5E7EB", borderRadius: 20, boxShadow: "0 16px 38px rgba(15,23,42,0.07)", overflow: "hidden" }}>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ background: "#F8FAFC", borderBottom: "1px solid #E5E7EB", textAlign: "left", fontSize: 11, color: "#64748B", letterSpacing: "0.04em" }}>
                    <th>Program</th><th>Volume</th><th>Portfolio %</th><th>Top-5 CP</th><th>Non-US</th><th>$50k+</th><th>Signals</th><th>Rating</th>
                  </tr>
                </thead>
                <tbody>
                  {MOCK_PROGRAM_LEADERBOARD.map((p) => (
                    <tr key={p.program} style={{ borderBottom: "1px solid #F1F5F9" }}>
                      <td style={{ padding: "14px 18px", fontWeight: 900, color: "#0F172A" }}>{p.program}</td>
                      <td>{fmtCompactUsd(p.volume_usd)}</td>
                      <td>{p.portfolio_pct.toFixed(1)}%</td>
                      <td>{p.top5_cp_share.toFixed(1)}%</td>
                      <td>{p.non_us_share.toFixed(1)}%</td>
                      <td>{p.high_value_share.toFixed(1)}%</td>
                      <td>
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                          <ScorePill label="Vol" score={p.scores.volume} />
                          <ScorePill label="Cpty" score={p.scores.concentration} />
                          <ScorePill label="XB" score={p.scores.cross_border} />
                          <ScorePill label="HV" score={p.scores.high_value} />
                          <ScorePill label="Σ" score={p.scores.total} />
                        </div>
                      </td>
                      <td><RatingPill rating={p.rating} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
        */}

        {/* Compliance & Monitoring (BSA/AML/KYC/SAR/OFAC) and Player Risk &
            Controls (GeoComply, self-exclusion, age verification) sections
            removed — they belong to identity / onboarding / responsible-gaming
            products, not the risk + BIN + score API surface this dashboard
            is for. MOCK_BSA_AML / MOCK_IGAMING_RISK / MOCK_TM_SCENARIOS still
            live higher up in case someone wants to bring them back. */}

        <SectionBanner
          eyebrow="Drilldowns"
          title="Issuer leaderboard & transaction detail"
          hint={`Ranked by volume — last ${selectedWindow}`}
        />

        <section style={{ marginBottom: space[6] }}>
          <div style={{ marginBottom: space[2], fontSize: text.base, fontWeight: weight.semibold, color: color.text.primary }}>
            Top issuer BINs — approval & decline rate
          </div>
          <Card padding="sm" style={{ overflow: "hidden", padding: 0 }}>
            <DataTable
              columns={[
                { key: "bin",      label: "BIN",
                  render: (r) => (
                    <span style={{ fontFamily: font.mono, color: color.info.text, ...numeric.tabular }}>{r.bin}••</span>
                  ) },
                { key: "issuer",   label: "Issuer",
                  render: (r) => (
                    <span style={{ fontWeight: weight.semibold }}>{r.issuer}</span>
                  ) },
                { key: "volume",   label: "Volume", numeric: true },
                { key: "approval", label: "Approval",
                  render: (r) => {
                    const tone = r.approvalRaw >= 90 ? "success" : r.approvalRaw >= 85 ? "warning" : "danger";
                    return <Chip tone={tone} size="sm">{r.approvalRaw}%</Chip>;
                  } },
                { key: "decline",  label: "Decline", numeric: true,
                  render: (r) => (
                    <span style={{ color: color.danger.text, fontWeight: weight.semibold }}>{r.declineRaw}%</span>
                  ) },
              ]}
              rows={MOCK_TOP_ISSUERS.map((r) => {
                const scale = WINDOW_SCALE[selectedWindow] ?? 1;
                const scaledVolume = Math.max(1, Math.round(r.volume * scale));
                return {
                  key: r.bin,
                  bin: r.bin,
                  issuer: r.issuer,
                  volume: fmtNumber(scaledVolume),
                  approvalRaw: r.approval,
                  declineRaw: r.decline,
                };
              })}
            />
          </Card>
        </section>

        {/* ORIGINAL — live transaction tables. Uncomment imports at top + this block
            and delete the four DemoTxnTable-driven sections that follow to revert. */}
        {/*
        <EnterpriseTableSection
          eyebrow="Prevention"
          title="Blocked Transactions"
          description="Transactions stopped by Idenza risk decisioning before reaching the downstream approval flow."
          badge="PREVENTED"
          badgeStyle={{ background: "#FEF2F2", color: "#B91C1C", borderColor: "#FECACA" }}
        >
          <BlockedTransactionsTable apiBase={apiBase} />
        </EnterpriseTableSection>

        <EnterpriseTableSection
          eyebrow="Authentication"
          title="3DS Failed Transactions"
          description="Transactions where forced authentication was required but the user did not successfully complete 3DS."
          badge="CHALLENGE FAIL"
          badgeStyle={{ background: "#FFF7ED", color: "#C2410C", borderColor: "#FED7AA" }}
        >
          <DeclineTable apiBase={apiBase} endpoint="threeds-failed-transactions" title="3DS Failed" reasonLabel="Reason" />
        </EnterpriseTableSection>

        <EnterpriseTableSection
          eyebrow="Issuer Outcome"
          title="Issuer Declined Transactions"
          description="Transactions that passed risk controls or authentication but were declined by the issuing bank."
          badge="ISSUER DECLINE"
          badgeStyle={{ background: "#FEFCE8", color: "#A16207", borderColor: "#FEF08A" }}
        >
          <DeclineTable apiBase={apiBase} endpoint="issuer-declined-transactions" title="Issuer Declined" reasonLabel="Issuer Decline Reason" />
        </EnterpriseTableSection>

        <EnterpriseTableSection
          eyebrow="Trusted User Monitoring"
          title="Post-Whitelisting Transactions"
          description="Activity from whitelisted users after trust treatment, useful for monitoring abuse after approval."
          badge="TRUSTED"
          badgeStyle={{ background: "#ECFDF5", color: "#047857", borderColor: "#A7F3D0" }}
        >
          <WhitelistedTransactionsTable apiBase={apiBase} />
        </EnterpriseTableSection>
        */}
        <EnterpriseTableSection
          eyebrow="Prevention"
          title="Blocked Transactions"
          description="Transactions stopped by Idenza risk decisioning before reaching the downstream approval flow."
          badge="PREVENTED"
          badgeStyle={{
            background: "#FEF2F2",
            color: "#B91C1C",
            borderColor: "#FECACA",
          }}
        >
          <PagedDemoTable
            columns={["Merchant", "Order", "Email / User Identity", "Block Reasons", "Timestamp"]}
            rows={filterByMerchant(shiftRowsForWindow(
              MOCK_BLOCKED_TXNS,
              selectedWindow,
              (kpis?.total_transactions ?? 0) * (kpis?.block_rate ?? 0) / 100
            )).map((r) => ({
              cells: [
                <span style={merchantTagStyle(assignMerchant(r.email).id)}>{merchantAbbr(assignMerchant(r.email).id)}</span>,
                <span style={{ fontFamily: "ui-monospace, monospace", fontWeight: 700, color: "#1D4ED8" }}>{r.id}</span>,
                <span style={{ color: "#1D4ED8" }}>{r.email}</span>,
                <span style={{ display: "inline-flex", flexWrap: "wrap", gap: 4 }}>
                  {(r.reasons ?? []).map((rule, idx) => (
                    <span
                      key={idx}
                      style={{
                        display: "inline-block",
                        padding: "2px 8px",
                        borderRadius: 4,
                        background: "#FEF2F2",
                        color: "#B91C1C",
                        fontSize: 11,
                        fontWeight: 600,
                        lineHeight: 1.5,
                      }}
                    >
                      {rule}
                    </span>
                  ))}
                </span>,
                <span style={{ fontFamily: "ui-monospace, monospace", color: "#475569" }}>{r.ts} UTC</span>,
              ],
            }))}
          />
        </EnterpriseTableSection>

        <EnterpriseTableSection
          eyebrow="Authentication"
          title="3DS Failed Transactions"
          description="Transactions where forced authentication was required but the user did not successfully complete 3DS."
          badge="CHALLENGE FAIL"
          badgeStyle={{
            background: "#FFF7ED",
            color: "#C2410C",
            borderColor: "#FED7AA",
          }}
        >
          <PagedDemoTable
            columns={["Merchant", "Order", "Email / User Identity", "Reason", "Timestamp"]}
            rows={filterByMerchant(shiftRowsForWindow(
              MOCK_3DS_FAILED,
              selectedWindow,
              (kpis?.total_transactions ?? 0) * (kpis?.threeds_fail_rate ?? 0) / 100
            )).map((r) => ({
              cells: [
                <span style={merchantTagStyle(assignMerchant(r.email).id)}>{merchantAbbr(assignMerchant(r.email).id)}</span>,
                <span style={{ fontFamily: "ui-monospace, monospace", fontWeight: 700, color: "#C2410C" }}>{r.id}</span>,
                <span style={{ color: "#C2410C" }}>{r.email}</span>,
                <span
                  style={{
                    display: "inline-block",
                    padding: "2px 8px",
                    borderRadius: 4,
                    background: "#FEF2F2",
                    color: "#DC2626",
                    fontSize: 12,
                    fontWeight: 500,
                  }}
                >
                  {r.reason}
                </span>,
                <span style={{ fontFamily: "ui-monospace, monospace", color: "#475569" }}>{r.ts} UTC</span>,
              ],
            }))}
          />
        </EnterpriseTableSection>

        <EnterpriseTableSection
          eyebrow="Issuer Outcome"
          title="Issuer Declined Transactions"
          description="Transactions that passed risk controls or authentication but were declined by the issuing bank."
          badge="ISSUER DECLINE"
          badgeStyle={{
            background: "#FEFCE8",
            color: "#A16207",
            borderColor: "#FEF08A",
          }}
        >
          <PagedDemoTable
            columns={["Merchant", "Order", "Email / User Identity", "Issuer Decline Reason", "Timestamp"]}
            rows={filterByMerchant(shiftRowsForWindow(
              MOCK_ISSUER_DECLINED,
              selectedWindow,
              (kpis?.total_transactions ?? 0) * (kpis?.issuer_decline_rate ?? 0) / 100
            )).map((r) => ({
              cells: [
                <span style={merchantTagStyle(assignMerchant(r.email).id)}>{merchantAbbr(assignMerchant(r.email).id)}</span>,
                <span style={{ fontFamily: "ui-monospace, monospace", fontWeight: 700, color: "#A16207" }}>{r.id}</span>,
                <span style={{ color: "#A16207" }}>{r.email}</span>,
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <span style={{ background: "#FEF3C7", color: "#92400E", padding: "2px 7px", borderRadius: 4, fontFamily: "ui-monospace, monospace", fontSize: 11, fontWeight: 900 }}>{r.code}</span>
                  <span
                    style={{
                      display: "inline-block",
                      padding: "2px 8px",
                      borderRadius: 4,
                      background: "#FEF2F2",
                      color: "#DC2626",
                      fontSize: 12,
                      fontWeight: 500,
                    }}
                  >
                    {r.reason}
                  </span>
                </span>,
                <span style={{ fontFamily: "ui-monospace, monospace", color: "#475569" }}>{r.ts} UTC</span>,
              ],
            }))}
          />
        </EnterpriseTableSection>

        <EnterpriseTableSection
          eyebrow="Trusted User Monitoring"
          title="Post-Whitelisting Transactions"
          description="Activity from whitelisted users after trust treatment, useful for monitoring abuse after approval."
          badge="TRUSTED"
          badgeStyle={{
            background: "#ECFDF5",
            color: "#047857",
            borderColor: "#A7F3D0",
          }}
        >
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
            <div style={{ position: "relative", width: 280 }}>
              <input
                type="search"
                value={whitelistEmailFilter}
                onChange={(e) => setWhitelistEmailFilter(e.target.value)}
                placeholder="Filter by email…"
                style={{
                  width: "100%",
                  padding: "8px 12px 8px 32px",
                  borderRadius: 10,
                  border: "1px solid #E2E8F0",
                  fontSize: 13,
                  outline: "none",
                  background: "#FFFFFF",
                  boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
                }}
              />
              <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#94A3B8", fontSize: 14 }}>⌕</span>
            </div>
          </div>
          {(() => {
            const expanded = filterByMerchant(shiftRowsForWindow(MOCK_WHITELISTED_TXNS, selectedWindow, "whitelisted"));
            const q = whitelistEmailFilter.trim().toLowerCase();
            const filtered = q
              ? expanded.filter((r) => (r.email || "").toLowerCase().includes(q))
              : expanded;
            if (filtered.length === 0) {
              return (
                <div style={{ padding: "24px 8px", textAlign: "center", color: "#64748B", fontSize: 13 }}>
                  No matches for <strong style={{ color: "#0F172A" }}>{whitelistEmailFilter}</strong>.
                </div>
              );
            }
            return (
              <PagedDemoTable
                columns={["Merchant", "Order", "Email / User Identity", "Deposit Status", "Decline Reason", "Timestamp"]}
                rows={filtered.map((r) => {
                  const isCompleted = r.status === "completed";
                  return {
                    cells: [
                      <span style={merchantTagStyle(assignMerchant(r.email).id)}>{merchantAbbr(assignMerchant(r.email).id)}</span>,
                      <span style={{ fontFamily: "ui-monospace, monospace", fontWeight: 700, color: "#047857" }}>{r.id}</span>,
                      <span style={{ color: "#047857" }}>{r.email}</span>,
                      <span
                        style={{
                          background: isCompleted ? "#D1FAE5" : "#FEE2E2",
                          color: isCompleted ? "#047857" : "#B91C1C",
                          padding: "3px 8px",
                          borderRadius: 6,
                          fontSize: 11,
                          fontWeight: 900,
                          textTransform: "uppercase",
                          letterSpacing: "0.04em",
                        }}
                      >
                        {r.status}
                      </span>,
                      isCompleted ? (
                        <span style={{ color: "#94A3B8" }}>—</span>
                      ) : (
                        <span
                          style={{
                            display: "inline-block",
                            padding: "2px 8px",
                            borderRadius: 4,
                            background: "#FEF2F2",
                            color: "#DC2626",
                            fontSize: 12,
                            fontWeight: 500,
                          }}
                        >
                          {r.decline_reason ?? "Issuer decline"}
                        </span>
                      ),
                      <span style={{ fontFamily: "ui-monospace, monospace", color: "#475569" }}>{r.ts} UTC</span>,
                    ],
                  };
                })}
              />
            );
          })()}
        </EnterpriseTableSection>
        </>
        )}

        <div
          style={{
            color: "#64748B",
            textAlign: "center",
            padding: 18,
            marginTop: 20,
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          Idenza &copy; 2026
        </div>
        </div>
      </Container>
    </div>
  );
}